import type { Election, Event, Party, Period, Person, PoliticalFamily, Regime, Relation } from "../types/domain";

export const regimes: Regime[] = [
  { id: "regime-troisieme-republique", name: "IIIe République", start: "1870-09-04", end: "1940-07-10", description: "Régime parlementaire républicain établi après la chute du Second Empire." },
  { id: "regime-vichy", name: "Régime de Vichy", start: "1940-07-10", end: "1944-08-20", description: "Régime autoritaire de l'Etat français pendant l'Occupation." },
  { id: "regime-liberation", name: "Libération", start: "1944-08-20", end: "1946-10-27", description: "Période de rétablissement républicain et de gouvernement provisoire." },
  { id: "regime-quatrieme-republique", name: "IVe République", start: "1946-10-27", end: "1958-10-04", description: "Régime parlementaire instauré par la Constitution de 1946." },
  { id: "regime-cinquieme-republique", name: "Ve République", start: "1958-10-04", description: "Régime constitutionnel fondé en 1958." }
];

export const periods: Period[] = [
  { id: "period-1880-1899", name: "1880-1899", start: "1880", end: "1899", regime: "regime-troisieme-republique", description: "Consolidation de la IIIe République, structuration du mouvement ouvrier et crise boulangiste puis affaire Dreyfus." },
  { id: "period-1900-1914", name: "1900-1914", start: "1900", end: "1914", regime: "regime-troisieme-republique", description: "Consolidation républicaine, recompositions radicales et socialistes." },
  { id: "period-1914-1918", name: "1914-1918", start: "1914", end: "1918", regime: "regime-troisieme-republique", description: "Première Guerre mondiale et Union sacrée." },
  { id: "period-1919-1932", name: "1919-1932", start: "1919", end: "1932", regime: "regime-troisieme-republique", description: "Après-guerre, scission socialiste et structuration communiste." },
  { id: "period-1932-1939", name: "1932-1939", start: "1932", end: "1939", regime: "regime-troisieme-republique", description: "Crises des années 1930 et expérience du Front populaire." },
  { id: "period-1939-1945", name: "1939-1945", start: "1939", end: "1945", regime: "regime-vichy", description: "Guerre, Occupation, Vichy, Résistance et Libération." },
  { id: "period-1945-1958", name: "1945-1958", start: "1945", end: "1958", regime: "regime-quatrieme-republique", description: "Reconstruction, partis de masse et crises gouvernementales." },
  { id: "period-1958-1969", name: "1958-1969", start: "1958", end: "1969", regime: "regime-cinquieme-republique", description: "Fondation de la Ve République et domination gaulliste initiale." },
  { id: "period-1969-1981", name: "1969-1981", start: "1969", end: "1981", regime: "regime-cinquieme-republique", description: "Recomposition à gauche et compétition gaullistes, centristes et libéraux." },
  { id: "period-1981-1995", name: "1981-1995", start: "1981", end: "1995", regime: "regime-cinquieme-republique", description: "Alternance, cohabitations et recompositions partisanes." },
  { id: "period-1995-2002", name: "1995-2002", start: "1995", end: "2002", regime: "regime-cinquieme-republique", description: "Pluralisme partisan et gouvernement de cohabitation." },
  { id: "period-2002-2012", name: "2002-2012", start: "2002", end: "2012", regime: "regime-cinquieme-republique", description: "Présidentialisation et transformations de l'UMP." },
  { id: "period-2012-2017", name: "2012-2017", start: "2012", end: "2017", regime: "regime-cinquieme-republique", description: "Crise des partis de gouvernement traditionnels." },
  { id: "period-2017-2022", name: "2017-2022", start: "2017", end: "2022", regime: "regime-cinquieme-republique", description: "Recomposition autour du macronisme, de LFI et du RN." },
  { id: "period-2022-present", name: "2022-présent", start: "2022", regime: "regime-cinquieme-republique", description: "Données contemporaines susceptibles d'évoluer." }
];

export const families: PoliticalFamily[] = [
  { id: "family-socialisme", name: "Socialisme", description: "Famille historique plurielle issue du mouvement ouvrier et de traditions républicaines sociales.", sources: ["source-vie-publique-front-populaire"] },
  { id: "family-communisme", name: "Communisme", description: "Tradition politique structurée en France après la scission socialiste du Congrès de Tours.", sources: ["source-maitron-tours"] },
  { id: "family-radicalisme", name: "Radicalisme", description: "Famille républicaine attachée à la laïcité et aux réformes politiques dans le contexte de la IIIe République.", sources: ["source-assemblee-1936"] },
  { id: "family-gaullisme", name: "Gaullisme", description: "Famille organisée autour de l'héritage politique du général de Gaulle et des institutions de la Ve République.", sources: ["source-vie-publique-1958"] },
  { id: "family-liberalisme", name: "Libéralisme", description: "Tradition politique diverse, attachée selon les périodes aux libertés publiques, économiques ou parlementaires.", sources: ["source-sanson-alliance-democratique"] },
  { id: "family-conservatisme", name: "Conservatisme", description: "Famille attachée, selon les contextes, à l'ordre social, aux institutions établies ou à une lecture modérée du changement politique.", sources: ["source-francearchives-federation-republicaine"] },
  { id: "family-nationalisme", name: "Nationalisme", description: "Famille politique à manier avec contextualisation, recouvrant des traditions différentes selon les périodes.", sources: ["source-larousse-action-francaise"] },
  { id: "family-monarchisme", name: "Monarchisme", description: "Tradition favorable au principe monarchique, présente sous diverses formes dans la vie politique française.", sources: ["source-larousse-action-francaise"] },
  { id: "family-democratie-chretienne", name: "Démocratie chrétienne", description: "Courants et organisations inspirés par des références chrétiennes et par une insertion variable dans les institutions républicaines.", sources: ["source-larousse-action-francaise"] },
  { id: "family-republicanisme", name: "Républicanisme", description: "Tradition attachée à la défense du régime républicain, avec des expressions très différentes selon les périodes et les familles sociales.", sources: ["source-ldh-histoire"] },
  { id: "family-fascisme", name: "Fascisme", description: "Catégorie historiographique à employer avec prudence pour des organisations explicitement inspirées ou rapprochées des fascismes de l'entre-deux-guerres.", sources: ["source-persee-faisceau"] },
  { id: "family-anarchisme", name: "Anarchisme", description: "Famille politique libertaire plurielle, présente dans certains mouvements étudiants et sociaux de la fin des années 1960.", sources: ["source-persee-mouvement-22-mars"] },
  { id: "family-ecologie-politique", name: "Écologie politique", description: "Famille émergente à partir des mobilisations environnementales et antinucléaires, avant les partis écologistes structurés.", sources: ["source-parti-occitan"] },
  { id: "family-regionalisme", name: "Régionalisme", description: "Famille de mouvements défendant l'autonomie, les langues régionales ou la reconnaissance de nations culturelles infra-étatiques.", sources: ["source-pno", "source-parti-occitan"] },
  { id: "family-souverainisme", name: "Souverainisme", description: "Courants attachés à la primauté de la souveraineté nationale, particulièrement visibles dans les débats européens des années 1990.", sources: ["source-vie-publique-maastricht"] }
];

export const parties: Party[] = [
  {
    id: "party-prrrs",
    name: "Parti républicain, radical et radical-socialiste",
    acronym: "PRRRS",
    variants: ["Parti radical", "Parti radical-socialiste"],
    foundedAt: "1901-06-21",
    dissolvedAt: null,
    status: "active",
    description: "Parti radical fondé au congrès de Paris de juin 1901, généralement présenté comme l'un des premiers partis français organisés au sens moderne.",
    historicalNote: "Son rattachement au centre gauche vaut surtout pour le contexte de la IIIe République ; l'expression radical-socialiste désigne ici le parti et ses sensibilités, sans créer automatiquement une entité séparée pour une aile interne.",
    families: ["family-radicalisme"],
    founders: [],
    leaders: ["person-edouard-herriot", "person-edouard-daladier", "person-pierre-mendes-france", "person-felix-gaillard", "person-jean-jacques-servan-schreiber"],
    sources: ["source-francearchives-parti-radical", "source-larousse-parti-radical"],
    dataStatus: "partially_verified",
    lastVerifiedAt: "2026-08-24"
  },
  {
    id: "party-ard",
    name: "Alliance républicaine démocratique",
    acronym: "ARD",
    variants: ["Alliance démocratique", "Parti républicain démocratique"],
    foundedAt: "1901",
    dissolvedAt: "1954",
    status: "historical",
    description: "Formation républicaine modérée et libérale, située dans les recompositions du centre et du centre droit sous la IIIe République.",
    historicalNote: "L'Alliance oscille entre association et parti ; devenue Parti républicain démocratique en 1911, elle est très présente dans les combinaisons gouvernementales et contribue à l'initiative du Bloc national en 1919.",
    families: ["family-liberalisme", "family-radicalisme"],
    founders: [],
    leaders: ["person-raymond-poincare", "person-pierre-etienne-flandin"],
    sources: ["source-sanson-alliance-democratique"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-alp",
    name: "Action libérale populaire",
    acronym: "ALP",
    variants: ["Action libérale"],
    foundedAt: "1902",
    dissolvedAt: "1919",
    status: "historical",
    description: "Organisation de catholiques ralliés à la République, structurée à droite dans l'opposition aux politiques anticléricales du Bloc des gauches.",
    historicalNote: "L'ALP doit être distinguée du monarchisme intransigeant : elle participe au processus de ralliement d'une partie des catholiques aux institutions républicaines.",
    families: ["family-democratie-chretienne", "family-conservatisme", "family-liberalisme"],
    founders: ["person-jacques-piou", "person-albert-de-mun"],
    leaders: ["person-jacques-piou"],
    sources: ["source-openedition-alp"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-federation-republicaine",
    name: "Fédération républicaine",
    acronym: "FR",
    variants: [],
    foundedAt: "1903",
    dissolvedAt: "1948",
    status: "historical",
    description: "Grand regroupement de la droite républicaine sous la IIIe République, notamment conservatrice et libérale.",
    historicalNote: "Elle rassemble des droites républicaines dont les contours doivent être distingués des ligues, des groupes parlementaires et des courants monarchistes.",
    families: ["family-conservatisme", "family-liberalisme"],
    founders: ["person-eugene-motte"],
    leaders: ["person-louis-marin", "person-georges-mandel"],
    sources: ["source-francearchives-federation-republicaine"],
    dataStatus: "verified"
  },
  {
    id: "party-sfio",
    name: "Section française de l'Internationale ouvrière",
    acronym: "SFIO",
    variants: ["Parti socialiste SFIO"],
    foundedAt: "1905",
    dissolvedAt: "1969",
    status: "historical",
    description: "Parti socialiste français créé au début du XXe siècle par l'unification de courants socialistes.",
    historicalNote: "Son positionnement doit être lu dans le contexte du socialisme parlementaire français et de ses débats avec le communisme après 1920.",
    families: ["family-socialisme"],
    founders: ["person-jean-jaures"],
    leaders: ["person-leon-blum"],
    sources: ["source-vie-publique-front-populaire", "source-bnf-blum"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-psf-1902",
    name: "Parti socialiste français",
    acronym: "PSF",
    variants: ["Parti socialiste français de 1902"],
    foundedAt: "1902",
    dissolvedAt: "1905",
    status: "historical",
    description: "Regroupement socialiste réformiste associé à Jean Jaurès avant l'unification socialiste de 1905.",
    historicalNote: "Cette formation transitoire doit être distinguée du Parti social français des années 1930 et des sigles ultérieurs.",
    families: ["family-socialisme"],
    founders: ["person-jean-jaures"],
    leaders: ["person-jean-jaures"],
    sources: ["source-vie-publique-front-populaire", "source-francearchives-jaures"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-psdf",
    name: "Parti socialiste de France",
    acronym: "PSdF",
    variants: ["Parti socialiste de France de 1902"],
    foundedAt: "1902",
    dissolvedAt: "1905",
    status: "historical",
    description: "Regroupement socialiste marqué par les courants guesdistes et marxistes avant l'unification dans la SFIO.",
    historicalNote: "Son rapport à la SFIO doit être présenté comme une fusion dans un contexte d'unification socialiste, non comme une simple continuité sans débat.",
    families: ["family-socialisme"],
    founders: ["person-jules-guesde"],
    leaders: ["person-jules-guesde"],
    sources: ["source-gallica-psdf-commentry"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-pof",
    name: "Parti ouvrier français",
    acronym: "POF",
    variants: ["Parti ouvrier", "Parti ouvrier de Jules Guesde"],
    foundedAt: "1882",
    dissolvedAt: "1902",
    status: "historical",
    description: "Parti socialiste marxiste associé à Jules Guesde et Paul Lafargue, antérieur à l'unification socialiste de 1905.",
    historicalNote: "Le POF n'existe plus comme organisation autonome entre 1910 et 1920 ; il est conservé comme prédécesseur permettant d'expliquer le PSdF puis la SFIO.",
    families: ["family-socialisme"],
    founders: ["person-jules-guesde", "person-paul-lafargue"],
    leaders: ["person-jules-guesde"],
    sources: ["source-persee-mouvement-socialiste", "source-marxists-pof-programme"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-prs",
    name: "Parti républicain-socialiste",
    acronym: "PRS",
    variants: [],
    foundedAt: "1911",
    dissolvedAt: "1934",
    status: "historical",
    description: "Formation de socialistes indépendants et républicains-socialistes située hors de la discipline de la SFIO.",
    historicalNote: "La qualification de cette formation relève du socialisme indépendant et du parlementarisme républicain ; elle regroupe notamment des réformistes refusant la discipline imposée par la SFIO.",
    families: ["family-socialisme", "family-radicalisme"],
    founders: ["person-aristide-briand", "person-rene-viviani", "person-joseph-paul-boncour"],
    leaders: ["person-aristide-briand", "person-rene-viviani", "person-joseph-paul-boncour"],
    sources: ["source-theses-prs"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-socialistes-independants",
    name: "Socialistes indépendants",
    acronym: "SI",
    variants: ["Socialistes indépendants républicains"],
    dissolvedAt: null,
    status: "movement",
    description: "Courant de socialistes parlementaires et réformistes demeurant en marge de la discipline de la SFIO.",
    historicalNote: "Cette entrée désigne un courant et des réseaux d'élus, pas un parti unifié durable ; elle sert à modéliser les origines du républicanisme-socialiste.",
    families: ["family-socialisme", "family-radicalisme"],
    founders: [],
    leaders: ["person-aristide-briand", "person-rene-viviani"],
    sources: ["source-persee-prs", "source-theses-prs"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-psf-1919",
    name: "Parti socialiste français",
    acronym: "PSF",
    variants: ["Parti socialiste français de 1919"],
    foundedAt: "1919",
    dissolvedAt: "1935",
    status: "historical",
    description: "Formation issue de dissidents socialistes favorables au maintien de logiques d'union ou de concentration républicaine après la Grande Guerre.",
    historicalNote: "Cette fiche est distincte du Parti socialiste français de 1902 ; elle revendique en partie cet héritage mais appartient à une autre séquence politique.",
    families: ["family-socialisme", "family-radicalisme"],
    founders: [],
    leaders: [],
    sources: ["source-persee-prs"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-action-francaise",
    name: "Action française",
    acronym: "AF",
    variants: ["Ligue d'Action française"],
    foundedAt: "1899",
    dissolvedAt: null,
    status: "movement",
    description: "Mouvement politique nationaliste et royaliste né dans le contexte de l'affaire Dreyfus, très actif sous la IIIe République.",
    historicalNote: "Cette fiche emploie les catégories de nationalisme, monarchisme et extrême droite comme qualifications historiques contextualisées et sourcées ; les sources consultées situent la fondation autour de 1899 et distinguent les fondateurs initiaux de l'influence doctrinale de Charles Maurras.",
    families: ["family-nationalisme", "family-monarchisme", "family-conservatisme"],
    founders: ["person-henri-vaugeois", "person-maurice-pujo"],
    leaders: ["person-charles-maurras"],
    sources: ["source-larousse-action-francaise", "source-hal-action-francaise"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-ligue-des-patriotes",
    name: "Ligue des patriotes",
    acronym: "LDP",
    variants: [],
    foundedAt: "1882",
    dissolvedAt: null,
    status: "movement",
    description: "Ligue nationaliste fondée dans le contexte de la revanche après la guerre franco-prussienne.",
    historicalNote: "La Ligue est une organisation nationaliste et non un parti parlementaire moderne ; son rôle doit être distingué de celui des partis structurés de la IIIe République.",
    families: ["family-nationalisme"],
    founders: ["person-paul-deroulede"],
    leaders: ["person-paul-deroulede"],
    sources: ["source-theses-deroulede"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-jeunesses-patriotes",
    name: "Jeunesses patriotes",
    acronym: "JP",
    variants: ["Ligue des Jeunesses patriotes"],
    foundedAt: "1924",
    dissolvedAt: "1936",
    status: "movement",
    description: "Ligue nationaliste d'extrême droite de l'entre-deux-guerres, issue du contexte politique postérieur au Cartel des gauches.",
    historicalNote: "Contrairement à certaines listes rapides, les Jeunesses patriotes ne sont pas actives entre 1910 et 1920 : les sources consultées situent leur création en 1924.",
    families: ["family-nationalisme", "family-conservatisme"],
    founders: ["person-pierre-taittinger"],
    leaders: ["person-pierre-taittinger"],
    sources: ["source-theses-jeunesses-patriotes", "source-archives-loire-atlantique-jp"],
    dataStatus: "verified"
  },
  {
    id: "party-bloc-national",
    name: "Bloc national",
    acronym: "BN",
    variants: ["Bloc national républicain"],
    foundedAt: "1919-10-22",
    dissolvedAt: "1924",
    status: "coalition",
    description: "Coalition électorale et parlementaire de droite et du centre formée après la Première Guerre mondiale.",
    historicalNote: "Le Bloc national n'est pas un parti unique. Il rassemble notamment l'Alliance démocratique, la Fédération républicaine, l'Action libérale populaire et des radicaux indépendants selon des configurations locales et parlementaires.",
    families: ["family-liberalisme", "family-conservatisme", "family-radicalisme"],
    founders: [],
    leaders: ["person-raymond-poincare", "person-alexandre-millerand"],
    sources: ["source-assemblee-bloc-national-1919", "source-larousse-bloc-national"],
    dataStatus: "verified"
  },
  {
    id: "party-cartel-des-gauches",
    name: "Cartel des gauches",
    acronym: "Cartel",
    variants: ["Cartel des gauches de 1924", "Cartel des gauches de 1932"],
    foundedAt: "1924",
    dissolvedAt: "1934",
    status: "coalition",
    description: "Alliance électorale de gauche et de centre gauche associant notamment radicaux, socialistes SFIO et républicains-socialistes en 1924 puis en 1932.",
    historicalNote: "Le Cartel des gauches est une coalition électorale et parlementaire, avec deux séquences principales ; il ne doit pas être confondu avec un parti unifié.",
    families: ["family-radicalisme", "family-socialisme", "family-republicanisme"],
    founders: [],
    leaders: ["person-edouard-herriot"],
    sources: ["source-jstor-cartel-socialistes", "source-france-memoire-herriot-cartel"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-republicains-progressistes",
    name: "Républicains progressistes",
    acronym: "RP",
    variants: ["Progressistes"],
    dissolvedAt: null,
    status: "movement",
    description: "Courant de républicains modérés puis de droite républicaine, opposé aux alliances avec les gauches radicales et socialistes au tournant du siècle.",
    historicalNote: "Cette entrée décrit un courant ou regroupement politique, pas un parti moderne unique avec une date de fondation centralisée.",
    families: ["family-liberalisme", "family-conservatisme"],
    founders: [],
    leaders: [],
    sources: ["source-francearchives-federation-republicaine"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-radicaux-independants",
    name: "Radicaux indépendants",
    acronym: "RI",
    variants: ["Radicaux de droite"],
    dissolvedAt: null,
    status: "movement",
    description: "Courant de radicaux et républicains indépendants participant à des alliances de centre et de droite selon les contextes électoraux.",
    historicalNote: "Cette entrée ne désigne pas le Parti radical lui-même. Elle sert à modéliser des élus ou listes indépendantes rattachées au radicalisme mais engagées hors de la discipline radicale principale.",
    families: ["family-radicalisme", "family-liberalisme"],
    founders: [],
    leaders: [],
    sources: ["source-assemblee-bloc-national-1919"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-pcf",
    name: "Parti communiste français",
    acronym: "PCF",
    variants: ["Section française de l'Internationale communiste"],
    foundedAt: "1920",
    dissolvedAt: null,
    status: "active",
    description: "Organisation issue de la majorité du Congrès de Tours, d'abord liée à l'Internationale communiste.",
    historicalNote: "La dénomination SFIC désigne la phase initiale du parti communiste après Tours ; elle est conservée comme variante de nom plutôt que comme fiche séparée afin de distinguer changement d'appellation et création d'une nouvelle organisation.",
    families: ["family-communisme"],
    founders: [],
    leaders: ["person-ludovic-oscar-frossard", "person-marcel-cachin", "person-maurice-thorez", "person-waldeck-rochet", "person-georges-marchais", "person-jacques-duclos"],
    sources: ["source-maitron-tours", "source-pcf-histoire"],
    dataStatus: "partially_verified",
    lastVerifiedAt: "2026-08-24"
  },
  {
    id: "party-front-populaire",
    name: "Front populaire",
    acronym: "FP",
    variants: ["Rassemblement populaire"],
    foundedAt: "1935",
    dissolvedAt: "1938",
    status: "coalition",
    description: "Coalition électorale et parlementaire associant notamment socialistes, radicaux et communistes dans les années 1930.",
    historicalNote: "Cette entrée est une coalition, pas un parti. Elle ne doit pas être confondue avec une organisation partisane unique.",
    families: ["family-socialisme", "family-radicalisme", "family-communisme"],
    founders: [],
    leaders: ["person-leon-blum"],
    sources: ["source-vie-publique-front-populaire", "source-assemblee-1936"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-mrp",
    name: "Mouvement républicain populaire",
    acronym: "MRP",
    variants: [],
    foundedAt: "1944",
    dissolvedAt: "1967",
    status: "historical",
    description: "Parti démocrate-chrétien issu de la Libération, central dans les coalitions de la IVe République et très engagé dans la construction européenne.",
    historicalNote: "Le MRP est un parti, distinct du PDP d'avant-guerre ; il reprend cependant une partie de traditions démocrates-chrétiennes et résistantes.",
    families: ["family-democratie-chretienne", "family-republicanisme", "family-liberalisme"],
    founders: [],
    leaders: ["person-georges-bidault", "person-robert-schuman", "person-pierre-pflimlin"],
    sources: ["source-francearchives-mrp"],
    dataStatus: "verified"
  },
  {
    id: "party-udsr",
    name: "Union démocratique et socialiste de la Résistance",
    acronym: "UDSR",
    variants: [],
    foundedAt: "1945-06-25",
    dissolvedAt: "1964",
    status: "historical",
    description: "Formation issue de la Résistance, située entre gauche non communiste et centre, jouant souvent un rôle charnière sous la IVe République.",
    historicalNote: "L'UDSR ne doit pas être confondue avec une hypothétique UDS fusionnée au MRP ; les sources consultées décrivent surtout l'UDSR comme parti autonome jusqu'à son effacement progressif.",
    families: ["family-republicanisme", "family-socialisme", "family-liberalisme"],
    founders: [],
    leaders: ["person-rene-pleven", "person-francois-mitterrand"],
    sources: ["source-francearchives-udsr", "source-sciencespo-udsr"],
    dataStatus: "verified"
  },
  {
    id: "party-rpf",
    name: "Rassemblement du peuple français",
    acronym: "RPF",
    variants: [],
    foundedAt: "1947-04-14",
    dissolvedAt: "1955",
    status: "historical",
    description: "Mouvement gaulliste fondé par Charles de Gaulle contre les institutions de la IVe République.",
    historicalNote: "Le RPF accepte des adhésions venues de plusieurs familles politiques ; son classement à droite doit être contextualisé par son projet institutionnel gaulliste.",
    families: ["family-gaullisme", "family-nationalisme", "family-republicanisme"],
    founders: ["person-charles-de-gaulle"],
    leaders: ["person-charles-de-gaulle", "person-jacques-soustelle", "person-andre-malraux"],
    sources: ["source-fondation-de-gaulle-rpf"],
    dataStatus: "verified"
  },
  {
    id: "party-mdc",
    name: "Mouvement des citoyens",
    acronym: "MDC",
    variants: ["Mouvement des citoyens - République"],
    foundedAt: "1993",
    dissolvedAt: "2003",
    status: "historical",
    description: "Formation républicaine et souverainiste de gauche fondée autour de Jean-Pierre Chevènement après la scission du courant Socialisme et République du Parti socialiste.",
    historicalNote: "Le MDC ne provient pas d'une scission du PCF : il est issu du Parti socialiste et de la transformation du Mouvement des citoyens créé en 1993.",
    families: ["family-socialisme", "family-republicanisme", "family-souverainisme"],
    founders: ["person-jean-pierre-chevenement"],
    leaders: ["person-jean-pierre-chevenement", "person-marie-noelle-lienemann"],
    sources: ["source-francearchives-mdc"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-gauche-plurielle",
    name: "Gauche plurielle",
    acronym: "GP",
    variants: ["Majorité plurielle"],
    foundedAt: "1997",
    dissolvedAt: "2002",
    status: "coalition",
    description: "Coalition parlementaire et gouvernementale réunissant principalement le PS, le PCF, les Verts, le PRG et le MDC autour du gouvernement de Lionel Jospin.",
    historicalNote: "La Gauche plurielle est une coalition, non un parti unique ; ses composantes conservent leurs organisations et leurs stratégies propres.",
    families: ["family-socialisme", "family-communisme", "family-ecologie-politique", "family-radicalisme", "family-souverainisme"],
    founders: [],
    leaders: ["person-lionel-jospin"],
    sources: ["source-vie-publique-gauche-plurielle"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-force-democrate",
    name: "Force démocrate",
    acronym: "FD",
    variants: [],
    foundedAt: "1995",
    dissolvedAt: "1998",
    status: "historical",
    description: "Formation démocrate-chrétienne issue du CDS et intégrée à la recomposition de l'UDF dans les années 1990.",
    historicalNote: "La chronologie fournie confond la création du CDS et celle de Force démocrate ; Force démocrate est ici placée dans la recomposition de 1995.",
    families: ["family-democratie-chretienne", "family-liberalisme"],
    founders: [],
    leaders: ["person-francois-bayrou", "person-pierre-mehaignerie"],
    sources: ["source-francearchives-democratie-liberale"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-democratie-liberale",
    name: "Démocratie libérale",
    acronym: "DL",
    variants: [],
    foundedAt: "1997",
    dissolvedAt: "2002",
    status: "historical",
    description: "Parti libéral créé par Alain Madelin après la transformation de la composante républicaine de l'UDF.",
    historicalNote: "La création est retenue en 1997, et non 1998 ; la séparation devient effective dans la recomposition de l'UDF après les législatives de 1997.",
    families: ["family-liberalisme", "family-conservatisme"],
    founders: ["person-alain-madelin"],
    leaders: ["person-alain-madelin"],
    sources: ["source-francearchives-democratie-liberale"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-rpf-1999",
    name: "Rassemblement pour la France",
    acronym: "RPF",
    variants: ["RPF-Le Rassemblement"],
    foundedAt: "1999",
    dissolvedAt: "2002",
    status: "historical",
    description: "Formation souverainiste fondée par Charles Pasqua et Philippe de Villiers après les élections européennes de 1999.",
    historicalNote: "Cette fiche est distincte du RPF gaulliste fondé par Charles de Gaulle en 1947.",
    families: ["family-gaullisme", "family-souverainisme", "family-conservatisme"],
    founders: ["person-charles-pasqua", "person-philippe-de-villiers"],
    leaders: ["person-charles-pasqua", "person-philippe-de-villiers"],
    sources: ["source-francearchives-rpf-1999"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-mpf",
    name: "Mouvement pour la France",
    acronym: "MPF",
    variants: [],
    foundedAt: "1994",
    dissolvedAt: "2018",
    status: "historical",
    description: "Parti souverainiste et conservateur fondé autour de Philippe de Villiers, opposé à une intégration européenne fédérale.",
    historicalNote: "Le MPF et le RPF de 1999 sont deux organisations distinctes, même si elles ont été liées par des alliances électorales.",
    families: ["family-souverainisme", "family-conservatisme", "family-nationalisme"],
    founders: ["person-philippe-de-villiers"],
    leaders: ["person-philippe-de-villiers"],
    sources: ["source-francearchives-mpf"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-cpnt",
    name: "Chasse, pêche, nature et traditions",
    acronym: "CPNT",
    variants: [],
    foundedAt: "1989",
    dissolvedAt: "2010",
    status: "historical",
    description: "Mouvement représentant les intérêts cynégétiques, halieutiques et ruraux, devenu une force électorale visible aux européennes de 1999.",
    historicalNote: "Le CPNT est une formation catégorielle et rurale ; il ne se confond pas avec un parti conservateur généraliste.",
    families: ["family-conservatisme", "family-regionalisme"],
    founders: ["person-jean-saint-josse"],
    leaders: ["person-jean-saint-josse"],
    sources: ["source-francearchives-cpnt"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-mnr",
    name: "Mouvement national républicain",
    acronym: "MNR",
    variants: [],
    foundedAt: "1999",
    dissolvedAt: null,
    status: "active",
    description: "Formation d'extrême droite fondée par Bruno Mégret après la scission du Front national.",
    historicalNote: "Le MNR est créé en 1999 après la crise interne du FN ; il ne doit pas être projeté comme une force déjà structurée au début de la décennie.",
    families: ["family-nationalisme", "family-conservatisme"],
    founders: ["person-bruno-megret"],
    leaders: ["person-bruno-megret"],
    sources: ["source-francearchives-mnr"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-ump",
    name: "Union pour un mouvement populaire",
    acronym: "UMP",
    variants: ["Union pour la majorité présidentielle"],
    foundedAt: "2002-11-17",
    dissolvedAt: "2015",
    status: "historical",
    description: "Parti de droite et de centre droit fondé pour réunir le RPR, Démocratie libérale et des composantes de la majorité présidentielle.",
    historicalNote: "L'UMP succède à l'Union pour la majorité présidentielle créée pour les législatives de 2002 ; elle devient Les Républicains en 2015.",
    families: ["family-gaullisme", "family-liberalisme", "family-conservatisme"],
    founders: ["person-alain-juppe"],
    leaders: ["person-jacques-chirac", "person-nicolas-sarkozy", "person-alain-juppe", "person-francois-fillon"],
    sources: ["source-vie-publique-ump"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-modem",
    name: "Mouvement démocrate",
    acronym: "MoDem",
    variants: ["Mouvement démocrate français"],
    foundedAt: "2007-12-01",
    dissolvedAt: null,
    status: "active",
    description: "Parti centriste créé par François Bayrou après la transformation de l'UDF, fondé sur un projet démocrate et pro-européen.",
    historicalNote: "Le MoDem est distingué de l'UDF, qui conserve une existence juridique et parlementaire résiduelle après 2007.",
    families: ["family-liberalisme", "family-republicanisme", "family-democratie-chretienne"],
    founders: ["person-francois-bayrou"],
    leaders: ["person-francois-bayrou"],
    sources: ["source-francearchives-modem"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-mrc",
    name: "Mouvement républicain et citoyen",
    acronym: "MRC",
    variants: [],
    foundedAt: "2003",
    dissolvedAt: null,
    status: "active",
    description: "Parti républicain et souverainiste issu de la transformation du Mouvement des citoyens autour de Jean-Pierre Chevènement.",
    historicalNote: "Le MRC succède au MDC en 2003 ; il ne doit pas être confondu avec le Mouvement républicain populaire ou avec le Mouvement des citoyens des années 1990.",
    families: ["family-souverainisme", "family-republicanisme", "family-socialisme"],
    founders: ["person-jean-pierre-chevenement"],
    leaders: ["person-jean-pierre-chevenement"],
    sources: ["source-francearchives-mrc"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-parti-gauche",
    name: "Parti de gauche",
    acronym: "PG",
    variants: [],
    foundedAt: "2008-11-29",
    dissolvedAt: "2018",
    status: "historical",
    description: "Parti de gauche fondé par Jean-Luc Mélenchon et des dissidents socialistes, engagé dans la construction du Front de gauche.",
    historicalNote: "Le Parti de gauche est issu d'une scission du PS et non du PCF ; son alliance avec le PCF intervient dans le Front de gauche à partir de 2009.",
    families: ["family-socialisme", "family-souverainisme", "family-republicanisme"],
    founders: ["person-jean-luc-melenchon"],
    leaders: ["person-jean-luc-melenchon"],
    sources: ["source-francearchives-front-gauche"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-front-gauche",
    name: "Front de gauche",
    acronym: "FdG",
    variants: ["Front de gauche pour changer d'Europe"],
    foundedAt: "2008-11-18",
    dissolvedAt: "2016",
    status: "coalition",
    description: "Alliance de gauche radicale réunissant notamment le PCF, le Parti de gauche et la Gauche unitaire.",
    historicalNote: "Le Front de gauche est une coalition électorale et politique, pas une fusion immédiate de ses composantes.",
    families: ["family-communisme", "family-socialisme", "family-souverainisme"],
    founders: ["person-jean-luc-melenchon", "person-marie-george-buffet"],
    leaders: ["person-jean-luc-melenchon", "person-marie-george-buffet"],
    sources: ["source-francearchives-front-gauche"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-npa",
    name: "Nouveau Parti anticapitaliste",
    acronym: "NPA",
    variants: [],
    foundedAt: "2009-02-05",
    dissolvedAt: null,
    status: "active",
    description: "Parti anticapitaliste créé dans le prolongement de la LCR et de comités unitaires rassemblant des courants révolutionnaires et militants sociaux.",
    historicalNote: "Le NPA ne résulte pas d'une fusion avec le Parti des travailleurs : il succède principalement à la LCR, avec des comités de soutien plus larges.",
    families: ["family-socialisme", "family-communisme", "family-ecologie-politique"],
    founders: ["person-olivier-besancenot"],
    leaders: ["person-olivier-besancenot", "person-philippe-poutou"],
    sources: ["source-francearchives-npa"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-europe-ecologie-2009",
    name: "Europe Écologie",
    acronym: "EE",
    variants: ["Europe Écologie 2009"],
    foundedAt: "2009",
    dissolvedAt: "2010",
    status: "coalition",
    description: "Rassemblement écologiste constitué pour les élections européennes de 2009 autour des Verts, de Daniel Cohn-Bendit et de personnalités associatives.",
    historicalNote: "Cette coalition de 2009 est distincte de la liste Europe Écologie de 1979 et du parti EELV créé en 2010.",
    families: ["family-ecologie-politique", "family-liberalisme"],
    founders: ["person-daniel-cohn-bendit", "person-cecile-duflot"],
    leaders: ["person-daniel-cohn-bendit", "person-cecile-duflot", "person-yannick-jadot"],
    sources: ["source-europe-ecologie-2009"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-eelv",
    name: "Europe Écologie Les Verts",
    acronym: "EELV",
    variants: ["Europe Écologie-Les Verts"],
    foundedAt: "2010-11-13",
    dissolvedAt: null,
    status: "active",
    description: "Parti écologiste issu de la fusion de la dynamique Europe Écologie et des Verts.",
    historicalNote: "La création intervient à la fin de la période demandée et marque la structuration durable de l'écologie politique autour d'une organisation commune.",
    families: ["family-ecologie-politique"],
    founders: ["person-cecile-duflot", "person-daniel-cohn-bendit"],
    leaders: ["person-cecile-duflot", "person-daniel-cohn-bendit"],
    sources: ["source-francearchives-eelv"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-lfi",
    name: "La France insoumise",
    acronym: "LFI",
    variants: ["La France insoumise - mouvement"],
    foundedAt: "2016-02-10",
    dissolvedAt: null,
    status: "active",
    description: "Mouvement de gauche radicale fondé autour de Jean-Luc Mélenchon, structuré par une plateforme programmatique et des campagnes présidentielles.",
    historicalNote: "LFI est un mouvement distinct du Parti de gauche et du Front de gauche, même si elle reprend une partie de leurs réseaux militants.",
    families: ["family-socialisme", "family-souverainisme", "family-ecologie-politique"],
    founders: ["person-jean-luc-melenchon"],
    leaders: ["person-jean-luc-melenchon", "person-manon-aubry", "person-alexis-corbiere", "person-clementine-autain"],
    sources: ["source-francearchives-lfi"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-lrem",
    name: "La République en marche",
    acronym: "LREM",
    variants: ["En marche !", "Renaissance"],
    foundedAt: "2016-04-06",
    dissolvedAt: null,
    status: "active",
    description: "Mouvement centriste et pro-européen fondé par Emmanuel Macron, devenu le principal parti de la majorité présidentielle après 2017.",
    historicalNote: "La fiche conserve LREM pour la période 2016-2020 ; les appellations ultérieures ne remplacent pas rétroactivement le nom historique.",
    families: ["family-liberalisme", "family-republicanisme"],
    founders: ["person-emmanuel-macron"],
    leaders: ["person-emmanuel-macron", "person-edouard-philippe", "person-christophe-castaner"],
    sources: ["source-francearchives-lrem"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-lr",
    name: "Les Républicains",
    acronym: "LR",
    variants: ["Les Républicains - parti"],
    foundedAt: "2015-05-30",
    dissolvedAt: null,
    status: "active",
    description: "Parti de droite succédant à l'UMP et réunissant des héritages gaullistes, libéraux et conservateurs.",
    historicalNote: "LR est le nouveau nom de l'UMP à partir de 2015 ; la fiche UMP reste la référence pour 2002-2015.",
    families: ["family-gaullisme", "family-liberalisme", "family-conservatisme"],
    founders: ["person-nicolas-sarkozy"],
    leaders: ["person-nicolas-sarkozy", "person-francois-fillon"],
    sources: ["source-francearchives-lr"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-dlf",
    name: "Debout la France",
    acronym: "DLF",
    variants: ["Debout la République"],
    foundedAt: "2008",
    dissolvedAt: null,
    status: "active",
    description: "Parti souverainiste et gaulliste social fondé par Nicolas Dupont-Aignan.",
    historicalNote: "Le mouvement est fondé sous le nom Debout la République avant de prendre le nom Debout la France en 2014.",
    families: ["family-gaullisme", "family-souverainisme", "family-conservatisme"],
    founders: ["person-nicolas-dupont-aignan"],
    leaders: ["person-nicolas-dupont-aignan"],
    sources: ["source-francearchives-dlf"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-upr",
    name: "Union populaire républicaine",
    acronym: "UPR",
    variants: [],
    foundedAt: "2007-03-25",
    dissolvedAt: null,
    status: "active",
    description: "Parti souverainiste prônant la sortie de l'Union européenne, de l'euro et de l'OTAN.",
    historicalNote: "L'UPR reste une formation marginale dans la période 2010-2020 et ne doit pas être assimilée à une composante de la droite parlementaire.",
    families: ["family-souverainisme", "family-gaullisme"],
    founders: ["person-francois-asselineau"],
    leaders: ["person-francois-asselineau"],
    sources: ["source-upr-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-rn",
    name: "Rassemblement national",
    acronym: "RN",
    variants: ["Front national", "FN"],
    foundedAt: "2018-06-01",
    dissolvedAt: null,
    status: "active",
    description: "Nom adopté par le Front national en 2018, dans le cadre de la stratégie de normalisation portée par Marine Le Pen.",
    historicalNote: "Le RN n'est pas rétro-projeté avant 2018 : les fiches électorales antérieures conservent le nom Front national.",
    families: ["family-nationalisme", "family-conservatisme"],
    founders: ["person-marine-le-pen"],
    leaders: ["person-marine-le-pen"],
    sources: ["source-francearchives-rn"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-parti-animaliste",
    name: "Parti animaliste",
    acronym: "PA",
    variants: [],
    foundedAt: "2016",
    dissolvedAt: null,
    status: "active",
    description: "Parti spécialisé dans la défense de la condition animale et la représentation des animaux dans les politiques publiques.",
    historicalNote: "Le Parti animaliste est distingué des partis écologistes généralistes et des associations de protection animale.",
    families: ["family-ecologie-politique"],
    founders: ["person-helene-thouy"],
    leaders: ["person-helene-thouy"],
    sources: ["source-parti-animaliste"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-nupes",
    name: "Nouvelle Union populaire écologique et sociale",
    acronym: "NUPES",
    variants: [],
    foundedAt: "2022",
    dissolvedAt: "2024",
    status: "coalition",
    description: "Coalition électorale réunissant LFI, EELV, le PS et le PCF autour des législatives de 2022.",
    historicalNote: "La NUPES est postérieure à la période 2010-2020 ; elle est conservée comme repère de continuité des recompositions engagées durant la décennie.",
    families: ["family-socialisme", "family-communisme", "family-ecologie-politique"],
    founders: ["person-jean-luc-melenchon"],
    leaders: ["person-jean-luc-melenchon"],
    sources: ["source-francearchives-nupes"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-reconquete",
    name: "Reconquête",
    acronym: "REC",
    variants: [],
    foundedAt: "2021",
    dissolvedAt: null,
    status: "active",
    description: "Formation nationaliste fondée par Éric Zemmour après la période 2010-2020.",
    historicalNote: "Reconquête n'est pas un parti actif durant la décennie demandée ; cette fiche documente explicitement le prolongement postérieur évoqué dans la liste.",
    families: ["family-nationalisme", "family-conservatisme", "family-souverainisme"],
    founders: ["person-eric-zemmour"],
    leaders: ["person-eric-zemmour"],
    sources: ["source-francearchives-rn"],
    dataStatus: "unverified"
  },
  {
    id: "party-cap21",
    name: "Citoyenneté, action, participation pour le XXIe siècle",
    acronym: "Cap21",
    variants: ["CAP 21"],
    foundedAt: "1996",
    dissolvedAt: null,
    status: "active",
    description: "Formation écologiste et centriste fondée par Corinne Lepage, présente notamment dans la campagne présidentielle de 2002.",
    historicalNote: "Cap21 est distingué des Verts et d'Europe Écologie ; il se situe dans une écologie réformiste et pro-européenne.",
    families: ["family-ecologie-politique", "family-liberalisme"],
    founders: ["person-corinne-lepage"],
    leaders: ["person-corinne-lepage"],
    sources: ["source-francearchives-verts"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-attac",
    name: "Association pour la taxation des transactions financières et pour l'action citoyenne",
    acronym: "ATTAC",
    variants: [],
    foundedAt: "1998",
    dissolvedAt: null,
    status: "association",
    description: "Association altermondialiste mobilisée contre la financiarisation et pour une régulation démocratique de la mondialisation.",
    historicalNote: "ATTAC n'est pas un parti politique ; elle est incluse comme organisation associative majeure des mobilisations des années 2000.",
    families: ["family-socialisme", "family-souverainisme"],
    founders: [],
    leaders: [],
    sources: ["source-attac-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-republicains-sociaux",
    name: "Républicains sociaux",
    acronym: "RS",
    variants: ["Centre national des républicains sociaux"],
    foundedAt: "1954",
    dissolvedAt: "1958",
    status: "historical",
    description: "Formation gaulliste issue du déclin du RPF et présente avant la création de l'UNR.",
    historicalNote: "Cette fiche sert à représenter la transition entre le RPF et l'UNR ; ses contours exacts devront être précisés par des sources archivistiques dédiées.",
    families: ["family-gaullisme"],
    founders: [],
    leaders: ["person-jacques-chaban-delmas"],
    sources: ["source-fondation-de-gaulle-rpf", "source-francearchives-unr"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-cnip",
    name: "Centre national des indépendants et paysans",
    acronym: "CNIP",
    variants: ["Centre national des indépendants", "CNI"],
    foundedAt: "1949",
    dissolvedAt: null,
    status: "active",
    description: "Formation de droite libérale et conservatrice fédérant indépendants, paysans et anciennes composantes modérées de droite.",
    historicalNote: "Créé d'abord comme CNI, il devient CNIP après intégrations successives, notamment du Parti paysan et du PRL.",
    families: ["family-liberalisme", "family-conservatisme"],
    founders: ["person-roger-duchet"],
    leaders: ["person-roger-duchet", "person-antoine-pinay", "person-valery-giscard-destaing"],
    sources: ["source-francearchives-cnip", "source-theses-cnip"],
    dataStatus: "partially_verified",
    lastVerifiedAt: "2026-08-24"
  },
  {
    id: "party-prl",
    name: "Parti républicain de la liberté",
    acronym: "PRL",
    variants: [],
    foundedAt: "1946",
    dissolvedAt: "1951",
    status: "historical",
    description: "Parti de droite libérale de l'après-Libération, absorbé dans les recompositions autour du CNIP.",
    historicalNote: "Georges Mandel, assassiné en 1944, ne peut pas être dirigeant du PRL ; cette fiche corrige cette attribution fréquente dans des listes rapides.",
    families: ["family-liberalisme", "family-conservatisme"],
    founders: [],
    leaders: ["person-michel-clemenceau", "person-pierre-de-gaulle"],
    sources: ["source-openedition-prl"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-front-republicain-1956",
    name: "Front républicain",
    acronym: "FR1956",
    variants: ["Front républicain de 1956"],
    foundedAt: "1956",
    dissolvedAt: "1956",
    status: "coalition",
    description: "Coalition électorale de 1956 autour de la SFIO, des radicaux, de l'UDSR et de courants mendésistes.",
    historicalNote: "Le PCF n'est pas traité comme membre de cette coalition ; il reste une force parlementaire distincte dans le contexte de guerre froide.",
    families: ["family-socialisme", "family-radicalisme", "family-republicanisme"],
    founders: [],
    leaders: ["person-guy-mollet", "person-pierre-mendes-france", "person-francois-mitterrand"],
    sources: ["source-francearchives-udsr"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-psa",
    name: "Parti socialiste autonome",
    acronym: "PSA",
    variants: [],
    foundedAt: "1958-09-11",
    dissolvedAt: "1960-04-03",
    status: "historical",
    description: "Scission de la SFIO hostile à la politique algérienne de Guy Mollet et au soutien socialiste au retour de De Gaulle.",
    historicalNote: "La fondation est située en 1958, non en 1955. Le PSA fusionne en 1960 dans le PSU.",
    families: ["family-socialisme"],
    founders: ["person-edouard-depreux"],
    leaders: ["person-edouard-depreux", "person-alain-savary"],
    sources: ["source-francearchives-psa", "source-maitron-depreux"],
    dataStatus: "verified"
  },
  {
    id: "party-psu",
    name: "Parti socialiste unifié",
    acronym: "PSU",
    variants: [],
    foundedAt: "1960-04-03",
    dissolvedAt: "1989",
    status: "historical",
    description: "Parti de la nouvelle gauche fondé par fusion du PSA, de l'UGS et de groupes issus ou proches du communisme oppositionnel.",
    historicalNote: "Le PSU apparaît à la toute fin de la période demandée ; Pierre Mendès France le rejoint comme figure majeure après son départ du Parti radical.",
    families: ["family-socialisme", "family-republicanisme"],
    founders: ["person-edouard-depreux"],
    leaders: ["person-edouard-depreux", "person-pierre-mendes-france", "person-michel-rocard"],
    sources: ["source-francearchives-psu", "source-maitron-depreux"],
    dataStatus: "verified"
  },
  {
    id: "party-pci-trotskiste",
    name: "Parti communiste internationaliste",
    acronym: "PCI",
    variants: ["PCI trotskiste"],
    foundedAt: "1944",
    dissolvedAt: null,
    status: "movement",
    description: "Organisation trotskiste marginale de l'après-guerre, issue de recompositions de courants se réclamant de la IVe Internationale.",
    historicalNote: "Les effectifs et épisodes judiciaires de 1952 demandent des sources complémentaires avant intégration détaillée.",
    families: ["family-communisme", "family-socialisme"],
    founders: [],
    leaders: ["person-pierre-frank", "person-marcel-bleibtreu", "person-michel-raptis"],
    sources: ["source-maitron-bleibtreu"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-jeune-nation",
    name: "Jeune Nation",
    acronym: "JN",
    variants: [],
    foundedAt: "1949",
    dissolvedAt: "1958",
    status: "movement",
    description: "Groupuscule nationaliste et néofasciste de l'après-guerre, actif dans le contexte de la guerre d'Algérie.",
    historicalNote: "La liste fournie évoquait un Mouvement national français de 1954 ; les sources consultées pointent plutôt Jeune Nation comme formation structurante autour de Pierre Sidos. L'entrée MNF n'est donc pas créée sans source plus solide.",
    families: ["family-nationalisme", "family-fascisme"],
    founders: ["person-pierre-sidos"],
    leaders: ["person-pierre-sidos"],
    sources: ["source-jeune-nation-sidos"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-oas",
    name: "Organisation armée secrète",
    acronym: "OAS",
    variants: ["Organisation de l'armée secrète"],
    foundedAt: "1961-02-11",
    dissolvedAt: "1962",
    status: "organization",
    description: "Organisation clandestine et terroriste opposée à l'indépendance de l'Algérie.",
    historicalNote: "L'OAS est hors période 1950-1960 : elle est ajoutée comme prolongement immédiat des crises algériennes, mais sa fondation est datée de 1961.",
    families: ["family-nationalisme"],
    founders: ["person-jean-jacques-susini", "person-pierre-lagaillarde"],
    leaders: ["person-raoul-salan", "person-jean-jacques-susini"],
    sources: ["source-oas-herodote"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-cir",
    name: "Convention des institutions républicaines",
    acronym: "CIR",
    variants: [],
    foundedAt: "1964",
    dissolvedAt: "1971",
    status: "historical",
    description: "Organisation de gauche républicaine fondée autour de François Mitterrand contre les institutions gaulliennes.",
    historicalNote: "La CIR n'est pas une scission de la SFIO : François Mitterrand n'en était pas membre en 1958. Elle participe à la FGDS puis rejoint le Parti socialiste au congrès d'Épinay en 1971.",
    families: ["family-republicanisme", "family-socialisme"],
    founders: ["person-francois-mitterrand"],
    leaders: ["person-francois-mitterrand"],
    sources: ["source-archives-socialistes-1969"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-fgds",
    name: "Fédération de la gauche démocrate et socialiste",
    acronym: "FGDS",
    variants: [],
    foundedAt: "1965",
    dissolvedAt: "1968",
    status: "coalition",
    description: "Fédération de la gauche non communiste rassemblée autour de la candidature de François Mitterrand puis des législatives de 1967.",
    historicalNote: "La FGDS associe notamment SFIO, radicaux, CIR et clubs ; le PSU soutient des dynamiques communes mais ne doit pas être présenté comme fusionné dans la FGDS.",
    families: ["family-socialisme", "family-radicalisme", "family-republicanisme"],
    founders: ["person-francois-mitterrand"],
    leaders: ["person-francois-mitterrand", "person-guy-mollet", "person-gaston-defferre"],
    sources: ["source-archives-socialistes-1969"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-centre-democrate",
    name: "Centre démocrate",
    acronym: "CD",
    variants: [],
    foundedAt: "1966",
    dissolvedAt: "1976",
    status: "historical",
    description: "Parti centriste et démocrate-chrétien fondé par Jean Lecanuet après l'élection présidentielle de 1965.",
    historicalNote: "Il n'est pas fondé en 1956 par fusion MRP-UDS ; les sources consultées situent sa création en 1966.",
    families: ["family-democratie-chretienne", "family-liberalisme", "family-republicanisme"],
    founders: ["person-jean-lecanuet"],
    leaders: ["person-jean-lecanuet", "person-alain-poher"],
    sources: ["source-francearchives-centre-democrate", "source-persee-centre-democrate"],
    dataStatus: "verified"
  },
  {
    id: "party-centre-democratie-progres",
    name: "Centre démocratie et progrès",
    acronym: "CDP",
    variants: [],
    foundedAt: "1969",
    dissolvedAt: "1976",
    status: "historical",
    description: "Scission centriste du Centre démocrate favorable à un rapprochement avec la majorité présidentielle après 1969.",
    historicalNote: "Cette entrée corrige la confusion avec le CDS : le Centre des démocrates sociaux est créé en 1976, pas en 1969.",
    families: ["family-democratie-chretienne", "family-liberalisme"],
    founders: [],
    leaders: [],
    sources: ["source-francearchives-centre-democrate", "source-persee-centre-democrate"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-fnri",
    name: "Fédération nationale des républicains indépendants",
    acronym: "FNRI",
    variants: ["Républicains indépendants", "RI"],
    foundedAt: "1966-06-01",
    dissolvedAt: "1977-05-20",
    status: "historical",
    description: "Formation libérale de centre droit conduite par Valéry Giscard d'Estaing, issue d'une scission du CNIP.",
    historicalNote: "La liste utilisateur parlait de Force démocratique ; le nom documenté pour le courant giscardien est Républicains indépendants puis FNRI.",
    families: ["family-liberalisme", "family-conservatisme"],
    founders: ["person-valery-giscard-destaing"],
    leaders: ["person-valery-giscard-destaing"],
    sources: ["source-persee-republicains-independants"],
    dataStatus: "verified"
  },
  {
    id: "party-udr",
    name: "Union des démocrates pour la République",
    acronym: "UDR",
    variants: ["Union des démocrates pour la Ve République", "UD-Ve"],
    foundedAt: "1967-11-24",
    dissolvedAt: "1976-12-05",
    status: "historical",
    description: "Parti gaulliste succédant à l'UNR-UDT et dominant la majorité parlementaire après 1968.",
    historicalNote: "L'UDR n'est pas une fusion simple de l'UNR avec la FNRI ; elle correspond à une transformation de la formation gaulliste.",
    families: ["family-gaullisme", "family-conservatisme", "family-republicanisme"],
    founders: [],
    leaders: ["person-georges-pompidou", "person-jacques-chaban-delmas", "person-roger-frey"],
    sources: ["source-francearchives-unr"],
    dataStatus: "verified"
  },
  {
    id: "party-ps-1969",
    name: "Parti socialiste",
    acronym: "PS",
    variants: ["Nouveau Parti socialiste"],
    foundedAt: "1969-07-13",
    dissolvedAt: null,
    status: "active",
    description: "Parti né de la transformation de la SFIO et de l'Union des clubs pour le renouveau de la gauche au congrès d'Issy-les-Moulineaux.",
    historicalNote: "Le PSU ne fusionne pas dans le PS en 1969 ; la CIR rejoint le Parti socialiste plus tard, au congrès d'Épinay de 1971.",
    families: ["family-socialisme", "family-republicanisme"],
    founders: [],
    leaders: ["person-guy-mollet", "person-gaston-defferre"],
    sources: ["source-archives-socialistes-1969"],
    dataStatus: "verified",
    lastVerifiedAt: "2026-08-24"
  },
  {
    id: "party-mouvement-22-mars",
    name: "Mouvement du 22 mars",
    acronym: "M22",
    variants: [],
    foundedAt: "1968-03-22",
    dissolvedAt: "1968",
    status: "movement",
    description: "Mouvement étudiant né à Nanterre, associé au déclenchement de Mai 68.",
    historicalNote: "Il s'agit d'un mouvement étudiant et politique, pas d'un parti électoral.",
    families: ["family-socialisme", "family-anarchisme"],
    founders: ["person-daniel-cohn-bendit"],
    leaders: ["person-daniel-cohn-bendit"],
    sources: ["source-persee-mouvement-22-mars", "source-mahj-cohn-bendit"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-gauche-proletarienne",
    name: "Gauche prolétarienne",
    acronym: "GP",
    variants: [],
    foundedAt: "1968",
    dissolvedAt: "1973",
    status: "movement",
    description: "Organisation maoïste née après Mai 68 dans les recompositions de l'extrême gauche.",
    historicalNote: "La GP ne doit pas être confondue avec le Mouvement du 22 mars ; elle appartient surtout à l'après-Mai 68.",
    families: ["family-communisme", "family-socialisme"],
    founders: ["person-benny-levy"],
    leaders: ["person-benny-levy", "person-alain-geismar"],
    sources: ["source-paris8-gauche-proletarienne", "source-gis-gauche-proletarienne"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-ligue-communiste",
    name: "Ligue communiste",
    acronym: "LC",
    variants: [],
    foundedAt: "1969",
    dissolvedAt: "1973",
    status: "movement",
    description: "Organisation trotskiste issue de la Jeunesse communiste révolutionnaire et de recompositions autour d'Alain Krivine.",
    historicalNote: "Elle succède aux organisations dissoutes après Mai 68 et précède la LCR ; elle ne doit pas être confondue avec le PCI trotskiste.",
    families: ["family-communisme", "family-socialisme"],
    founders: ["person-alain-krivine"],
    leaders: ["person-alain-krivine"],
    sources: ["source-maitron-krivine"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-parti-nationaliste",
    name: "Parti nationaliste",
    acronym: "PN",
    variants: [],
    foundedAt: "1959",
    dissolvedAt: "1960",
    status: "movement",
    description: "Petit mouvement nationaliste lié aux milieux d'extrême droite de l'après-guerre.",
    historicalNote: "La datation et la forme organisationnelle restent insuffisamment sourcées ; cette entrée est conservée comme donnée à vérifier.",
    families: ["family-nationalisme"],
    founders: ["person-pierre-sidos"],
    leaders: ["person-pierre-sidos"],
    sources: ["source-jeune-nation-sidos"],
    dataStatus: "unverified"
  },
  {
    id: "party-amis-terre",
    name: "Les Amis de la Terre",
    acronym: "ADT",
    variants: [],
    foundedAt: "1970",
    dissolvedAt: null,
    status: "association",
    description: "Association environnementale relevant des préfigurations de l'écologie politique française.",
    historicalNote: "Ce n'est pas un parti politique et son apparition se situe à la fin ou juste après la décennie 1960.",
    families: ["family-ecologie-politique"],
    founders: [],
    leaders: ["person-rene-dumont"],
    sources: ["source-parti-occitan"],
    dataStatus: "unverified"
  },
  {
    id: "party-pno",
    name: "Partit de la Nacion Occitana",
    acronym: "PNO",
    variants: ["Parti nationaliste occitan"],
    foundedAt: "1959",
    dissolvedAt: null,
    status: "movement",
    description: "Mouvement nationaliste occitan fondé autour de François Fontan.",
    historicalNote: "Il ne doit pas être confondu avec le Partit Occitan actuel, fondé en 1987.",
    families: ["family-regionalisme"],
    founders: ["person-francois-fontan"],
    leaders: ["person-francois-fontan"],
    sources: ["source-pno", "source-parti-occitan"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-lcr",
    name: "Ligue communiste révolutionnaire",
    acronym: "LCR",
    variants: ["Front communiste révolutionnaire"],
    foundedAt: "1974",
    dissolvedAt: "2009",
    status: "historical",
    description: "Organisation trotskiste française constituée après la dissolution de la Ligue communiste.",
    historicalNote: "La LCR relève de l'extrême gauche révolutionnaire et doit être distinguée de Lutte ouvrière, qui porte la candidature d'Arlette Laguiller en 1974.",
    families: ["family-communisme", "family-socialisme"],
    founders: ["person-alain-krivine"],
    leaders: ["person-alain-krivine"],
    sources: ["source-maitron-krivine"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-lutte-ouvriere",
    name: "Lutte ouvrière",
    acronym: "LO",
    variants: ["Union communiste internationaliste"],
    foundedAt: "1968",
    dissolvedAt: null,
    status: "active",
    description: "Organisation trotskiste connue sous le nom Lutte ouvrière à partir de la fin des années 1960.",
    historicalNote: "Arlette Laguiller est candidate de Lutte ouvrière à la présidentielle de 1974 ; elle ne relève pas de la LCR.",
    families: ["family-communisme", "family-socialisme"],
    founders: [],
    leaders: ["person-arlette-laguiller"],
    sources: ["source-lo-histoire", "source-ina-lutte-ouvriere-1974"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-programme-commun-gauche",
    name: "Programme commun de la gauche",
    acronym: "Programme commun",
    variants: ["Programme commun de gouvernement"],
    foundedAt: "1972-06-27",
    dissolvedAt: "1977",
    status: "coalition",
    description: "Accord politique et programmatique conclu entre le Parti socialiste, le Parti communiste français et les radicaux de gauche.",
    historicalNote: "Il s'agit d'une alliance-programme et non d'un parti ; la rupture est située en 1977 dans les négociations d'actualisation, avant les législatives de 1978.",
    families: ["family-socialisme", "family-communisme", "family-radicalisme"],
    founders: [],
    leaders: ["person-francois-mitterrand", "person-georges-marchais", "person-robert-fabre"],
    sources: ["source-archives-socialistes-1969", "source-pcf-histoire", "source-prg-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-prg",
    name: "Parti radical de gauche",
    acronym: "PRG",
    variants: ["Mouvement des radicaux de gauche", "MRG", "Radicaux de gauche"],
    foundedAt: "1972",
    dissolvedAt: null,
    status: "active",
    description: "Formation radicale de centre gauche issue de la scission du Parti radical autour du choix de l'union de la gauche.",
    historicalNote: "La direction de cette composante est associée à Robert Fabre puis Michel Crépeau ; Jean-Jacques Servan-Schreiber relève plutôt du radicalisme valoisien et centriste.",
    families: ["family-radicalisme", "family-liberalisme", "family-republicanisme"],
    founders: ["person-robert-fabre"],
    leaders: ["person-robert-fabre", "person-michel-crepeau"],
    sources: ["source-prg-histoire", "source-francearchives-parti-radical"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-parti-radical-valoisien",
    name: "Parti radical valoisien",
    acronym: "RAD",
    variants: ["Parti radical", "Parti radical dit valoisien"],
    foundedAt: "1972",
    dissolvedAt: null,
    status: "active",
    description: "Continuité centriste du Parti radical après la scission des radicaux de gauche au début des années 1970.",
    historicalNote: "Cette fiche évite de confondre le Parti radical historique, la composante radicale de gauche et le Parti républicain libéral de 1977.",
    families: ["family-radicalisme", "family-liberalisme", "family-republicanisme"],
    founders: [],
    leaders: ["person-jean-jacques-servan-schreiber"],
    sources: ["source-francearchives-parti-radical", "source-prg-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-cds",
    name: "Centre des démocrates sociaux",
    acronym: "CDS",
    variants: ["Centre démocrate et social"],
    foundedAt: "1976",
    dissolvedAt: "1995",
    status: "historical",
    description: "Parti centriste démocrate-chrétien créé par le rapprochement du Centre démocrate et du Centre démocratie et progrès.",
    historicalNote: "La création du CDS est située en 1976 ; elle ne doit pas être confondue avec le Centre démocrate fondé par Jean Lecanuet en 1966.",
    families: ["family-democratie-chretienne", "family-liberalisme", "family-republicanisme"],
    founders: ["person-jean-lecanuet"],
    leaders: ["person-jean-lecanuet", "person-jacques-duhamel", "person-pierre-mehaignerie"],
    sources: ["source-francearchives-cds"],
    dataStatus: "verified"
  },
  {
    id: "party-parti-republicain-1977",
    name: "Parti républicain",
    acronym: "PR",
    variants: ["Parti républicain giscardien"],
    foundedAt: "1977-05-20",
    dissolvedAt: "1997",
    status: "historical",
    description: "Parti libéral de centre droit issu de la transformation des Républicains indépendants.",
    historicalNote: "Il ne provient pas d'une scission directe du CNIP en 1977 : il prolonge surtout la FNRI et les réseaux giscardiens.",
    families: ["family-liberalisme", "family-conservatisme"],
    founders: ["person-valery-giscard-destaing"],
    leaders: ["person-jean-pierre-soisson", "person-francois-leotard"],
    sources: ["source-perspective-ri", "source-francearchives-udf"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-udf",
    name: "Union pour la démocratie française",
    acronym: "UDF",
    variants: [],
    foundedAt: "1978-02-01",
    dissolvedAt: "2007",
    status: "historical",
    description: "Confédération centriste et libérale créée autour de Valéry Giscard d'Estaing pour rassembler les forces non gaullistes de la majorité.",
    historicalNote: "L'UDF regroupe notamment le Parti républicain, le CDS et le Parti radical valoisien ; elle est une confédération électorale et partisane, pas un simple renommage de la FNRI.",
    families: ["family-liberalisme", "family-democratie-chretienne", "family-radicalisme"],
    founders: ["person-valery-giscard-destaing"],
    leaders: ["person-valery-giscard-destaing", "person-jean-lecanuet", "person-jean-pierre-fourcade"],
    sources: ["source-francearchives-udf"],
    dataStatus: "verified"
  },
  {
    id: "party-rpr",
    name: "Rassemblement pour la République",
    acronym: "RPR",
    variants: [],
    foundedAt: "1976-12-05",
    dissolvedAt: "2002",
    status: "historical",
    description: "Parti gaulliste fondé par Jacques Chirac après la transformation de l'UDR.",
    historicalNote: "Le RPR succède à l'UDR et devient le principal parti gaulliste de la majorité et de la concurrence avec les giscardiens.",
    families: ["family-gaullisme", "family-conservatisme", "family-republicanisme"],
    founders: ["person-jacques-chirac"],
    leaders: ["person-jacques-chirac"],
    sources: ["source-francearchives-rpr"],
    dataStatus: "verified"
  },
  {
    id: "party-fn",
    name: "Front national",
    acronym: "FN",
    variants: ["Front national pour l'unité française", "Rassemblement national"],
    foundedAt: "1972-10-05",
    dissolvedAt: null,
    status: "active",
    description: "Parti d'extrême droite fondé au début des années 1970 et présidé durablement par Jean-Marie Le Pen.",
    historicalNote: "L'entrée conserve le nom historique Front national pour la période 1970-1980 ; le nom Rassemblement national relève d'une période ultérieure.",
    families: ["family-nationalisme", "family-conservatisme"],
    founders: ["person-jean-marie-le-pen"],
    leaders: ["person-jean-marie-le-pen"],
    sources: ["source-fondation-jean-jaures-le-pen"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-pfn",
    name: "Parti des forces nouvelles",
    acronym: "PFN",
    variants: [],
    foundedAt: "1974",
    dissolvedAt: "1981",
    status: "historical",
    description: "Formation d'extrême droite issue de dissidences et concurrences internes au Front national naissant.",
    historicalNote: "La donnée reste à consolider par une source spécialisée ; elle est intégrée comme acteur marginal de la décennie.",
    families: ["family-nationalisme", "family-conservatisme"],
    founders: ["person-pascal-gauchon"],
    leaders: ["person-pascal-gauchon"],
    sources: ["source-fondation-jean-jaures-le-pen"],
    dataStatus: "unverified"
  },
  {
    id: "party-parti-travailleurs",
    name: "Parti des travailleurs",
    acronym: "PT",
    variants: ["Parti des travailleurs indépendant", "Parti des travailleurs français"],
    foundedAt: "1991",
    dissolvedAt: "2015",
    status: "historical",
    description: "Formation issue de la tradition lambertiste et du Mouvement pour un parti des travailleurs.",
    historicalNote: "La fiche corrige la chronologie fournie : le PT est créé en 1991. Dans les années 1980, la formation correspondante est le MPPT, fondé en 1985 ; Daniel Gluckstein en devient ensuite une figure dirigeante.",
    families: ["family-communisme", "family-socialisme"],
    founders: [],
    leaders: ["person-daniel-gluckstein"],
    sources: ["source-maitron-parti-travailleurs"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-mppt",
    name: "Mouvement pour un parti des travailleurs",
    acronym: "MPPT",
    variants: ["Mouvement pour le parti des travailleurs"],
    foundedAt: "1985",
    dissolvedAt: "1991",
    status: "historical",
    description: "Organisation politique issue du courant lambertiste et prédécesseur du Parti des travailleurs.",
    historicalNote: "Cette formation est retenue pour représenter la présence du courant auquel la liste utilisateur rattache anachroniquement le PT dans les années 1980.",
    families: ["family-communisme", "family-socialisme"],
    founders: [],
    leaders: ["person-daniel-gluckstein"],
    sources: ["source-maitron-parti-travailleurs"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-verts",
    name: "Les Verts",
    acronym: "Les Verts",
    variants: ["Parti écologiste", "Les Verts-Confédération écologiste"],
    foundedAt: "1984",
    dissolvedAt: "2010",
    status: "historical",
    description: "Parti écologiste français structuré en 1984 autour de la défense de l'environnement, de l'antinucléarisme et de pratiques politiques alternatives.",
    historicalNote: "Les Verts sont distingués des listes écologistes antérieures, notamment Europe Écologie de 1979, et de leur successeur Europe Écologie Les Verts.",
    families: ["family-ecologie-politique"],
    founders: [],
    leaders: ["person-brice-lalonde", "person-noel-mamere", "person-antoine-waechter"],
    sources: ["source-francearchives-verts"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-pnf-1983",
    name: "Parti nationaliste français",
    acronym: "PNF",
    variants: [],
    foundedAt: "1983",
    dissolvedAt: null,
    status: "movement",
    description: "Mouvement nationaliste d'extrême droite marginal apparu dans les années 1980.",
    historicalNote: "La chronologie et l'identification de ses fondateurs sont conservées avec prudence ; cette entrée ne doit pas être confondue avec le PFN des années 1970.",
    families: ["family-nationalisme", "family-conservatisme"],
    founders: ["person-pascal-gauchon"],
    leaders: ["person-pascal-gauchon"],
    sources: ["source-fondation-jean-jaures-fn-1984"],
    dataStatus: "unverified"
  },
  {
    id: "party-generation-ecologie",
    name: "Génération écologie",
    acronym: "GE",
    variants: [],
    foundedAt: "1990",
    dissolvedAt: null,
    status: "active",
    description: "Formation écologiste créée à la fin de la décennie autour de Brice Lalonde.",
    historicalNote: "La création intervient en 1990, à la limite de la période demandée ; elle est incluse comme transition vers la décennie suivante.",
    families: ["family-ecologie-politique", "family-liberalisme"],
    founders: ["person-brice-lalonde"],
    leaders: ["person-brice-lalonde"],
    sources: ["source-francearchives-verts"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-parti-occitan-1987",
    name: "Parti occitan",
    acronym: "POc",
    variants: ["Partit Occitan"],
    foundedAt: "1987",
    dissolvedAt: null,
    status: "active",
    description: "Parti régionaliste occitan défendant l'autonomie, la reconnaissance de la langue et une transformation démocratique des institutions.",
    historicalNote: "Il est distingué du Partit de la Nacion Occitana, fondé en 1959, qui relève d'une autre tradition occitaniste.",
    families: ["family-regionalisme"],
    founders: [],
    leaders: [],
    sources: ["source-parti-occitan-1987"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-cuncolta-naziunale",
    name: "A Cuncolta Naziunale",
    acronym: "ACN",
    variants: ["A Cuncolta", "A Cuncolta Naziunale Corsa"],
    foundedAt: "1987",
    dissolvedAt: null,
    status: "movement",
    description: "Mouvement nationaliste corse apparu dans la recomposition autonomiste et indépendantiste de la fin des années 1980.",
    historicalNote: "Cette entrée décrit un mouvement régionaliste corse et non un parti national de gouvernement.",
    families: ["family-regionalisme", "family-nationalisme"],
    founders: [],
    leaders: [],
    sources: ["source-cuncolta-naziunale"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-choisir-cause-femmes",
    name: "Choisir - la cause des femmes",
    acronym: "Choisir",
    variants: ["Choisir la cause des femmes"],
    foundedAt: "1971",
    dissolvedAt: null,
    status: "association",
    description: "Association féministe engagée pour les droits reproductifs, l'égalité et la défense des femmes.",
    historicalNote: "Ce n'est pas un parti politique ; l'association est incluse pour représenter les mobilisations féministes structurantes des années 1980.",
    families: ["family-republicanisme"],
    founders: ["person-gisele-halimi", "person-simone-de-beauvoir"],
    leaders: ["person-gisele-halimi"],
    sources: ["source-choisir-feminisme"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-mlac",
    name: "Mouvement pour la liberté de l'avortement et de la contraception",
    acronym: "MLAC",
    variants: [],
    foundedAt: "1973",
    dissolvedAt: "1975",
    status: "association",
    description: "Mouvement militant pour le droit à l'avortement et à la contraception dans la séquence précédant la loi Veil.",
    historicalNote: "Le MLAC n'est pas un parti politique ; il est conservé comme organisation sociale majeure du paysage politique des années 1970.",
    families: ["family-republicanisme"],
    founders: ["person-gisele-halimi", "person-simone-de-beauvoir"],
    leaders: ["person-gisele-halimi", "person-simone-de-beauvoir"],
    sources: ["source-mlac-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-europe-ecologie-1979",
    name: "Europe Écologie",
    acronym: "EE",
    variants: ["Europe Écologie 1979"],
    foundedAt: "1979",
    dissolvedAt: "1979",
    status: "coalition",
    description: "Liste écologiste présentée lors des premières élections européennes au suffrage universel direct.",
    historicalNote: "Cette entrée désigne une liste électorale écologiste de 1979, pas le parti Europe Écologie-Les Verts créé plusieurs décennies plus tard.",
    families: ["family-ecologie-politique"],
    founders: [],
    leaders: ["person-rene-dumont"],
    sources: ["source-europe-ecologie-1979"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-unr",
    name: "Union pour la nouvelle République",
    acronym: "UNR",
    variants: [],
    foundedAt: "1958",
    dissolvedAt: "1962-11-10",
    status: "historical",
    description: "Formation gaulliste créée au moment de l'installation de la Ve République et des élections législatives de novembre 1958.",
    historicalNote: "L'UNR regroupe la plupart des mouvements gaullistes de 1958 ; le 10 novembre 1962, son rapprochement avec l'Union démocratique du travail conduit à l'appellation UNR-UDT.",
    families: ["family-gaullisme"],
    founders: [],
    leaders: ["person-charles-de-gaulle", "person-georges-pompidou", "person-jacques-chaban-delmas"],
    sources: ["source-vie-publique-1958", "source-francearchives-unr", "source-bnf-unr-udt"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-udt",
    name: "Union démocratique du travail",
    acronym: "UDT",
    variants: [],
    foundedAt: "1959",
    dissolvedAt: "1962-11-10",
    status: "historical",
    description: "Formation des gaullistes de gauche qui se rapproche de l'UNR lors des élections législatives de 1962.",
    historicalNote: "L'UDT constitue une composante distincte du gaullisme de gauche avant sa fusion avec l'UNR ; elle ne doit pas être confondue avec le groupe parlementaire UNR-UDT.",
    families: ["family-gaullisme"],
    founders: [],
    leaders: [],
    sources: ["source-bnf-unr-udt", "source-assemblee-unr-udt"],
    dataStatus: "verified"
  },
  {
    id: "party-unr-udt",
    name: "Union pour la nouvelle République - Union démocratique du travail",
    acronym: "UNR-UDT",
    variants: ["Union pour la nouvelle République-UDT"],
    foundedAt: "1962-11-10",
    dissolvedAt: "1967-11-24",
    status: "historical",
    description: "Formation et groupe parlementaire gaullistes issus du rapprochement de l'UNR et de l'UDT entre 1962 et 1967.",
    historicalNote: "L'appellation UNR-UDT désigne à la fois la formation gaulliste recomposée et son groupe parlementaire durant la IIe législature ; elle précède l'Union des démocrates pour la République.",
    families: ["family-gaullisme"],
    founders: [],
    leaders: ["person-charles-de-gaulle", "person-georges-pompidou", "person-jacques-chaban-delmas"],
    sources: ["source-bnf-unr-udt", "source-assemblee-unr-udt"],
    dataStatus: "verified"
  },
  {
    id: "party-pdp",
    name: "Parti démocrate populaire",
    acronym: "PDP",
    variants: [],
    foundedAt: "1924",
    dissolvedAt: "1944",
    status: "historical",
    description: "Parti démocrate-chrétien fondé dans l'entre-deux-guerres, situé dans les recompositions du centre et du catholicisme social.",
    historicalNote: "Marc Sangnier relève davantage d'une impulsion intellectuelle et militante issue du Sillon que d'une fondation personnelle unique du parti.",
    families: ["family-democratie-chretienne", "family-republicanisme"],
    founders: [],
    leaders: ["person-marc-sangnier"],
    sources: ["source-persee-pdp", "source-maitron-sangnier"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-ligue-droits-homme",
    name: "Ligue des droits de l'Homme",
    acronym: "LDH",
    variants: ["Ligue française pour la défense des droits de l'homme et du citoyen"],
    foundedAt: "1898",
    dissolvedAt: null,
    status: "association",
    description: "Association républicaine de défense des droits civiques et politiques, influente dans les débats de la IIIe République.",
    historicalNote: "La LDH n'est pas un parti politique ; elle est intégrée comme association influente afin de ne pas confondre mobilisation civique et organisation partisane.",
    families: ["family-republicanisme"],
    founders: [],
    leaders: [],
    sources: ["source-ldh-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-faisceau",
    name: "Le Faisceau",
    acronym: "Faisceau",
    variants: [],
    foundedAt: "1925-11-11",
    dissolvedAt: "1928",
    status: "movement",
    description: "Mouvement fasciste français fondé par Georges Valois dans les années 1920.",
    historicalNote: "La qualification de fasciste est ici historiographique et sourcée ; elle ne doit pas être généralisée sans examen aux autres ligues ou droites de la période.",
    families: ["family-fascisme", "family-nationalisme"],
    founders: ["person-georges-valois"],
    leaders: ["person-georges-valois"],
    sources: ["source-persee-faisceau"],
    dataStatus: "verified"
  },
  {
    id: "party-cagoule",
    name: "Comité secret d'action révolutionnaire",
    acronym: "CSAR",
    variants: ["La Cagoule", "OSARN", "OSAR"],
    foundedAt: "1935",
    dissolvedAt: "1939",
    status: "organization",
    description: "Organisation clandestine d'extrême droite active dans les années 1930.",
    historicalNote: "La Cagoule n'est pas un parti électoral mais une organisation clandestine ; elle est liée à la crise des ligues et à l'après-Front populaire.",
    families: ["family-nationalisme", "family-fascisme"],
    founders: ["person-eugene-deloncle", "person-jean-filliol"],
    leaders: ["person-eugene-deloncle", "person-jean-filliol"],
    sources: ["source-cagoule-historytoday"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-pco",
    name: "Parti communiste oppositionnel",
    acronym: "PCO",
    variants: [],
    foundedAt: "1929",
    dissolvedAt: "1935",
    status: "historical",
    description: "Petite organisation oppositionnelle issue de dissidences communistes critiques de la ligne du PCF et de la stalinisation.",
    historicalNote: "La filiation trotskiste doit être formulée prudemment : les courants oppositionnels communistes des années 1930 sont fragmentés et ne se réduisent pas tous à un lien organisationnel direct avec Trotski.",
    families: ["family-communisme", "family-socialisme"],
    founders: ["person-pierre-celor"],
    leaders: ["person-pierre-celor"],
    sources: ["source-maitron-tours"],
    dataStatus: "unverified"
  },
  {
    id: "party-psdf-ujj",
    name: "Parti socialiste de France - Union Jean Jaurès",
    acronym: "PSdF-UJJ",
    variants: ["Union Jean Jaurès", "Parti néo-socialiste"],
    foundedAt: "1933",
    dissolvedAt: "1935",
    status: "historical",
    description: "Formation néo-socialiste créée par des exclus ou dissidents de la SFIO autour de Marcel Déat, Adrien Marquet et Paul Ramadier.",
    historicalNote: "Son évolution ultérieure vers des trajectoires autoritaires ou collaborationnistes concerne certains dirigeants ; elle ne doit pas être projetée mécaniquement sur tous les militants.",
    families: ["family-socialisme"],
    founders: ["person-marcel-deat", "person-adrien-marquet", "person-paul-ramadier"],
    leaders: ["person-marcel-deat", "person-adrien-marquet"],
    sources: ["source-cambridge-neosocialisme"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-psop",
    name: "Parti socialiste ouvrier et paysan",
    acronym: "PSOP",
    variants: [],
    foundedAt: "1938-06-08",
    dissolvedAt: "1940",
    status: "historical",
    description: "Parti socialiste révolutionnaire issu d'une scission de la SFIO autour de Marceau Pivert.",
    historicalNote: "Le PSOP prolonge la tendance Gauche révolutionnaire et s'oppose à la modération gouvernementale du Front populaire finissant.",
    families: ["family-socialisme"],
    founders: ["person-marceau-pivert"],
    leaders: ["person-marceau-pivert"],
    sources: ["source-francearchives-psop", "source-bataille-socialiste-psop"],
    dataStatus: "verified"
  },
  {
    id: "party-croix-de-feu",
    name: "Croix-de-Feu",
    acronym: "CDF",
    variants: ["Association des Croix-de-Feu"],
    foundedAt: "1927",
    dissolvedAt: "1936",
    status: "movement",
    description: "Ligue d'anciens combattants transformée dans les années 1930 en mouvement politique de masse sous la direction de François de La Rocque.",
    historicalNote: "La qualification de fasciste fait débat dans l'historiographie ; la fiche privilégie une description contextualisée : nationalisme, anticommunisme, antiparlementarisme et rejet revendiqué de l'étiquette fasciste par La Rocque.",
    families: ["family-nationalisme", "family-conservatisme"],
    founders: [],
    leaders: ["person-francois-de-la-rocque"],
    sources: ["source-1914-1918-croix-de-feu", "source-erudit-croix-de-feu-psf"],
    dataStatus: "verified"
  },
  {
    id: "party-social-francais",
    name: "Parti social français",
    acronym: "PSF",
    variants: ["Parti social français de La Rocque"],
    foundedAt: "1936",
    dissolvedAt: "1940",
    status: "historical",
    description: "Parti créé par François de La Rocque après la dissolution des Croix-de-Feu.",
    historicalNote: "Cette fiche est distincte du Parti socialiste français : malgré le même sigle PSF, il s'agit d'une organisation de droite nationaliste et sociale issue des Croix-de-Feu.",
    families: ["family-nationalisme", "family-conservatisme", "family-democratie-chretienne"],
    founders: ["person-francois-de-la-rocque"],
    leaders: ["person-francois-de-la-rocque"],
    sources: ["source-erudit-croix-de-feu-psf", "source-1914-1918-croix-de-feu"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-ppf",
    name: "Parti populaire français",
    acronym: "PPF",
    variants: [],
    foundedAt: "1936-06-28",
    dissolvedAt: "1945",
    status: "historical",
    description: "Parti fondé par Jacques Doriot après son exclusion du PCF, devenu l'une des principales organisations fascisantes puis collaborationnistes.",
    historicalNote: "Les positions de 1936-1939 et l'engagement collaborationniste sous l'Occupation doivent être distingués chronologiquement.",
    families: ["family-fascisme", "family-nationalisme"],
    founders: ["person-jacques-doriot"],
    leaders: ["person-jacques-doriot"],
    sources: ["source-francearchives-ppf", "source-larousse-ppf"],
    dataStatus: "verified"
  },
  {
    id: "party-ligue-francaise",
    name: "Ligue française",
    acronym: "LF",
    variants: ["Ligue française d'épuration, d'entraide sociale et de collaboration européenne"],
    foundedAt: "1940",
    dissolvedAt: "1944",
    status: "movement",
    description: "Mouvement collaborationniste fondé par Pierre Costantini sous l'Occupation.",
    historicalNote: "La liste fournie la datait de 1933, mais les sources consultées situent cette Ligue française collaborationniste en 1940 ; elle est donc hors période 1930-1940 sauf comme débouché de guerre.",
    families: ["family-fascisme", "family-nationalisme"],
    founders: ["person-pierre-costantini"],
    leaders: ["person-pierre-costantini"],
    sources: ["source-cagoule-historytoday"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-francisme",
    name: "Mouvement franciste",
    acronym: "Francisme",
    variants: ["Parti franciste"],
    foundedAt: "1933-09-29",
    dissolvedAt: "1936",
    status: "movement",
    description: "Mouvement fasciste fondé par Marcel Bucard dans les années 1930.",
    historicalNote: "Le mouvement est dissous avec les ligues en 1936 puis connaît des tentatives de reconstitution et une relance collaborationniste sous l'Occupation.",
    families: ["family-fascisme", "family-nationalisme"],
    founders: ["person-marcel-bucard"],
    leaders: ["person-marcel-bucard"],
    sources: ["source-francearchives-bucard"],
    dataStatus: "partially_verified"
  },
  {
    id: "party-droite-anti-front-populaire",
    name: "Droite anti-Front populaire",
    acronym: "DAFP",
    variants: ["Oppositions de droite au Front populaire"],
    foundedAt: "1936",
    dissolvedAt: "1938",
    status: "coalition",
    description: "Désignation analytique de forces de droite et de centre droit opposées au Front populaire.",
    historicalNote: "Cette entrée n'est pas une organisation formelle unique. Elle sert uniquement à regrouper des relations d'opposition politique ; elle ne doit pas être confondue avec un parti ou avec le Bloc national de 1919.",
    families: ["family-conservatisme", "family-liberalisme", "family-nationalisme"],
    founders: [],
    leaders: [],
    sources: ["source-vie-publique-front-populaire"],
    dataStatus: "partially_verified"
  }
];

export const persons: Person[] = [
  {
    id: "person-jean-jaures",
    firstName: "Jean",
    lastName: "Jaurès",
    bornAt: "1859-09-03",
    diedAt: "1914-07-31",
    professions: ["homme politique", "journaliste"],
    summary: "Figure socialiste majeure de la IIIe République et acteur de l'unification socialiste.",
    parties: ["party-psf-1902", "party-sfio"],
    functions: ["député"],
    sources: ["source-vie-publique-front-populaire", "source-francearchives-jaures", "source-wikisource-jaures-1903"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-leon-blum",
    firstName: "Léon",
    lastName: "Blum",
    bornAt: "1872-04-09",
    diedAt: "1950-03-30",
    professions: ["homme politique", "écrivain"],
    summary: "Dirigeant socialiste, président du Conseil lors du Front populaire.",
    parties: ["party-sfio", "party-front-populaire"],
    functions: ["président du Conseil"],
    sources: ["source-bnf-blum", "source-vie-publique-front-populaire"],
    dataStatus: "verified"
  },
  {
    id: "person-maurice-thorez",
    firstName: "Maurice",
    lastName: "Thorez",
    bornAt: "1900-04-28",
    diedAt: "1964-07-11",
    professions: ["homme politique"],
    summary: "Dirigeant communiste français du XXe siècle.",
    parties: ["party-pcf"],
    functions: ["secrétaire général du PCF"],
    sources: ["source-pcf-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-charles-de-gaulle",
    firstName: "Charles",
    lastName: "de Gaulle",
    bornAt: "1890-11-22",
    diedAt: "1970-11-09",
    professions: ["militaire", "homme d'Etat"],
    summary: "Chef de la France libre puis président de la République, figure centrale de la naissance de la Ve République.",
    parties: ["party-unr"],
    functions: ["président de la République"],
    sources: ["source-vie-publique-1958"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-jules-guesde",
    firstName: "Jules",
    lastName: "Guesde",
    bornAt: "1845-11-11",
    diedAt: "1922-07-28",
    professions: ["homme politique", "journaliste"],
    summary: "Figure du socialisme marxiste français et dirigeant guesdiste avant l'unification socialiste.",
    parties: ["party-pof", "party-psdf", "party-sfio"],
    functions: ["dirigeant socialiste"],
    sources: ["source-gallica-psdf-commentry", "source-wikisource-guesde-deux-methodes"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-paul-lafargue",
    firstName: "Paul",
    lastName: "Lafargue",
    bornAt: "1842-01-15",
    diedAt: "1911-11-25",
    professions: ["homme politique", "journaliste"],
    summary: "Militant socialiste associé au Parti ouvrier français et au courant guesdiste.",
    parties: ["party-pof"],
    functions: ["dirigeant socialiste"],
    sources: ["source-persee-mouvement-socialiste", "source-marxists-pof-programme", "source-wikisource-lafargue-paresse"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-aristide-briand",
    firstName: "Aristide",
    lastName: "Briand",
    bornAt: "1862-03-28",
    diedAt: "1932-03-07",
    professions: ["homme politique"],
    summary: "Socialiste indépendant puis républicain-socialiste, plusieurs fois président du Conseil.",
    parties: ["party-prs"],
    functions: ["président du Conseil"],
    sources: ["source-theses-prs", "source-wikisource-briand-1927"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-joseph-paul-boncour",
    firstName: "Joseph",
    lastName: "Paul-Boncour",
    bornAt: "1873-08-04",
    diedAt: "1972-03-28",
    professions: ["homme politique"],
    summary: "Personnalité républicaine-socialiste associée au Parti républicain-socialiste.",
    parties: ["party-prs"],
    functions: ["président du Conseil"],
    sources: ["source-theses-prs"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-rene-viviani",
    firstName: "René",
    lastName: "Viviani",
    bornAt: "1863-11-08",
    diedAt: "1925-09-07",
    professions: ["homme politique", "avocat"],
    summary: "Socialiste indépendant puis républicain-socialiste, président du Conseil au début de la Première Guerre mondiale.",
    parties: ["party-prs"],
    functions: ["président du Conseil"],
    sources: ["source-theses-prs"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-jacques-piou",
    firstName: "Jacques",
    lastName: "Piou",
    bornAt: "1838-08-06",
    diedAt: "1932-05-12",
    professions: ["homme politique"],
    summary: "Dirigeant catholique rallié à la République et fondateur de l'Action libérale populaire.",
    parties: ["party-alp"],
    functions: ["dirigeant politique"],
    sources: ["source-openedition-alp"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-albert-de-mun",
    firstName: "Albert",
    lastName: "de Mun",
    bornAt: "1841-02-28",
    diedAt: "1914-10-06",
    professions: ["homme politique"],
    summary: "Figure catholique sociale associée à l'Action libérale populaire.",
    parties: ["party-alp"],
    functions: ["député"],
    sources: ["source-openedition-alp"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-eugene-motte",
    firstName: "Eugène",
    lastName: "Motte",
    bornAt: "1860-12-15",
    diedAt: "1932-10-23",
    professions: ["industriel", "homme politique"],
    summary: "Fondateur associé à la Fédération républicaine.",
    parties: ["party-federation-republicaine"],
    functions: ["dirigeant politique"],
    sources: ["source-francearchives-federation-republicaine"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-henri-vaugeois",
    firstName: "Henri",
    lastName: "Vaugeois",
    bornAt: "1864-04-25",
    diedAt: "1916-04-11",
    professions: ["enseignant", "journaliste"],
    summary: "Fondateur associé à l'Action française.",
    parties: ["party-action-francaise"],
    functions: ["fondateur"],
    sources: ["source-hal-action-francaise", "source-larousse-action-francaise"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-maurice-pujo",
    firstName: "Maurice",
    lastName: "Pujo",
    bornAt: "1872-01-26",
    diedAt: "1955-09-06",
    professions: ["journaliste", "homme politique"],
    summary: "Fondateur associé à l'Action française.",
    parties: ["party-action-francaise"],
    functions: ["fondateur"],
    sources: ["source-hal-action-francaise", "source-larousse-action-francaise"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-charles-maurras",
    firstName: "Charles",
    lastName: "Maurras",
    bornAt: "1868-04-20",
    diedAt: "1952-11-16",
    professions: ["écrivain", "journaliste", "homme politique"],
    summary: "Théoricien monarchiste et nationaliste associé à l'Action française.",
    parties: ["party-action-francaise"],
    functions: ["idéologue", "dirigeant"],
    sources: ["source-larousse-action-francaise", "source-hal-action-francaise"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-paul-deroulede",
    firstName: "Paul",
    lastName: "Déroulède",
    bornAt: "1846-09-02",
    diedAt: "1914-01-30",
    professions: ["écrivain", "homme politique"],
    summary: "Figure nationaliste française associée à la fondation de la Ligue des patriotes.",
    parties: ["party-ligue-des-patriotes"],
    functions: ["fondateur"],
    sources: ["source-theses-deroulede"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-pierre-taittinger",
    firstName: "Pierre",
    lastName: "Taittinger",
    bornAt: "1887-10-04",
    diedAt: "1965-01-22",
    professions: ["homme politique", "entrepreneur"],
    summary: "Fondateur des Jeunesses patriotes en 1924.",
    parties: ["party-jeunesses-patriotes"],
    functions: ["fondateur"],
    sources: ["source-theses-jeunesses-patriotes"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-ludovic-oscar-frossard",
    firstName: "Ludovic-Oscar",
    lastName: "Frossard",
    bornAt: "1889-03-05",
    diedAt: "1946-02-11",
    professions: ["homme politique", "journaliste"],
    summary: "Dirigeant socialiste puis premier secrétaire général de la SFIC issue du Congrès de Tours.",
    parties: ["party-sfio", "party-pcf"],
    functions: ["secrétaire général"],
    sources: ["source-maitron-tours"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-marcel-cachin",
    firstName: "Marcel",
    lastName: "Cachin",
    bornAt: "1869-09-20",
    diedAt: "1958-02-12",
    professions: ["homme politique", "journaliste"],
    summary: "Figure socialiste passée au communisme lors de la scission de Tours.",
    parties: ["party-sfio", "party-pcf"],
    functions: ["dirigeant communiste"],
    sources: ["source-maitron-tours"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-edouard-herriot",
    firstName: "Édouard",
    lastName: "Herriot",
    bornAt: "1872-07-05",
    diedAt: "1957-03-26",
    professions: ["homme politique"],
    summary: "Dirigeant radical, maire de Lyon et président du Conseil lors du Cartel des gauches.",
    parties: ["party-prrrs", "party-cartel-des-gauches"],
    functions: ["président du Conseil", "maire de Lyon"],
    sources: ["source-france-memoire-herriot-cartel"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-raymond-poincare",
    firstName: "Raymond",
    lastName: "Poincaré",
    bornAt: "1860-08-20",
    diedAt: "1934-10-15",
    professions: ["homme politique"],
    summary: "Président de la République puis président du Conseil, figure républicaine modérée de la période.",
    parties: ["party-ard", "party-bloc-national"],
    functions: ["président de la République", "président du Conseil"],
    sources: ["source-assemblee-bloc-national-1919"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-alexandre-millerand",
    firstName: "Alexandre",
    lastName: "Millerand",
    bornAt: "1859-02-10",
    diedAt: "1943-04-06",
    professions: ["homme politique"],
    summary: "Ancien socialiste indépendant devenu figure du centre républicain et président de la République après la Grande Guerre.",
    parties: ["party-socialistes-independants", "party-bloc-national"],
    functions: ["président de la République", "président du Conseil"],
    sources: ["source-assemblee-bloc-national-1919"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-louis-marin",
    firstName: "Louis",
    lastName: "Marin",
    bornAt: "1871-02-07",
    diedAt: "1960-05-23",
    professions: ["homme politique"],
    summary: "Dirigeant de la Fédération républicaine dans l'entre-deux-guerres.",
    parties: ["party-federation-republicaine"],
    functions: ["dirigeant politique"],
    sources: ["source-francearchives-federation-republicaine"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-georges-mandel",
    firstName: "Georges",
    lastName: "Mandel",
    bornAt: "1885-06-05",
    diedAt: "1944-07-07",
    professions: ["homme politique"],
    summary: "Personnalité de droite républicaine associée aux majorités modérées de l'entre-deux-guerres.",
    parties: ["party-federation-republicaine"],
    functions: ["ministre"],
    sources: ["source-francearchives-federation-republicaine"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-marc-sangnier",
    firstName: "Marc",
    lastName: "Sangnier",
    bornAt: "1873-04-03",
    diedAt: "1950-05-28",
    professions: ["homme politique", "journaliste"],
    summary: "Fondateur du Sillon et figure du catholicisme social démocratique.",
    parties: ["party-pdp"],
    functions: ["dirigeant démocrate-chrétien"],
    sources: ["source-maitron-sangnier", "source-persee-pdp"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-georges-valois",
    firstName: "Georges",
    lastName: "Valois",
    bornAt: "1878-10-07",
    diedAt: "1945-02",
    professions: ["homme politique", "éditeur"],
    summary: "Fondateur du Faisceau après un parcours lié à l'Action française.",
    parties: ["party-action-francaise", "party-faisceau"],
    functions: ["fondateur"],
    sources: ["source-persee-faisceau"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-eugene-deloncle",
    firstName: "Eugène",
    lastName: "Deloncle",
    bornAt: "1890-06-20",
    diedAt: "1944-01-07",
    professions: ["ingénieur", "militant politique"],
    summary: "Fondateur associé à l'organisation clandestine connue sous le nom de Cagoule.",
    parties: ["party-cagoule"],
    functions: ["fondateur"],
    sources: ["source-cagoule-historytoday"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-jean-filliol",
    firstName: "Jean",
    lastName: "Filiol",
    bornAt: "1909-05-12",
    diedAt: "1975",
    professions: ["militant politique"],
    summary: "Militant d'extrême droite associé à l'organisation clandestine de la Cagoule.",
    parties: ["party-cagoule"],
    functions: ["dirigeant clandestin"],
    sources: ["source-cagoule-historytoday"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-pierre-celor",
    firstName: "Pierre",
    lastName: "Celor",
    bornAt: "1902-04-19",
    diedAt: "1957-04-06",
    professions: ["homme politique"],
    summary: "Militant communiste dissident associé aux oppositions internes au PCF.",
    parties: ["party-pco", "party-pcf"],
    functions: ["dirigeant oppositionnel"],
    sources: ["source-maitron-tours"],
    dataStatus: "unverified"
  },
  {
    id: "person-marcel-deat",
    firstName: "Marcel",
    lastName: "Déat",
    bornAt: "1894-03-07",
    diedAt: "1955-01-05",
    professions: ["homme politique"],
    summary: "Néo-socialiste issu de la SFIO, fondateur du PSdF-UJJ et futur dirigeant collaborationniste.",
    parties: ["party-sfio", "party-psdf-ujj"],
    functions: ["dirigeant néo-socialiste"],
    sources: ["source-cambridge-neosocialisme"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-adrien-marquet",
    firstName: "Adrien",
    lastName: "Marquet",
    bornAt: "1884-10-06",
    diedAt: "1955-02-03",
    professions: ["homme politique"],
    summary: "Néo-socialiste, maire de Bordeaux, associé à la scission de 1933 hors de la SFIO.",
    parties: ["party-sfio", "party-psdf-ujj"],
    functions: ["maire", "dirigeant néo-socialiste"],
    sources: ["source-cambridge-neosocialisme"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-paul-ramadier",
    firstName: "Paul",
    lastName: "Ramadier",
    bornAt: "1888-03-17",
    diedAt: "1961-10-14",
    professions: ["homme politique"],
    summary: "Socialiste puis néo-socialiste, associé à la scission de 1933.",
    parties: ["party-sfio", "party-psdf-ujj"],
    functions: ["homme politique"],
    sources: ["source-cambridge-neosocialisme"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-marceau-pivert",
    firstName: "Marceau",
    lastName: "Pivert",
    bornAt: "1895-10-02",
    diedAt: "1958-06-03",
    professions: ["homme politique", "enseignant"],
    summary: "Animateur de la Gauche révolutionnaire puis fondateur du PSOP.",
    parties: ["party-sfio", "party-psop"],
    functions: ["fondateur"],
    sources: ["source-francearchives-psop", "source-bataille-socialiste-psop"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-vincent-auriol",
    firstName: "Vincent",
    lastName: "Auriol",
    bornAt: "1884-08-27",
    diedAt: "1966-01-01",
    professions: ["homme politique"],
    summary: "Responsable socialiste, ministre du Front populaire puis président de la IVe République.",
    parties: ["party-sfio", "party-front-populaire"],
    functions: ["ministre", "président de la République"],
    sources: ["source-vie-publique-front-populaire"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-jean-zyromski",
    firstName: "Jean",
    lastName: "Zyromski",
    bornAt: "1890-04-20",
    diedAt: "1975-10-20",
    professions: ["homme politique"],
    summary: "Responsable de l'aile gauche de la SFIO dans l'entre-deux-guerres.",
    parties: ["party-sfio"],
    functions: ["dirigeant socialiste"],
    sources: ["source-vie-publique-front-populaire"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-edouard-daladier",
    firstName: "Édouard",
    lastName: "Daladier",
    bornAt: "1884-06-18",
    diedAt: "1970-10-10",
    professions: ["homme politique"],
    summary: "Dirigeant radical, président du Conseil lors de crises majeures des années 1930.",
    parties: ["party-prrrs", "party-front-populaire"],
    functions: ["président du Conseil"],
    sources: ["source-vie-publique-front-populaire", "source-larousse-6-fevrier-1934"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-pierre-etienne-flandin",
    firstName: "Pierre-Étienne",
    lastName: "Flandin",
    bornAt: "1889-04-12",
    diedAt: "1958-06-13",
    professions: ["homme politique"],
    summary: "Dirigeant de l'Alliance démocratique et président du Conseil en 1934-1935.",
    parties: ["party-ard"],
    functions: ["président du Conseil"],
    sources: ["source-sanson-alliance-democratique"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-francois-de-la-rocque",
    firstName: "François",
    lastName: "de La Rocque",
    bornAt: "1885-10-06",
    diedAt: "1946-04-28",
    professions: ["militaire", "homme politique"],
    summary: "Dirigeant des Croix-de-Feu puis fondateur du Parti social français.",
    parties: ["party-croix-de-feu", "party-social-francais"],
    functions: ["dirigeant politique"],
    sources: ["source-1914-1918-croix-de-feu", "source-erudit-croix-de-feu-psf"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-jacques-doriot",
    firstName: "Jacques",
    lastName: "Doriot",
    bornAt: "1898-09-26",
    diedAt: "1945-02-22",
    professions: ["homme politique"],
    summary: "Ancien dirigeant communiste exclu du PCF en 1934, fondateur du Parti populaire français en 1936.",
    parties: ["party-pcf", "party-ppf"],
    functions: ["fondateur"],
    sources: ["source-francearchives-ppf", "source-larousse-ppf"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-leon-daudet",
    firstName: "Léon",
    lastName: "Daudet",
    bornAt: "1867-11-16",
    diedAt: "1942-06-30",
    professions: ["écrivain", "journaliste", "homme politique"],
    summary: "Figure de l'Action française et polémiste royaliste.",
    parties: ["party-action-francaise"],
    functions: ["dirigeant politique"],
    sources: ["source-larousse-action-francaise"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-marcel-bucard",
    firstName: "Marcel",
    lastName: "Bucard",
    bornAt: "1895-12-07",
    diedAt: "1946-03-19",
    professions: ["homme politique"],
    summary: "Fondateur du Mouvement franciste.",
    parties: ["party-francisme"],
    functions: ["fondateur"],
    sources: ["source-francearchives-bucard"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-pierre-costantini",
    firstName: "Pierre",
    lastName: "Costantini",
    bornAt: "1889-02-16",
    diedAt: "1986-06-30",
    professions: ["militaire", "journaliste"],
    summary: "Fondateur de la Ligue française collaborationniste sous l'Occupation.",
    parties: ["party-ligue-francaise"],
    functions: ["fondateur"],
    sources: ["source-cagoule-historytoday"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-philippe-petain",
    firstName: "Philippe",
    lastName: "Pétain",
    bornAt: "1856-04-24",
    diedAt: "1951-07-23",
    professions: ["militaire", "chef de gouvernement"],
    summary: "Président du Conseil en 1940 puis chef de l'État français sous Vichy.",
    parties: [],
    functions: ["président du Conseil", "chef de l'État français"],
    sources: ["source-assemblee-pleins-pouvoirs-1940"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-paul-reynaud",
    firstName: "Paul",
    lastName: "Reynaud",
    bornAt: "1878-10-15",
    diedAt: "1966-09-21",
    professions: ["homme politique"],
    summary: "Président du Conseil au début de la défaite militaire de 1940.",
    parties: ["party-ard"],
    functions: ["président du Conseil"],
    sources: ["source-assemblee-pleins-pouvoirs-1940"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-guy-mollet",
    firstName: "Guy",
    lastName: "Mollet",
    bornAt: "1905-12-31",
    diedAt: "1975-10-03",
    professions: ["homme politique"],
    summary: "Secrétaire général de la SFIO et président du Conseil en 1956-1957.",
    parties: ["party-sfio", "party-front-republicain-1956"],
    functions: ["secrétaire général", "président du Conseil"],
    sources: ["source-francearchives-psa"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-pierre-mendes-france",
    firstName: "Pierre",
    lastName: "Mendès France",
    bornAt: "1907-01-11",
    diedAt: "1982-10-18",
    professions: ["homme politique"],
    summary: "Dirigeant radical, président du Conseil en 1954-1955 et figure de l'opposition à la guerre d'Algérie.",
    parties: ["party-prrrs", "party-front-republicain-1956", "party-psu"],
    functions: ["président du Conseil"],
    sources: ["source-francearchives-psu", "source-archives-nationales-1958"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-felix-gaillard",
    firstName: "Félix",
    lastName: "Gaillard",
    bornAt: "1919-11-05",
    diedAt: "1970-07-09",
    professions: ["homme politique"],
    summary: "Dirigeant radical et président du Conseil en 1957-1958.",
    parties: ["party-prrrs"],
    functions: ["président du Conseil"],
    sources: ["source-archives-nationales-1958"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-georges-bidault",
    firstName: "Georges",
    lastName: "Bidault",
    bornAt: "1899-10-05",
    diedAt: "1983-01-27",
    professions: ["homme politique", "résistant"],
    summary: "Dirigeant du MRP, ancien président du Conseil et ministre des Affaires étrangères.",
    parties: ["party-mrp"],
    functions: ["président du Conseil", "ministre"],
    sources: ["source-francearchives-mrp"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-robert-schuman",
    firstName: "Robert",
    lastName: "Schuman",
    bornAt: "1886-06-29",
    diedAt: "1963-09-04",
    professions: ["homme politique"],
    summary: "Dirigeant du MRP et figure majeure de la construction européenne.",
    parties: ["party-mrp"],
    functions: ["président du Conseil", "ministre des Affaires étrangères"],
    sources: ["source-francearchives-mrp"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-pierre-pflimlin",
    firstName: "Pierre",
    lastName: "Pflimlin",
    bornAt: "1907-02-05",
    diedAt: "2000-06-27",
    professions: ["homme politique"],
    summary: "Dirigeant du MRP et dernier président du Conseil avant le retour de De Gaulle en 1958.",
    parties: ["party-mrp"],
    functions: ["président du Conseil"],
    sources: ["source-francearchives-mrp", "source-archives-nationales-1958"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-rene-pleven",
    firstName: "René",
    lastName: "Pleven",
    bornAt: "1901-04-15",
    diedAt: "1993-01-13",
    professions: ["homme politique"],
    summary: "Dirigeant de l'UDSR et président du Conseil sous la IVe République.",
    parties: ["party-udsr"],
    functions: ["président du Conseil"],
    sources: ["source-francearchives-udsr", "source-sciencespo-udsr"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-francois-mitterrand",
    firstName: "François",
    lastName: "Mitterrand",
    bornAt: "1916-10-26",
    diedAt: "1996-01-08",
    professions: ["homme politique"],
    summary: "Dirigeant de l'UDSR dans les années 1950, fondateur de la CIR puis premier secrétaire du Parti socialiste à partir de 1971.",
    parties: ["party-udsr", "party-front-republicain-1956", "party-cir", "party-ps-1969", "party-programme-commun-gauche"],
    functions: ["ministre", "dirigeant politique"],
    sources: ["source-francearchives-udsr", "source-sciencespo-udsr"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-jacques-soustelle",
    firstName: "Jacques",
    lastName: "Soustelle",
    bornAt: "1912-02-03",
    diedAt: "1990-08-06",
    professions: ["homme politique", "ethnologue"],
    summary: "Gaulliste, secrétaire général du RPF et acteur de la crise algérienne.",
    parties: ["party-rpf", "party-unr"],
    functions: ["secrétaire général", "ministre"],
    sources: ["source-fondation-de-gaulle-rpf", "source-archives-nationales-1958"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-andre-malraux",
    firstName: "André",
    lastName: "Malraux",
    bornAt: "1901-11-03",
    diedAt: "1976-11-23",
    professions: ["écrivain", "homme politique"],
    summary: "Écrivain et compagnon politique du gaullisme, associé au RPF puis aux débuts de la Ve République.",
    parties: ["party-rpf", "party-unr"],
    functions: ["ministre"],
    sources: ["source-fondation-de-gaulle-rpf"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-jacques-chaban-delmas",
    firstName: "Jacques",
    lastName: "Chaban-Delmas",
    bornAt: "1915-03-07",
    diedAt: "2000-11-10",
    professions: ["homme politique", "résistant"],
    summary: "Gaulliste, dirigeant des Républicains sociaux puis de l'UNR, Premier ministre de 1969 à 1972.",
    parties: ["party-republicains-sociaux", "party-unr", "party-udr"],
    functions: ["dirigeant gaulliste", "Premier ministre"],
    sources: ["source-francearchives-unr"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-georges-pompidou",
    firstName: "Georges",
    lastName: "Pompidou",
    bornAt: "1911-07-05",
    diedAt: "1974-04-02",
    professions: ["haut fonctionnaire", "homme politique"],
    summary: "Collaborateur de Charles de Gaulle, Premier ministre puis président de la République de 1969 à 1974.",
    parties: ["party-unr", "party-udr"],
    functions: ["président de la République", "Premier ministre"],
    sources: ["source-francearchives-unr"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-roger-duchet",
    firstName: "Roger",
    lastName: "Duchet",
    bornAt: "1904-07-04",
    diedAt: "1981-09-06",
    professions: ["homme politique"],
    summary: "Fondateur du Centre national des indépendants.",
    parties: ["party-cnip"],
    functions: ["fondateur", "dirigeant politique"],
    sources: ["source-francearchives-cnip", "source-theses-cnip"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-antoine-pinay",
    firstName: "Antoine",
    lastName: "Pinay",
    bornAt: "1891-12-30",
    diedAt: "1994-12-13",
    professions: ["homme politique"],
    summary: "Figure du CNIP et président du Conseil en 1952.",
    parties: ["party-cnip"],
    functions: ["président du Conseil"],
    sources: ["source-francearchives-cnip", "source-theses-cnip"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-valery-giscard-destaing",
    firstName: "Valéry",
    lastName: "Giscard d'Estaing",
    bornAt: "1926-02-02",
    diedAt: "2020-12-02",
    professions: ["homme politique"],
    summary: "Dirigeant des Républicains indépendants, fondateur de l'UDF et président de la République de 1974 à 1981.",
    parties: ["party-cnip", "party-fnri", "party-parti-republicain-1977", "party-udf"],
    functions: ["président de la République", "dirigeant libéral"],
    sources: ["source-francearchives-cnip", "source-persee-republicains-independants", "source-francearchives-udf"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-michel-clemenceau",
    firstName: "Michel",
    lastName: "Clemenceau",
    bornAt: "1873-11-24",
    diedAt: "1964-03-04",
    professions: ["homme politique"],
    summary: "Dirigeant associé au Parti républicain de la liberté.",
    parties: ["party-prl"],
    functions: ["dirigeant politique"],
    sources: ["source-openedition-prl"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-pierre-de-gaulle",
    firstName: "Pierre",
    lastName: "de Gaulle",
    bornAt: "1897-03-22",
    diedAt: "1959-12-26",
    professions: ["homme politique"],
    summary: "Homme politique de droite libérale, frère de Charles de Gaulle, associé au PRL.",
    parties: ["party-prl"],
    functions: ["dirigeant politique"],
    sources: ["source-openedition-prl"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-edouard-depreux",
    firstName: "Édouard",
    lastName: "Depreux",
    bornAt: "1898-10-31",
    diedAt: "1981-10-16",
    professions: ["homme politique"],
    summary: "Socialiste, fondateur du PSA puis dirigeant du PSU.",
    parties: ["party-sfio", "party-psa", "party-psu"],
    functions: ["fondateur", "dirigeant socialiste"],
    sources: ["source-maitron-depreux", "source-francearchives-psa", "source-francearchives-psu"],
    dataStatus: "verified"
  },
  {
    id: "person-alain-savary",
    firstName: "Alain",
    lastName: "Savary",
    bornAt: "1918-04-25",
    diedAt: "1988-02-17",
    professions: ["homme politique"],
    summary: "Socialiste opposé à la politique algérienne de la SFIO, associé au PSA puis au PSU.",
    parties: ["party-sfio", "party-psa", "party-psu"],
    functions: ["dirigeant socialiste"],
    sources: ["source-francearchives-psa", "source-francearchives-psu"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-pierre-frank",
    firstName: "Pierre",
    lastName: "Frank",
    bornAt: "1905-10-24",
    diedAt: "1984-04-18",
    professions: ["militant politique"],
    summary: "Militant trotskiste associé au Parti communiste internationaliste.",
    parties: ["party-pci-trotskiste"],
    functions: ["dirigeant trotskiste"],
    sources: ["source-maitron-bleibtreu"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-marcel-bleibtreu",
    firstName: "Marcel",
    lastName: "Bleibtreu",
    bornAt: "1918-08-26",
    diedAt: "2001-12-25",
    professions: ["médecin", "militant politique"],
    summary: "Militant trotskiste, dirigeant du PCI dans l'après-guerre.",
    parties: ["party-pci-trotskiste"],
    functions: ["dirigeant trotskiste"],
    sources: ["source-maitron-bleibtreu"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-pierre-sidos",
    firstName: "Pierre",
    lastName: "Sidos",
    bornAt: "1927-01-06",
    diedAt: "2020-09-04",
    professions: ["militant politique"],
    summary: "Militant nationaliste, animateur de Jeune Nation dans les années 1950.",
    parties: ["party-jeune-nation"],
    functions: ["dirigeant nationaliste"],
    sources: ["source-jeune-nation-sidos"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-jean-jacques-susini",
    firstName: "Jean-Jacques",
    lastName: "Susini",
    bornAt: "1933-07-30",
    diedAt: "2017-07-03",
    professions: ["militant politique"],
    summary: "Fondateur associé à l'OAS en 1961.",
    parties: ["party-oas"],
    functions: ["fondateur"],
    sources: ["source-oas-herodote"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-pierre-lagaillarde",
    firstName: "Pierre",
    lastName: "Lagaillarde",
    bornAt: "1931-05-15",
    diedAt: "2014-08-17",
    professions: ["avocat", "militant politique"],
    summary: "Fondateur associé à l'OAS en 1961.",
    parties: ["party-oas"],
    functions: ["fondateur"],
    sources: ["source-oas-herodote"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-raoul-salan",
    firstName: "Raoul",
    lastName: "Salan",
    bornAt: "1899-06-10",
    diedAt: "1984-07-03",
    professions: ["militaire"],
    summary: "Général français devenu dirigeant de l'OAS pendant la guerre d'Algérie.",
    parties: ["party-oas"],
    functions: ["chef militaire"],
    sources: ["source-oas-herodote"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-waldeck-rochet",
    firstName: "Waldeck",
    lastName: "Rochet",
    bornAt: "1905-04-05",
    diedAt: "1983-02-15",
    professions: ["homme politique"],
    summary: "Dirigeant communiste, secrétaire général du PCF à partir de 1964.",
    parties: ["party-pcf"],
    functions: ["secrétaire général du PCF"],
    sources: ["source-pcf-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-georges-marchais",
    firstName: "Georges",
    lastName: "Marchais",
    bornAt: "1920-06-07",
    diedAt: "1997-11-16",
    professions: ["homme politique"],
    summary: "Responsable communiste montant à la fin des années 1960, futur secrétaire général du PCF.",
    parties: ["party-pcf"],
    functions: ["dirigeant communiste"],
    sources: ["source-pcf-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-jacques-duclos",
    firstName: "Jacques",
    lastName: "Duclos",
    bornAt: "1896-10-02",
    diedAt: "1975-04-25",
    professions: ["homme politique"],
    summary: "Dirigeant historique du PCF et candidat à l'élection présidentielle de 1969.",
    parties: ["party-pcf"],
    functions: ["dirigeant communiste"],
    sources: ["source-pcf-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-michel-raptis",
    firstName: "Michel",
    lastName: "Raptis",
    bornAt: "1911-08-24",
    diedAt: "1996-02-17",
    professions: ["militant politique"],
    summary: "Militant trotskiste connu sous le pseudonyme Pablo.",
    parties: ["party-pci-trotskiste"],
    functions: ["dirigeant trotskiste"],
    sources: ["source-maitron-bleibtreu"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-alain-krivine",
    firstName: "Alain",
    lastName: "Krivine",
    bornAt: "1941-07-10",
    diedAt: "2022-03-12",
    professions: ["homme politique", "enseignant"],
    summary: "Figure trotskiste de Mai 68, fondateur de la Ligue communiste et candidat présidentiel en 1969.",
    parties: ["party-ligue-communiste"],
    functions: ["fondateur", "candidat présidentiel"],
    sources: ["source-maitron-krivine"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-daniel-cohn-bendit",
    firstName: "Daniel",
    lastName: "Cohn-Bendit",
    bornAt: "1945-04-04",
    professions: ["militant politique", "homme politique"],
    summary: "Figure du Mouvement du 22 mars et du mouvement étudiant de Mai 68.",
    parties: ["party-mouvement-22-mars"],
    functions: ["dirigeant étudiant"],
    sources: ["source-persee-mouvement-22-mars", "source-mahj-cohn-bendit"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-alain-geismar",
    firstName: "Alain",
    lastName: "Geismar",
    bornAt: "1939-07-17",
    professions: ["enseignant", "militant politique"],
    summary: "Militant de Mai 68 puis figure associée à la Gauche prolétarienne.",
    parties: ["party-gauche-proletarienne"],
    functions: ["dirigeant militant"],
    sources: ["source-paris8-gauche-proletarienne", "source-gis-gauche-proletarienne"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-benny-levy",
    firstName: "Benny",
    lastName: "Lévy",
    bornAt: "1945-08-28",
    diedAt: "2003-10-15",
    professions: ["militant politique", "philosophe"],
    summary: "Militant maoïste associé à la direction de la Gauche prolétarienne.",
    parties: ["party-gauche-proletarienne"],
    functions: ["dirigeant militant"],
    sources: ["source-paris8-gauche-proletarienne", "source-gis-gauche-proletarienne"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-michel-rocard",
    firstName: "Michel",
    lastName: "Rocard",
    bornAt: "1930-08-23",
    diedAt: "2016-07-02",
    professions: ["homme politique"],
    summary: "Dirigeant du PSU puis du Parti socialiste, Premier ministre de François Mitterrand de 1988 à 1991.",
    parties: ["party-psu", "party-ps-1969"],
    functions: ["dirigeant socialiste", "Premier ministre", "candidat présidentiel"],
    sources: ["source-francearchives-psu", "source-vie-publique-mitterrand-1981"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-gaston-defferre",
    firstName: "Gaston",
    lastName: "Defferre",
    bornAt: "1910-09-14",
    diedAt: "1986-05-07",
    professions: ["homme politique"],
    summary: "Dirigeant socialiste, maire de Marseille et candidat présidentiel en 1969.",
    parties: ["party-sfio", "party-fgds", "party-ps-1969"],
    functions: ["maire", "dirigeant socialiste", "candidat présidentiel"],
    sources: ["source-archives-socialistes-1969"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-jean-jacques-servan-schreiber",
    firstName: "Jean-Jacques",
    lastName: "Servan-Schreiber",
    bornAt: "1924-02-13",
    diedAt: "2006-11-07",
    professions: ["journaliste", "homme politique"],
    summary: "Figure radicale et centriste montante à la fin des années 1960.",
    parties: ["party-prrrs"],
    functions: ["dirigeant radical"],
    sources: ["source-larousse-parti-radical"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-jean-lecanuet",
    firstName: "Jean",
    lastName: "Lecanuet",
    bornAt: "1920-03-04",
    diedAt: "1993-02-22",
    professions: ["homme politique"],
    summary: "Candidat centriste à la présidentielle de 1965 et fondateur du Centre démocrate.",
    parties: ["party-mrp", "party-centre-democrate"],
    functions: ["fondateur", "candidat présidentiel"],
    sources: ["source-francearchives-centre-democrate", "source-persee-centre-democrate"],
    dataStatus: "verified"
  },
  {
    id: "person-alain-poher",
    firstName: "Alain",
    lastName: "Poher",
    bornAt: "1909-04-17",
    diedAt: "1996-12-09",
    professions: ["homme politique"],
    summary: "Centriste démocrate-chrétien, président du Sénat et candidat à la présidentielle de 1969.",
    parties: ["party-mrp", "party-centre-democrate"],
    functions: ["président du Sénat", "candidat présidentiel"],
    sources: ["source-francearchives-centre-democrate"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-roger-frey",
    firstName: "Roger",
    lastName: "Frey",
    bornAt: "1913-06-11",
    diedAt: "1997-09-13",
    professions: ["homme politique"],
    summary: "Dirigeant gaulliste associé à l'UNR puis à l'UDR.",
    parties: ["party-unr", "party-udr"],
    functions: ["dirigeant gaulliste", "ministre"],
    sources: ["source-francearchives-unr"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-michel-debre",
    firstName: "Michel",
    lastName: "Debré",
    bornAt: "1912-01-15",
    diedAt: "1996-08-02",
    professions: ["homme politique"],
    summary: "Premier Premier ministre de la Ve République et figure du gaullisme institutionnel.",
    parties: ["party-unr", "party-udr"],
    functions: ["Premier ministre"],
    sources: ["source-vie-publique-1958", "source-francearchives-unr"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-maurice-couve-de-murville",
    firstName: "Maurice",
    lastName: "Couve de Murville",
    bornAt: "1907-01-24",
    diedAt: "1999-12-24",
    professions: ["diplomate", "homme politique"],
    summary: "Ministre des Affaires étrangères puis Premier ministre gaulliste en 1968-1969.",
    parties: ["party-udr"],
    functions: ["Premier ministre", "ministre"],
    sources: ["source-francearchives-unr"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-francois-fontan",
    firstName: "François",
    lastName: "Fontan",
    bornAt: "1929-02-07",
    diedAt: "1979-12-19",
    professions: ["militant régionaliste"],
    summary: "Fondateur du Partit de la Nacion Occitana.",
    parties: ["party-pno"],
    functions: ["fondateur"],
    sources: ["source-pno"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-rene-dumont",
    firstName: "René",
    lastName: "Dumont",
    bornAt: "1904-03-13",
    diedAt: "2001-06-18",
    professions: ["agronome", "militant écologiste"],
    summary: "Agronome et future figure de l'écologie politique française.",
    parties: ["party-amis-terre"],
    functions: ["figure écologiste"],
    sources: ["source-parti-occitan"],
    dataStatus: "unverified"
  },
  {
    id: "person-jacques-chirac",
    firstName: "Jacques",
    lastName: "Chirac",
    bornAt: "1932-11-29",
    diedAt: "2019-09-26",
    professions: ["homme politique"],
    summary: "Premier ministre de Valéry Giscard d'Estaing de 1974 à 1976 puis fondateur du RPR.",
    parties: ["party-udr", "party-rpr"],
    functions: ["Premier ministre", "fondateur du RPR"],
    sources: ["source-francearchives-rpr", "source-francearchives-unr"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-pierre-messmer",
    firstName: "Pierre",
    lastName: "Messmer",
    bornAt: "1916-03-20",
    diedAt: "2007-08-29",
    professions: ["homme politique", "haut fonctionnaire"],
    summary: "Gaulliste, Premier ministre de 1972 à 1974 sous Georges Pompidou.",
    parties: ["party-udr"],
    functions: ["Premier ministre"],
    sources: ["source-francearchives-unr"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-raymond-barre",
    firstName: "Raymond",
    lastName: "Barre",
    bornAt: "1924-04-12",
    diedAt: "2007-08-25",
    professions: ["économiste", "homme politique"],
    summary: "Premier ministre de 1976 à 1981, associé à la majorité giscardienne et à la politique économique de rigueur.",
    parties: ["party-udf"],
    functions: ["Premier ministre"],
    sources: ["source-francearchives-udf"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-robert-fabre",
    firstName: "Robert",
    lastName: "Fabre",
    bornAt: "1915-12-21",
    diedAt: "2006-12-23",
    professions: ["homme politique"],
    summary: "Dirigeant radical de gauche, signataire du Programme commun en 1972.",
    parties: ["party-prg", "party-programme-commun-gauche"],
    functions: ["dirigeant radical"],
    sources: ["source-prg-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-michel-crepeau",
    firstName: "Michel",
    lastName: "Crépeau",
    bornAt: "1930-10-30",
    diedAt: "1999-03-30",
    professions: ["homme politique", "avocat"],
    summary: "Figure des radicaux de gauche dans la seconde moitié des années 1970.",
    parties: ["party-prg"],
    functions: ["dirigeant radical"],
    sources: ["source-prg-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-lionel-jospin",
    firstName: "Lionel",
    lastName: "Jospin",
    bornAt: "1937-07-12",
    professions: ["homme politique"],
    summary: "Cadre socialiste montant dans les années 1970, futur Premier ministre.",
    parties: ["party-ps-1969"],
    functions: ["dirigeant socialiste"],
    sources: ["source-archives-socialistes-1969"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-jean-pierre-soisson",
    firstName: "Jean-Pierre",
    lastName: "Soisson",
    bornAt: "1934-11-09",
    professions: ["homme politique"],
    summary: "Responsable libéral associé au Parti républicain giscardien.",
    parties: ["party-parti-republicain-1977", "party-udf"],
    functions: ["dirigeant libéral"],
    sources: ["source-perspective-ri", "source-francearchives-udf"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-francois-leotard",
    firstName: "François",
    lastName: "Léotard",
    bornAt: "1942-03-26",
    diedAt: "2023-04-25",
    professions: ["homme politique"],
    summary: "Responsable du Parti républicain dans la génération libérale issue de l'UDF.",
    parties: ["party-parti-republicain-1977", "party-udf"],
    functions: ["dirigeant libéral"],
    sources: ["source-francearchives-udf"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-michel-poniatowski",
    firstName: "Michel",
    lastName: "Poniatowski",
    bornAt: "1922-05-16",
    diedAt: "2002-01-15",
    professions: ["homme politique"],
    summary: "Proche de Valéry Giscard d'Estaing et responsable des Républicains indépendants.",
    parties: ["party-fnri", "party-parti-republicain-1977", "party-udf"],
    functions: ["dirigeant libéral", "ministre"],
    sources: ["source-persee-republicains-independants", "source-francearchives-udf"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-jacques-duhamel",
    firstName: "Jacques",
    lastName: "Duhamel",
    bornAt: "1924-09-24",
    diedAt: "1977-07-08",
    professions: ["homme politique"],
    summary: "Centriste, associé au Centre démocratie et progrès puis aux recompositions démocrates sociales.",
    parties: ["party-centre-democratie-progres", "party-cds"],
    functions: ["dirigeant centriste", "ministre"],
    sources: ["source-francearchives-cds", "source-francearchives-centre-democrate"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-pierre-mehaignerie",
    firstName: "Pierre",
    lastName: "Méhaignerie",
    bornAt: "1939-05-04",
    professions: ["homme politique"],
    summary: "Responsable démocrate-chrétien associé au CDS et à l'UDF.",
    parties: ["party-cds", "party-udf"],
    functions: ["dirigeant centriste"],
    sources: ["source-francearchives-cds", "source-francearchives-udf"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-jean-pierre-fourcade",
    firstName: "Jean-Pierre",
    lastName: "Fourcade",
    bornAt: "1929-10-18",
    professions: ["homme politique"],
    summary: "Responsable de la majorité giscardienne et figure de l'UDF.",
    parties: ["party-udf"],
    functions: ["dirigeant centriste", "ministre"],
    sources: ["source-francearchives-udf"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-jean-marie-le-pen",
    firstName: "Jean-Marie",
    lastName: "Le Pen",
    bornAt: "1928-06-20",
    diedAt: "2025-01-07",
    professions: ["homme politique"],
    summary: "Président fondateur du Front national et candidat présidentiel en 1974.",
    parties: ["party-fn"],
    functions: ["fondateur", "candidat présidentiel"],
    sources: ["source-fondation-jean-jaures-le-pen"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-pascal-gauchon",
    firstName: "Pascal",
    lastName: "Gauchon",
    bornAt: "1950-03-24",
    professions: ["militant politique"],
    summary: "Militant d'extrême droite associé au Parti des forces nouvelles.",
    parties: ["party-pfn"],
    functions: ["dirigeant nationaliste"],
    sources: ["source-fondation-jean-jaures-le-pen"],
    dataStatus: "unverified"
  },
  {
    id: "person-arlette-laguiller",
    firstName: "Arlette",
    lastName: "Laguiller",
    bornAt: "1940-03-18",
    professions: ["militante politique", "employée de banque"],
    summary: "Militante de Lutte ouvrière et candidate à l'élection présidentielle de 1974.",
    parties: ["party-lutte-ouvriere"],
    functions: ["candidate présidentielle"],
    sources: ["source-lo-histoire", "source-ina-lutte-ouvriere-1974"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-simone-veil",
    firstName: "Simone",
    lastName: "Veil",
    bornAt: "1927-07-13",
    diedAt: "2017-06-30",
    professions: ["magistrate", "femme politique"],
    summary: "Ministre de la Santé portant la loi dépénalisant l'interruption volontaire de grossesse en 1975.",
    parties: ["party-udf"],
    functions: ["ministre"],
    sources: ["source-francearchives-udf", "source-mlac-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-gisele-halimi",
    firstName: "Gisèle",
    lastName: "Halimi",
    bornAt: "1927-07-27",
    diedAt: "2020-07-28",
    professions: ["avocate", "militante féministe"],
    summary: "Avocate et militante féministe associée aux combats pour l'avortement et la contraception.",
    parties: ["party-mlac"],
    functions: ["militante féministe"],
    sources: ["source-mlac-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-simone-de-beauvoir",
    firstName: "Simone",
    lastName: "de Beauvoir",
    bornAt: "1908-01-09",
    diedAt: "1986-04-14",
    professions: ["écrivaine", "philosophe", "militante féministe"],
    summary: "Figure intellectuelle et féministe associée aux mobilisations pour le droit à l'avortement.",
    parties: ["party-mlac"],
    functions: ["militante féministe"],
    sources: ["source-mlac-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-olivier-rolin",
    firstName: "Olivier",
    lastName: "Rolin",
    bornAt: "1947-05-17",
    professions: ["écrivain", "militant politique"],
    summary: "Militant maoïste associé à la Gauche prolétarienne.",
    parties: ["party-gauche-proletarienne"],
    functions: ["militant maoïste"],
    sources: ["source-paris8-gauche-proletarienne", "source-gis-gauche-proletarienne"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-jean-royer",
    firstName: "Jean",
    lastName: "Royer",
    bornAt: "1920-10-31",
    diedAt: "2011-03-25",
    professions: ["homme politique"],
    summary: "Maire de Tours et candidat conservateur à l'élection présidentielle de 1974.",
    parties: [],
    functions: ["maire", "candidat présidentiel"],
    sources: ["source-francearchives-udf"],
    dataStatus: "unverified"
  },
  {
    id: "person-daniel-gluckstein",
    firstName: "Daniel",
    lastName: "Gluckstein",
    bornAt: "1953",
    professions: ["militant politique"],
    summary: "Militant lambertiste et dirigeant du courant du MPPT puis du Parti des travailleurs.",
    parties: ["party-mppt", "party-parti-travailleurs"],
    functions: ["dirigeant politique"],
    sources: ["source-maitron-parti-travailleurs"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-brice-lalonde",
    firstName: "Brice",
    lastName: "Lalonde",
    bornAt: "1946-02-10",
    professions: ["homme politique", "militant écologiste"],
    summary: "Figure de l'écologie politique française, candidat écologiste à l'élection présidentielle de 1981 puis fondateur de Génération écologie.",
    parties: ["party-verts", "party-generation-ecologie"],
    functions: ["candidat présidentiel", "dirigeant écologiste"],
    sources: ["source-francearchives-verts"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-noel-mamere",
    firstName: "Noël",
    lastName: "Mamère",
    bornAt: "1948-12-25",
    professions: ["journaliste", "homme politique"],
    summary: "Figure écologiste et ancien candidat des Verts à l'élection présidentielle.",
    parties: ["party-verts"],
    functions: ["député", "dirigeant écologiste"],
    sources: ["source-francearchives-verts"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-antoine-waechter",
    firstName: "Antoine",
    lastName: "Waechter",
    bornAt: "1949-02-11",
    professions: ["homme politique", "militant écologiste"],
    summary: "Dirigeant des Verts et candidat écologiste à l'élection présidentielle de 1988.",
    parties: ["party-verts"],
    functions: ["candidat présidentiel", "dirigeant écologiste"],
    sources: ["source-francearchives-verts"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-pierre-mauroy",
    firstName: "Pierre",
    lastName: "Mauroy",
    bornAt: "1928-07-05",
    diedAt: "2013-06-07",
    professions: ["homme politique"],
    summary: "Premier ministre de François Mitterrand de 1981 à 1984 et figure du Parti socialiste.",
    parties: ["party-ps-1969"],
    functions: ["Premier ministre", "dirigeant socialiste"],
    sources: ["source-vie-publique-mitterrand-1981"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-andre-lajoinie",
    firstName: "André",
    lastName: "Lajoinie",
    bornAt: "1929-12-26",
    diedAt: "2024-11-26",
    professions: ["homme politique"],
    summary: "Dirigeant communiste et candidat du PCF à l'élection présidentielle de 1988.",
    parties: ["party-pcf"],
    functions: ["candidat présidentiel", "député"],
    sources: ["source-pcf-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-jean-pierre-chevenement",
    firstName: "Jean-Pierre",
    lastName: "Chevènement",
    bornAt: "1939-03-09",
    professions: ["homme politique"],
    summary: "Fondateur du Mouvement des citoyens et figure du républicanisme souverainiste de gauche.",
    parties: ["party-ps-1969", "party-mdc"],
    functions: ["ministre", "fondateur", "dirigeant politique"],
    sources: ["source-francearchives-mdc"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-marie-noelle-lienemann",
    firstName: "Marie-Noëlle",
    lastName: "Lienemann",
    bornAt: "1951-07-12",
    professions: ["femme politique"],
    summary: "Responsable socialiste puis figure du Mouvement des citoyens et de la gauche républicaine.",
    parties: ["party-ps-1969", "party-mdc"],
    functions: ["ministre", "députée européenne"],
    sources: ["source-francearchives-mdc"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-francois-bayrou",
    firstName: "François",
    lastName: "Bayrou",
    bornAt: "1951-05-25",
    professions: ["homme politique"],
    summary: "Dirigeant centriste et président de Force démocrate dans la recomposition de l'UDF des années 1990.",
    parties: ["party-force-democrate", "party-udf"],
    functions: ["ministre", "dirigeant centriste"],
    sources: ["source-francearchives-democratie-liberale"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-alain-madelin",
    firstName: "Alain",
    lastName: "Madelin",
    bornAt: "1946-03-26",
    professions: ["homme politique"],
    summary: "Dirigeant libéral, fondateur de Démocratie libérale après la recomposition de l'UDF.",
    parties: ["party-parti-republicain-1977", "party-democratie-liberale", "party-udf"],
    functions: ["ministre", "fondateur", "dirigeant libéral"],
    sources: ["source-francearchives-democratie-liberale"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-charles-pasqua",
    firstName: "Charles",
    lastName: "Pasqua",
    bornAt: "1927-04-18",
    diedAt: "2015-06-29",
    professions: ["homme politique"],
    summary: "Dirigeant gaulliste, ministre de l'Intérieur et cofondateur du RPF souverainiste de 1999.",
    parties: ["party-rpr", "party-rpf-1999"],
    functions: ["ministre de l'Intérieur", "fondateur", "dirigeant politique"],
    sources: ["source-francearchives-rpf-1999"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-philippe-de-villiers",
    firstName: "Philippe",
    lastName: "de Villiers",
    bornAt: "1949-03-25",
    professions: ["homme politique"],
    summary: "Fondateur du Mouvement pour la France et cofondateur du RPF souverainiste en 1999.",
    parties: ["party-mpf", "party-rpf-1999"],
    functions: ["député européen", "fondateur", "dirigeant souverainiste"],
    sources: ["source-francearchives-mpf", "source-francearchives-rpf-1999"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-jean-saint-josse",
    firstName: "Jean",
    lastName: "Saint-Josse",
    bornAt: "1944-03-22",
    professions: ["homme politique"],
    summary: "Dirigeant de Chasse, pêche, nature et traditions et député européen en 1999.",
    parties: ["party-cpnt"],
    functions: ["député européen", "dirigeant politique"],
    sources: ["source-francearchives-cpnt"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-bruno-megret",
    firstName: "Bruno",
    lastName: "Mégret",
    bornAt: "1949-04-04",
    professions: ["homme politique"],
    summary: "Dirigeant du Front national puis fondateur du Mouvement national républicain après la scission de 1998-1999.",
    parties: ["party-fn", "party-mnr"],
    functions: ["dirigeant nationaliste", "fondateur"],
    sources: ["source-francearchives-mnr"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-edouard-balladur",
    firstName: "Édouard",
    lastName: "Balladur",
    bornAt: "1929-05-02",
    professions: ["homme politique"],
    summary: "Premier ministre pendant la deuxième cohabitation et candidat à l'élection présidentielle de 1995.",
    parties: ["party-rpr", "party-udf"],
    functions: ["Premier ministre", "candidat présidentiel"],
    sources: ["source-vie-publique-chirac-1995"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-alain-juppe",
    firstName: "Alain",
    lastName: "Juppé",
    bornAt: "1945-08-15",
    professions: ["homme politique"],
    summary: "Dirigeant du RPR et Premier ministre de Jacques Chirac de 1995 à 1997.",
    parties: ["party-rpr"],
    functions: ["Premier ministre", "dirigeant gaulliste"],
    sources: ["source-vie-publique-chirac-1995"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-robert-hue",
    firstName: "Robert",
    lastName: "Hue",
    bornAt: "1946-10-19",
    professions: ["homme politique"],
    summary: "Secrétaire national du PCF à partir de 1994 et candidat communiste à l'élection présidentielle de 1995.",
    parties: ["party-pcf"],
    functions: ["dirigeant communiste", "candidat présidentiel"],
    sources: ["source-pcf-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-marie-george-buffet",
    firstName: "Marie-George",
    lastName: "Buffet",
    bornAt: "1949-05-07",
    professions: ["femme politique"],
    summary: "Dirigeante communiste et ministre de la Jeunesse et des Sports dans le gouvernement de la gauche plurielle.",
    parties: ["party-pcf", "party-gauche-plurielle"],
    functions: ["ministre", "dirigeante communiste"],
    sources: ["source-vie-publique-gauche-plurielle", "source-pcf-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-dominique-voynet",
    firstName: "Dominique",
    lastName: "Voynet",
    bornAt: "1958-11-04",
    professions: ["médecin", "femme politique"],
    summary: "Dirigeante des Verts, candidate à l'élection présidentielle de 1995 et ministre de l'Environnement en 1997.",
    parties: ["party-verts", "party-gauche-plurielle"],
    functions: ["candidate présidentielle", "ministre"],
    sources: ["source-francearchives-verts", "source-vie-publique-gauche-plurielle"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-olivier-besancenot",
    firstName: "Olivier",
    lastName: "Besancenot",
    bornAt: "1974-04-18",
    professions: ["facteur", "homme politique"],
    summary: "Porte-parole de la LCR puis figure fondatrice du Nouveau Parti anticapitaliste.",
    parties: ["party-lcr", "party-npa"],
    functions: ["candidat présidentiel", "porte-parole"],
    sources: ["source-francearchives-npa"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-philippe-poutou",
    firstName: "Philippe",
    lastName: "Poutou",
    bornAt: "1967-03-14",
    professions: ["ouvrier", "homme politique"],
    summary: "Militant syndical et figure du Nouveau Parti anticapitaliste.",
    parties: ["party-npa"],
    functions: ["candidat présidentiel", "porte-parole"],
    sources: ["source-francearchives-npa"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-jean-luc-melenchon",
    firstName: "Jean-Luc",
    lastName: "Mélenchon",
    bornAt: "1951-08-19",
    professions: ["homme politique"],
    summary: "Fondateur du Parti de gauche et acteur de la constitution du Front de gauche en 2009.",
    parties: ["party-ps-1969", "party-parti-gauche", "party-front-gauche"],
    functions: ["sénateur", "fondateur", "dirigeant politique"],
    sources: ["source-francearchives-front-gauche"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-nicolas-sarkozy",
    firstName: "Nicolas",
    lastName: "Sarkozy",
    bornAt: "1955-01-28",
    professions: ["homme politique"],
    summary: "Dirigeant de l'UMP, ministre puis président de la République de 2007 à 2012.",
    parties: ["party-ump"],
    functions: ["président de la République", "ministre", "dirigeant politique"],
    sources: ["source-vie-publique-ump", "source-vie-publique-presidentielle-2007"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-segolene-royal",
    firstName: "Ségolène",
    lastName: "Royal",
    bornAt: "1953-09-22",
    professions: ["femme politique"],
    summary: "Candidate du Parti socialiste à l'élection présidentielle de 2007.",
    parties: ["party-ps-1969"],
    functions: ["candidate présidentielle", "ministre"],
    sources: ["source-vie-publique-presidentielle-2007"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-cecile-duflot",
    firstName: "Cécile",
    lastName: "Duflot",
    bornAt: "1975-04-01",
    professions: ["femme politique", "architecte"],
    summary: "Secrétaire nationale des Verts puis figure de la création d'Europe Écologie-Les Verts.",
    parties: ["party-verts", "party-europe-ecologie-2009", "party-eelv"],
    functions: ["dirigeante écologiste", "ministre"],
    sources: ["source-europe-ecologie-2009", "source-francearchives-eelv"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-yannick-jadot",
    firstName: "Yannick",
    lastName: "Jadot",
    bornAt: "1967-07-27",
    professions: ["homme politique", "militant écologiste"],
    summary: "Militant écologiste et membre de la dynamique Europe Écologie aux européennes de 2009.",
    parties: ["party-europe-ecologie-2009", "party-verts"],
    functions: ["député européen", "dirigeant écologiste"],
    sources: ["source-europe-ecologie-2009"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-corinne-lepage",
    firstName: "Corinne",
    lastName: "Lepage",
    bornAt: "1951-05-11",
    professions: ["avocate", "femme politique"],
    summary: "Fondatrice de Cap21 et candidate écologiste centriste à l'élection présidentielle de 2002.",
    parties: ["party-cap21"],
    functions: ["ministre", "candidate présidentielle", "fondatrice"],
    sources: ["source-francearchives-verts"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-francois-fillon",
    firstName: "François",
    lastName: "Fillon",
    bornAt: "1954-03-04",
    professions: ["homme politique"],
    summary: "Premier ministre de Nicolas Sarkozy de 2007 à 2012 et figure de l'UMP.",
    parties: ["party-ump"],
    functions: ["Premier ministre", "dirigeant politique"],
    sources: ["source-vie-publique-ump"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-jean-pierre-raffarin",
    firstName: "Jean-Pierre",
    lastName: "Raffarin",
    bornAt: "1948-08-03",
    professions: ["homme politique"],
    summary: "Premier ministre de Jacques Chirac de 2002 à 2005 et figure de la majorité UMP.",
    parties: ["party-ump"],
    functions: ["Premier ministre", "dirigeant politique"],
    sources: ["source-vie-publique-ump"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-dominique-villepin",
    firstName: "Dominique",
    lastName: "de Villepin",
    bornAt: "1953-11-14",
    professions: ["diplomate", "homme politique"],
    summary: "Premier ministre de Jacques Chirac de 2005 à 2007 et responsable de la réforme du CPE.",
    parties: ["party-ump"],
    functions: ["Premier ministre", "ministre"],
    sources: ["source-vie-publique-ump"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-manon-aubry",
    firstName: "Manon",
    lastName: "Aubry",
    bornAt: "1989-12-22",
    professions: ["femme politique"],
    summary: "Députée européenne de La France insoumise à partir de 2019.",
    parties: ["party-lfi"],
    functions: ["députée européenne", "dirigeante politique"],
    sources: ["source-francearchives-lfi"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-alexis-corbiere",
    firstName: "Alexis",
    lastName: "Corbière",
    bornAt: "1968-08-17",
    professions: ["homme politique"],
    summary: "Député et dirigeant de La France insoumise à partir de 2017.",
    parties: ["party-lfi"],
    functions: ["député", "porte-parole"],
    sources: ["source-francearchives-lfi"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-clementine-autain",
    firstName: "Clémentine",
    lastName: "Autain",
    bornAt: "1973-05-26",
    professions: ["femme politique", "journaliste"],
    summary: "Députée et figure féministe de La France insoumise.",
    parties: ["party-lfi"],
    functions: ["députée", "militante féministe"],
    sources: ["source-francearchives-lfi"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-emmanuel-macron",
    firstName: "Emmanuel",
    lastName: "Macron",
    bornAt: "1977-12-21",
    professions: ["haut fonctionnaire", "homme politique"],
    summary: "Fondateur d'En marche !, puis président de la République à partir de 2017.",
    parties: ["party-lrem"],
    functions: ["président de la République", "ministre", "fondateur"],
    sources: ["source-francearchives-lrem", "source-vie-publique-macron-2017"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-edouard-philippe",
    firstName: "Édouard",
    lastName: "Philippe",
    bornAt: "1970-11-28",
    professions: ["homme politique"],
    summary: "Premier ministre d'Emmanuel Macron de 2017 à 2020.",
    parties: ["party-lrem", "party-lr"],
    functions: ["Premier ministre", "dirigeant politique"],
    sources: ["source-francearchives-lrem"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-christophe-castaner",
    firstName: "Christophe",
    lastName: "Castaner",
    bornAt: "1966-01-03",
    professions: ["homme politique"],
    summary: "Dirigeant de La République en marche et ministre de l'Intérieur à partir de 2018.",
    parties: ["party-lrem"],
    functions: ["ministre", "dirigeant politique"],
    sources: ["source-francearchives-lrem"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-nicolas-dupont-aignan",
    firstName: "Nicolas",
    lastName: "Dupont-Aignan",
    bornAt: "1961-03-07",
    professions: ["homme politique"],
    summary: "Fondateur de Debout la France et candidat souverainiste à plusieurs élections présidentielles.",
    parties: ["party-dlf"],
    functions: ["député", "candidat présidentiel", "fondateur"],
    sources: ["source-francearchives-dlf"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-francois-asselineau",
    firstName: "François",
    lastName: "Asselineau",
    bornAt: "1957-09-14",
    professions: ["haut fonctionnaire", "homme politique"],
    summary: "Fondateur de l'Union populaire républicaine et défenseur du souverainisme de rupture.",
    parties: ["party-upr"],
    functions: ["candidat présidentiel", "fondateur"],
    sources: ["source-upr-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-marine-le-pen",
    firstName: "Marine",
    lastName: "Le Pen",
    bornAt: "1968-08-05",
    professions: ["femme politique"],
    summary: "Dirigeante du Front national puis du Rassemblement national à partir de 2011.",
    parties: ["party-fn", "party-rn"],
    functions: ["présidente de parti", "candidate présidentielle"],
    sources: ["source-francearchives-rn"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-helene-thouy",
    firstName: "Hélène",
    lastName: "Thouy",
    bornAt: "1983-12-23",
    professions: ["avocate", "femme politique"],
    summary: "Cofondatrice et dirigeante du Parti animaliste.",
    parties: ["party-parti-animaliste"],
    functions: ["candidate européenne", "fondatrice"],
    sources: ["source-parti-animaliste"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-eric-zemmour",
    firstName: "Éric",
    lastName: "Zemmour",
    bornAt: "1958-08-31",
    professions: ["journaliste", "écrivain", "homme politique"],
    summary: "Fondateur de Reconquête en 2021 ; son activité politique est postérieure à la décennie 2010-2020.",
    parties: ["party-reconquete"],
    functions: ["fondateur", "candidat présidentiel"],
    sources: ["source-francearchives-rn"],
    dataStatus: "unverified"
  },
  {
    id: "person-francois-hollande",
    firstName: "François",
    lastName: "Hollande",
    bornAt: "1954-08-12",
    professions: ["homme politique"],
    summary: "Premier secrétaire du Parti socialiste puis président de la République de 2012 à 2017.",
    parties: ["party-ps-1969"],
    functions: ["président de la République", "Premier secrétaire"],
    sources: ["source-vie-publique-hollande-2012"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-eva-joly",
    firstName: "Eva",
    lastName: "Joly",
    bornAt: "1943-12-05",
    professions: ["magistrate", "femme politique"],
    summary: "Candidate d'Europe Écologie-Les Verts à l'élection présidentielle de 2012.",
    parties: ["party-eelv"],
    functions: ["candidate présidentielle", "députée européenne"],
    sources: ["source-francearchives-eelv"],
    dataStatus: "partially_verified"
  },
  {
    id: "person-benoit-hamon",
    firstName: "Benoît",
    lastName: "Hamon",
    bornAt: "1967-06-26",
    professions: ["homme politique"],
    summary: "Candidat du Parti socialiste à l'élection présidentielle de 2017.",
    parties: ["party-ps-1969"],
    functions: ["candidat présidentiel", "ministre"],
    sources: ["source-vie-publique-macron-2017"],
    dataStatus: "partially_verified"
  }
];

export const elections: Election[] = [
  {
    id: "election-legislatives-1919",
    name: "Élections législatives de 1919",
    date: "1919-11-16",
    type: "legislative",
    regime: "regime-troisieme-republique",
    context: "Premier scrutin législatif national après la Grande Guerre, marqué par la victoire du Bloc national et par la Chambre dite bleu horizon.",
    electoralSystem: "Scrutin de liste départemental à un tour, combinant représentation proportionnelle et prime majoritaire.",
    resultsNote: "Le dataset encode la victoire politique du Bloc national mais n'intègre pas encore de tableau de résultats chiffrés détaillés.",
    parties: ["party-bloc-national", "party-ard", "party-federation-republicaine", "party-alp", "party-radicaux-independants", "party-sfio"],
    persons: [],
    consequences: "La majorité parlementaire bascule vers une coalition de centre et de droite dans le contexte de sortie de guerre.",
    sources: ["source-assemblee-bloc-national-1919", "source-larousse-bloc-national"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-legislatives-1924",
    name: "Élections législatives de 1924",
    date: "1924-05-11",
    type: "legislative",
    regime: "regime-troisieme-republique",
    context: "Scrutin marqué par la victoire du Cartel des gauches contre les forces issues du Bloc national.",
    electoralSystem: "Scrutin de liste départemental à un tour, combinant représentation proportionnelle et prime majoritaire.",
    resultsNote: "Le dataset encode la victoire politique du Cartel des gauches mais ne contient pas encore de résultats chiffrés complets.",
    parties: ["party-cartel-des-gauches", "party-prrrs", "party-sfio", "party-prs", "party-bloc-national", "party-ard", "party-federation-republicaine"],
    persons: ["person-edouard-herriot"],
    consequences: "La victoire conduit à la formation d'un gouvernement dirigé par Édouard Herriot.",
    sources: ["source-jstor-cartel-socialistes", "source-france-memoire-herriot-cartel"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-legislatives-1932",
    name: "Élections législatives de 1932",
    date: "1932-05-01",
    type: "legislative",
    regime: "regime-troisieme-republique",
    context: "Scrutin marqué par une nouvelle victoire des gauches, dans un contexte de crise économique et de fragilité parlementaire.",
    electoralSystem: "Scrutin uninominal majoritaire à deux tours dans le cadre de l'arrondissement.",
    resultsNote: "Les chiffres détaillés ne sont pas intégrés tant qu'une table institutionnelle contrôlée n'a pas été ajoutée.",
    parties: ["party-cartel-des-gauches", "party-prrrs", "party-sfio", "party-prs", "party-ard", "party-federation-republicaine", "party-pcf"],
    persons: ["person-edouard-herriot"],
    consequences: "La séquence conduit à un gouvernement Herriot, puis à une forte instabilité ministérielle.",
    sources: ["source-jstor-cartel-socialistes", "source-france-memoire-herriot-cartel"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-legislatives-1936",
    name: "Élections législatives de 1936",
    date: "1936-04-26",
    type: "legislative",
    regime: "regime-troisieme-republique",
    context: "Scrutin tenu dans le contexte de la crise des années 1930 et de l'alliance du Front populaire.",
    electoralSystem: "Scrutin uninominal majoritaire à deux tours dans le cadre de l'arrondissement.",
    resultsNote: "Les chiffres détaillés ne sont pas intégrés dans ce premier dataset afin de ne pas les reconstituer sans table source contrôlée.",
    parties: ["party-sfio", "party-pcf", "party-prrrs", "party-front-populaire", "party-pdp", "party-ard", "party-federation-republicaine", "party-croix-de-feu", "party-jeunesses-patriotes", "party-action-francaise"],
    persons: ["person-leon-blum", "person-edouard-daladier", "person-maurice-thorez"],
    consequences: "La victoire parlementaire du Front populaire conduit à la formation du gouvernement Blum ; le PCF soutient la majorité sans participer au gouvernement.",
    sources: ["source-vie-publique-front-populaire", "source-assemblee-1936"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-referendum-1958",
    name: "Référendum constitutionnel de 1958",
    date: "1958-09-28",
    type: "referendum",
    regime: "regime-quatrieme-republique",
    context: "Consultation sur le projet constitutionnel qui fonde la Ve République.",
    resultsNote: "Le projet de Constitution est approuvé par 85,1 % des suffrages exprimés.",
    parties: ["party-unr"],
    persons: ["person-charles-de-gaulle"],
    consequences: "La Constitution est promulguée le 4 octobre 1958.",
    sources: ["source-vie-publique-1958", "source-conseil-constitutionnel-1958"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-legislatives-1951",
    name: "Élections législatives de 1951",
    date: "1951-06-17",
    type: "legislative",
    regime: "regime-quatrieme-republique",
    context: "Scrutin de la IVe République dans un contexte de guerre froide, d'exclusion du PCF du pouvoir et de recomposition à droite autour du RPF et du CNIP.",
    electoralSystem: "Scrutin de liste départemental à un tour, à la représentation proportionnelle avec système des apparentements.",
    resultsNote: "Les pourcentages et sièges fournis par listes secondaires ne sont pas intégrés tant qu'une table officielle contrôlée n'a pas été ajoutée.",
    parties: ["party-pcf", "party-sfio", "party-mrp", "party-rpf", "party-cnip", "party-prrrs", "party-udsr", "party-prl"],
    persons: ["person-maurice-thorez", "person-guy-mollet", "person-charles-de-gaulle", "person-roger-duchet"],
    consequences: "L'Assemblée issue du scrutin renforce les effets de coalition et de blocage parlementaire de la IVe République.",
    sources: ["source-francearchives-mrp", "source-fondation-de-gaulle-rpf", "source-francearchives-cnip", "source-francearchives-udsr"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-legislatives-1956",
    name: "Élections législatives de 1956",
    date: "1956-01-02",
    type: "legislative",
    regime: "regime-quatrieme-republique",
    context: "Scrutin marqué par le Front républicain, la force électorale du PCF et les tensions coloniales.",
    electoralSystem: "Scrutin de liste départemental à un tour, à la représentation proportionnelle avec système des apparentements.",
    resultsNote: "Le PCF est traité comme force parlementaire distincte du Front républicain, conformément à la prudence méthodologique.",
    parties: ["party-front-republicain-1956", "party-pcf", "party-sfio", "party-prrrs", "party-udsr", "party-mrp", "party-cnip", "party-rpf"],
    persons: ["person-guy-mollet", "person-pierre-mendes-france", "person-francois-mitterrand", "person-maurice-thorez"],
    consequences: "La victoire du Front républicain conduit au gouvernement Guy Mollet, rapidement dominé par la guerre d'Algérie.",
    sources: ["source-francearchives-udsr", "source-francearchives-psa"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-legislatives-1958",
    name: "Élections législatives de 1958",
    date: "1958-11-23",
    type: "legislative",
    regime: "regime-cinquieme-republique",
    context: "Premières élections législatives de la Ve République, dominées par la poussée gaulliste de l'UNR.",
    electoralSystem: "Scrutin uninominal majoritaire à deux tours.",
    resultsNote: "Les chiffres fournis dans la liste utilisateur ne sont pas intégrés sans source officielle électorale.",
    parties: ["party-unr", "party-cnip", "party-mrp", "party-sfio", "party-prrrs", "party-udsr", "party-pcf", "party-psa"],
    persons: ["person-charles-de-gaulle", "person-georges-pompidou", "person-jacques-chaban-delmas", "person-maurice-thorez", "person-guy-mollet"],
    consequences: "Le scrutin installe une majorité favorable aux nouvelles institutions et marginalise fortement le PCF dans la représentation parlementaire.",
    sources: ["source-francearchives-unr", "source-archives-nationales-1958"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-referendum-1962",
    name: "Référendum constitutionnel de 1962",
    date: "1962-10-28",
    type: "referendum",
    regime: "regime-cinquieme-republique",
    context: "Référendum sur l'élection du président de la République au suffrage universel direct.",
    resultsNote: "Le « oui » l'emporte avec 62,25 % des suffrages exprimés.",
    parties: ["party-unr", "party-pcf", "party-sfio", "party-mrp", "party-cnip", "party-psu"],
    persons: ["person-charles-de-gaulle", "person-francois-mitterrand", "person-guy-mollet"],
    consequences: "La réforme accroît la légitimité présidentielle et accentue la présidentialisation de la Ve République.",
    sources: ["source-vie-publique-referendum-1962", "source-conseil-constitutionnel-1962"],
    dataStatus: "verified"
  },
  {
    id: "election-legislatives-1962",
    name: "Élections législatives de 1962",
    date: "1962-11-18",
    type: "legislative",
    regime: "regime-cinquieme-republique",
    context: "Scrutin organisé après la dissolution qui suit la censure du gouvernement Pompidou.",
    electoralSystem: "Scrutin uninominal majoritaire à deux tours.",
    resultsNote: "Les chiffres de voix et sièges ne sont pas intégrés sans source électorale officielle.",
    parties: ["party-unr", "party-fnri", "party-cnip", "party-mrp", "party-pcf", "party-sfio", "party-prrrs", "party-psu"],
    persons: ["person-georges-pompidou", "person-charles-de-gaulle", "person-maurice-thorez"],
    consequences: "La majorité gaulliste est consolidée autour de l'UNR et de ses alliés.",
    sources: ["source-francearchives-unr", "source-persee-republicains-independants"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-presidentielle-1965",
    name: "Élection présidentielle de 1965",
    date: "1965-12-05",
    type: "presidentielle",
    regime: "regime-cinquieme-republique",
    context: "Première élection présidentielle française au suffrage universel direct sous la Ve République.",
    resultsNote: "Charles de Gaulle est réélu au second tour avec 55,20 % des suffrages exprimés.",
    parties: ["party-unr", "party-cir", "party-fgds", "party-centre-democrate", "party-pcf", "party-sfio", "party-psu", "party-prrrs"],
    persons: ["person-charles-de-gaulle", "person-francois-mitterrand", "person-jean-lecanuet"],
    consequences: "La mise en ballottage du général de Gaulle révèle la structuration d'une opposition présidentielle.",
    sources: ["source-vie-publique-referendum-1962", "source-francearchives-centre-democrate"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-legislatives-1967",
    name: "Élections législatives de 1967",
    date: "1967-03-05",
    type: "legislative",
    regime: "regime-cinquieme-republique",
    context: "Scrutin marqué par une majorité gaulliste réduite et par l'organisation de la gauche non communiste autour de la FGDS.",
    electoralSystem: "Scrutin uninominal majoritaire à deux tours.",
    resultsNote: "Les résultats chiffrés ne sont pas intégrés sans table officielle contrôlée.",
    parties: ["party-unr", "party-fnri", "party-centre-democrate", "party-cnip", "party-pcf", "party-fgds", "party-sfio", "party-psu", "party-prrrs"],
    persons: ["person-georges-pompidou", "person-francois-mitterrand", "person-guy-mollet", "person-jean-lecanuet"],
    consequences: "La majorité gaulliste subsiste mais la compétition parlementaire se resserre.",
    sources: ["source-francearchives-unr", "source-persee-centre-democrate", "source-archives-socialistes-1969"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-legislatives-1968",
    name: "Élections législatives de 1968",
    date: "1968-06-23",
    type: "legislative",
    regime: "regime-cinquieme-republique",
    context: "Scrutin organisé après la dissolution consécutive à la crise de Mai 68.",
    electoralSystem: "Scrutin uninominal majoritaire à deux tours.",
    resultsNote: "Les résultats chiffrés ne sont pas intégrés sans source officielle.",
    parties: ["party-udr", "party-fnri", "party-centre-democrate", "party-cnip", "party-pcf", "party-fgds", "party-sfio", "party-psu"],
    persons: ["person-georges-pompidou", "person-charles-de-gaulle", "person-francois-mitterrand", "person-jacques-duclos"],
    consequences: "La droite gaulliste obtient une très large majorité dans le contexte de sortie de crise.",
    sources: ["source-francearchives-unr", "source-persee-republicains-independants", "source-archives-socialistes-1969"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-referendum-1969",
    name: "Référendum de 1969 sur le Sénat et la régionalisation",
    date: "1969-04-27",
    type: "referendum",
    regime: "regime-cinquieme-republique",
    context: "Référendum sur la régionalisation et la réforme du Sénat, dont l'échec entraîne la démission de De Gaulle.",
    resultsNote: "Le « non » l'emporte avec 52,41 % des suffrages exprimés.",
    parties: ["party-udr", "party-fnri", "party-centre-democrate", "party-pcf", "party-sfio", "party-psu"],
    persons: ["person-charles-de-gaulle", "person-alain-poher", "person-georges-pompidou"],
    consequences: "Charles de Gaulle démissionne le 28 avril 1969.",
    sources: ["source-senat-referendum-1969"],
    dataStatus: "verified"
  },
  {
    id: "election-presidentielle-1969",
    name: "Élection présidentielle de 1969",
    date: "1969-06-01",
    type: "presidentielle",
    regime: "regime-cinquieme-republique",
    context: "Scrutin anticipé après la démission de De Gaulle.",
    resultsNote: "Georges Pompidou est élu au second tour avec 58,21 % des suffrages exprimés.",
    parties: ["party-udr", "party-centre-democrate", "party-pcf", "party-sfio", "party-psu", "party-ligue-communiste"],
    persons: ["person-georges-pompidou", "person-alain-poher", "person-jacques-duclos", "person-gaston-defferre", "person-michel-rocard", "person-alain-krivine"],
    consequences: "Georges Pompidou est élu président de la République.",
    sources: ["source-senat-referendum-1969", "source-maitron-krivine"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-legislatives-1973",
    name: "Élections législatives de 1973",
    date: "1973-03-04",
    type: "legislative",
    regime: "regime-cinquieme-republique",
    context: "Scrutin de la fin de la présidence Pompidou, marqué par la majorité gaulliste et centriste face à l'union de la gauche portée par le Programme commun.",
    electoralSystem: "Scrutin uninominal majoritaire à deux tours.",
    resultsNote: "Les chiffres de voix et de sièges proposés dans les listes secondaires ne sont pas intégrés sans table officielle.",
    parties: ["party-udr", "party-fnri", "party-cds", "party-prrrs", "party-pcf", "party-ps-1969", "party-prg", "party-programme-commun-gauche", "party-psu", "party-fn"],
    persons: ["person-georges-pompidou", "person-francois-mitterrand", "person-georges-marchais", "person-robert-fabre", "person-valery-giscard-destaing"],
    consequences: "La majorité présidentielle conserve l'Assemblée, mais l'union de la gauche devient un pôle électoral structurant.",
    sources: ["source-francearchives-unr", "source-persee-republicains-independants", "source-pcf-histoire", "source-prg-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-presidentielle-1974",
    name: "Élection présidentielle de 1974",
    date: "1974-05-05",
    type: "presidentielle",
    regime: "regime-cinquieme-republique",
    context: "Scrutin anticipé après la mort de Georges Pompidou, opposant notamment Valéry Giscard d'Estaing, François Mitterrand et Jacques Chaban-Delmas.",
    resultsNote: "Valéry Giscard d'Estaing est élu avec 50,81 % des suffrages exprimés.",
    parties: ["party-fnri", "party-udr", "party-ps-1969", "party-programme-commun-gauche", "party-pcf", "party-lutte-ouvriere", "party-lcr", "party-amis-terre", "party-fn"],
    persons: ["person-valery-giscard-destaing", "person-francois-mitterrand", "person-jacques-chaban-delmas", "person-arlette-laguiller", "person-alain-krivine", "person-rene-dumont", "person-jean-marie-le-pen", "person-jean-royer"],
    consequences: "Valéry Giscard d'Estaing est élu président de la République et ouvre une recomposition de la majorité non gaulliste.",
    sources: ["source-francearchives-udf", "source-ina-lutte-ouvriere-1974", "source-maitron-krivine", "source-fondation-jean-jaures-le-pen"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-legislatives-1978",
    name: "Élections législatives de 1978",
    date: "1978-03-12",
    type: "legislative",
    regime: "regime-cinquieme-republique",
    context: "Scrutin disputé après la rupture du Programme commun, opposant la majorité RPR-UDF à la gauche PS-PCF-radicaux de gauche.",
    electoralSystem: "Scrutin uninominal majoritaire à deux tours.",
    resultsNote: "Les données chiffrées seront ajoutées uniquement à partir d'une source électorale dédiée.",
    parties: ["party-rpr", "party-udf", "party-cds", "party-parti-republicain-1977", "party-parti-radical-valoisien", "party-pcf", "party-ps-1969", "party-prg", "party-programme-commun-gauche", "party-fn"],
    persons: ["person-jacques-chirac", "person-valery-giscard-destaing", "person-raymond-barre", "person-francois-mitterrand", "person-georges-marchais"],
    consequences: "La droite conserve la majorité parlementaire et la concurrence entre RPR et UDF s'installe durablement.",
    sources: ["source-francearchives-rpr", "source-francearchives-udf", "source-pcf-histoire", "source-prg-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-europeennes-1979",
    name: "Élections européennes de 1979",
    date: "1979-06-10",
    type: "autre",
    regime: "regime-cinquieme-republique",
    context: "Première élection du Parlement européen au suffrage universel direct, révélant aussi une première structuration électorale écologiste.",
    electoralSystem: "Scrutin de liste national à un tour, à la représentation proportionnelle.",
    resultsNote: "La présence d'Europe Écologie est encodée, mais les pourcentages et sièges restent à intégrer avec une source officielle.",
    parties: ["party-udf", "party-rpr", "party-pcf", "party-ps-1969", "party-europe-ecologie-1979", "party-fn"],
    persons: ["person-valery-giscard-destaing", "person-jacques-chirac", "person-francois-mitterrand", "person-georges-marchais", "person-rene-dumont"],
    consequences: "Le scrutin installe l'élection européenne comme nouveau terrain de concurrence entre majorité, gauche et écologie politique.",
    sources: ["source-francearchives-udf", "source-francearchives-rpr", "source-europe-ecologie-1979"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-presidentielle-1981",
    name: "Élection présidentielle de 1981",
    date: "1981-04-26",
    type: "presidentielle",
    regime: "regime-cinquieme-republique",
    context: "Scrutin qui oppose notamment François Mitterrand, Valéry Giscard d'Estaing, Jacques Chirac et Georges Marchais dans un contexte d'union de la gauche.",
    resultsNote: "Les résultats détaillés par tour sont à compléter depuis une table électorale officielle.",
    parties: ["party-ps-1969", "party-pcf", "party-udf", "party-rpr", "party-verts", "party-lcr", "party-fn"],
    persons: ["person-francois-mitterrand", "person-valery-giscard-destaing", "person-jacques-chirac", "person-georges-marchais", "person-brice-lalonde", "person-alain-krivine", "person-jean-marie-le-pen"],
    consequences: "François Mitterrand est élu président de la République, ouvrant la première alternance présidentielle de la Ve République.",
    sources: ["source-vie-publique-mitterrand-1981"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-legislatives-1981",
    name: "Élections législatives de 1981",
    date: "1981-06-14",
    type: "legislative",
    regime: "regime-cinquieme-republique",
    context: "Scrutin organisé après l'élection de François Mitterrand et la dissolution de l'Assemblée nationale.",
    electoralSystem: "Scrutin uninominal majoritaire à deux tours.",
    resultsNote: "Les pourcentages et sièges détaillés ne sont pas intégrés sans source électorale officielle consolidée.",
    parties: ["party-ps-1969", "party-pcf", "party-prg", "party-udf", "party-rpr", "party-fn"],
    persons: ["person-francois-mitterrand", "person-pierre-mauroy", "person-georges-marchais", "person-jacques-chirac", "person-valery-giscard-destaing"],
    consequences: "La gauche obtient une majorité parlementaire et forme le gouvernement Pierre Mauroy, avec une participation communiste jusqu'en 1984.",
    sources: ["source-vie-publique-mitterrand-1981", "source-assemblee-legislatives-1988"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-legislatives-1986",
    name: "Élections législatives de 1986",
    date: "1986-03-16",
    type: "legislative",
    regime: "regime-cinquieme-republique",
    context: "Premières élections législatives de la Ve République au scrutin proportionnel, marquées par la progression du Front national.",
    electoralSystem: "Scrutin proportionnel départemental à un tour.",
    resultsNote: "Les données chiffrées détaillées par liste sont à compléter depuis une table électorale officielle.",
    parties: ["party-rpr", "party-udf", "party-ps-1969", "party-pcf", "party-fn", "party-prg", "party-verts"],
    persons: ["person-jacques-chirac", "person-raymond-barre", "person-francois-mitterrand", "person-georges-marchais", "person-jean-marie-le-pen"],
    consequences: "La droite parlementaire devient majoritaire et ouvre la première cohabitation, avec Jacques Chirac comme Premier ministre.",
    sources: ["source-vie-publique-cohabitation-1986", "source-assemblee-legislatives-1988", "source-fondation-jean-jaures-fn-1984"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-presidentielle-1988",
    name: "Élection présidentielle de 1988",
    date: "1988-04-24",
    type: "presidentielle",
    regime: "regime-cinquieme-republique",
    context: "Élection présidentielle organisée à la fin de la première cohabitation, opposant notamment François Mitterrand et Jacques Chirac.",
    resultsNote: "Les résultats détaillés par tour sont à compléter depuis une table électorale officielle.",
    parties: ["party-ps-1969", "party-rpr", "party-udf", "party-fn", "party-pcf", "party-verts", "party-lcr"],
    persons: ["person-francois-mitterrand", "person-jacques-chirac", "person-raymond-barre", "person-jean-marie-le-pen", "person-andre-lajoinie", "person-antoine-waechter", "person-alain-krivine"],
    consequences: "François Mitterrand est réélu président de la République et met fin à la première cohabitation.",
    sources: ["source-vie-publique-mitterrand-1981", "source-vie-publique-cohabitation-1986"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-legislatives-1988",
    name: "Élections législatives de 1988",
    date: "1988-06-05",
    type: "legislative",
    regime: "regime-cinquieme-republique",
    context: "Scrutin consécutif à la réélection de François Mitterrand et à la dissolution de l'Assemblée nationale.",
    electoralSystem: "Scrutin majoritaire à deux tours.",
    resultsNote: "Les pourcentages et sièges détaillés ne sont pas intégrés sans source électorale officielle consolidée.",
    parties: ["party-ps-1969", "party-pcf", "party-rpr", "party-udf", "party-fn", "party-prg", "party-verts"],
    persons: ["person-francois-mitterrand", "person-michel-rocard", "person-jacques-chirac", "person-georges-marchais", "person-jean-marie-le-pen"],
    consequences: "La majorité présidentielle est reconstituée autour du PS, sans majorité absolue initiale ; Michel Rocard forme un gouvernement d'ouverture.",
    sources: ["source-assemblee-legislatives-1988", "source-vie-publique-mitterrand-1981"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-europeennes-1989",
    name: "Élections européennes de 1989",
    date: "1989-06-18",
    type: "autre",
    regime: "regime-cinquieme-republique",
    context: "Scrutin européen marqué par la progression des listes écologistes et la consolidation électorale du Front national.",
    electoralSystem: "Scrutin de liste à la représentation proportionnelle.",
    resultsNote: "Les résultats détaillés par liste et le nombre de sièges sont à compléter depuis une source électorale officielle.",
    parties: ["party-ps-1969", "party-udf", "party-rpr", "party-pcf", "party-verts", "party-fn"],
    persons: ["person-francois-mitterrand", "person-valery-giscard-destaing", "person-jacques-chirac", "person-georges-marchais", "person-antoine-waechter", "person-jean-marie-le-pen"],
    consequences: "L'écologie politique devient une force électorale nationale visible et le FN confirme son implantation.",
    sources: ["source-francearchives-verts", "source-fondation-jean-jaures-fn-1984"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-referendum-maastricht-1992",
    name: "Référendum sur le traité de Maastricht",
    date: "1992-09-20",
    type: "referendum",
    regime: "regime-cinquieme-republique",
    context: "Consultation nationale sur la ratification du traité de Maastricht, dans un contexte de divisions sur la construction européenne.",
    resultsNote: "Les résultats détaillés ne sont pas intégrés sans source électorale officielle.",
    parties: ["party-ps-1969", "party-udf", "party-rpr", "party-pcf", "party-mdc", "party-fn"],
    persons: ["person-francois-mitterrand", "person-jean-pierre-chevenement", "person-charles-pasqua"],
    consequences: "La ratification est approuvée de justesse et prépare la création de l'Union européenne et de la monnaie unique.",
    sources: ["source-vie-publique-maastricht"],
    dataStatus: "verified"
  },
  {
    id: "election-legislatives-1993",
    name: "Élections législatives de 1993",
    date: "1993-03-21",
    type: "legislative",
    regime: "regime-cinquieme-republique",
    context: "Scrutin marqué par la victoire de la droite parlementaire et l'ouverture de la deuxième cohabitation.",
    electoralSystem: "Scrutin majoritaire à deux tours.",
    resultsNote: "Les résultats chiffrés détaillés par parti ne sont pas intégrés sans table électorale officielle consolidée.",
    parties: ["party-rpr", "party-udf", "party-ps-1969", "party-pcf", "party-fn", "party-verts", "party-prg"],
    persons: ["person-edouard-balladur", "person-francois-mitterrand", "person-jacques-chirac", "person-georges-marchais", "person-jean-marie-le-pen"],
    consequences: "La droite obtient une large majorité et Édouard Balladur devient Premier ministre de François Mitterrand.",
    sources: ["source-francearchives-1990s-elections"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-presidentielle-1995",
    name: "Élection présidentielle de 1995",
    date: "1995-04-23",
    type: "presidentielle",
    regime: "regime-cinquieme-republique",
    context: "Élection marquée par la compétition entre Jacques Chirac, Lionel Jospin et Édouard Balladur, ainsi que par la progression du Front national.",
    resultsNote: "Les résultats détaillés par tour sont à compléter depuis une table électorale officielle.",
    parties: ["party-rpr", "party-ps-1969", "party-udf", "party-fn", "party-pcf", "party-lcr", "party-mpf", "party-verts"],
    persons: ["person-jacques-chirac", "person-lionel-jospin", "person-edouard-balladur", "person-jean-marie-le-pen", "person-robert-hue", "person-alain-krivine", "person-philippe-de-villiers", "person-dominique-voynet"],
    consequences: "Jacques Chirac est élu président de la République et nomme Alain Juppé Premier ministre.",
    sources: ["source-vie-publique-chirac-1995"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-legislatives-1997",
    name: "Élections législatives de 1997",
    date: "1997-05-25",
    type: "legislative",
    regime: "regime-cinquieme-republique",
    context: "Scrutin consécutif à la dissolution de l'Assemblée nationale décidée par Jacques Chirac.",
    electoralSystem: "Scrutin majoritaire à deux tours.",
    resultsNote: "Les résultats chiffrés détaillés par parti ne sont pas intégrés sans table électorale officielle consolidée.",
    parties: ["party-gauche-plurielle", "party-ps-1969", "party-pcf", "party-verts", "party-mdc", "party-prg", "party-rpr", "party-udf", "party-fn"],
    persons: ["person-lionel-jospin", "person-jacques-chirac", "person-robert-hue", "person-dominique-voynet", "person-jean-pierre-chevenement"],
    consequences: "La victoire de la gauche plurielle ouvre la troisième cohabitation ; Lionel Jospin devient Premier ministre.",
    sources: ["source-vie-publique-gauche-plurielle", "source-francearchives-1990s-elections"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-europeennes-1999",
    name: "Élections européennes de 1999",
    date: "1999-06-13",
    type: "autre",
    regime: "regime-cinquieme-republique",
    context: "Scrutin marqué par la concurrence entre la liste RPF souverainiste, le PS, les Verts, le FN, le MPF et les listes de la droite parlementaire.",
    electoralSystem: "Scrutin de liste à la représentation proportionnelle.",
    resultsNote: "Les pourcentages et sièges détaillés sont à compléter depuis une source électorale officielle.",
    parties: ["party-ps-1969", "party-rpr", "party-udf", "party-rpf-1999", "party-fn", "party-mnr", "party-verts", "party-mdc", "party-mpf", "party-cpnt"],
    persons: ["person-francois-mitterrand", "person-charles-pasqua", "person-philippe-de-villiers", "person-jean-marie-le-pen", "person-bruno-megret", "person-dominique-voynet", "person-jean-pierre-chevenement", "person-jean-saint-josse"],
    consequences: "Le scrutin confirme la visibilité du souverainisme, de l'écologie et des formations issues de la scission du FN.",
    sources: ["source-francearchives-rpf-1999", "source-francearchives-verts", "source-francearchives-mnr", "source-francearchives-cpnt"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-presidentielle-2002",
    name: "Élection présidentielle de 2002",
    date: "2002-04-21",
    type: "presidentielle",
    regime: "regime-cinquieme-republique",
    context: "Scrutin marqué par l'élimination de Lionel Jospin au premier tour et la présence de Jean-Marie Le Pen au second tour face à Jacques Chirac.",
    resultsNote: "Les résultats détaillés par tour sont à compléter depuis une table électorale officielle.",
    parties: ["party-rpr", "party-ump", "party-fn", "party-ps-1969", "party-udf", "party-lcr", "party-verts", "party-pcf", "party-mrc", "party-cap21"],
    persons: ["person-jacques-chirac", "person-jean-marie-le-pen", "person-lionel-jospin", "person-francois-bayrou", "person-olivier-besancenot", "person-noel-mamere", "person-robert-hue", "person-jean-pierre-chevenement", "person-corinne-lepage"],
    consequences: "Jacques Chirac est réélu au second tour et la création de l'UMP accélère la recomposition de la droite parlementaire.",
    sources: ["source-vie-publique-presidentielle-2002"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-legislatives-2002",
    name: "Élections législatives de 2002",
    date: "2002-06-09",
    type: "legislative",
    regime: "regime-cinquieme-republique",
    context: "Scrutin organisé après la réélection de Jacques Chirac et la constitution de l'UMP.",
    electoralSystem: "Scrutin majoritaire à deux tours.",
    resultsNote: "Les résultats chiffrés détaillés par formation ne sont pas intégrés sans table électorale officielle consolidée.",
    parties: ["party-ump", "party-ps-1969", "party-udf", "party-fn", "party-pcf", "party-verts", "party-lcr"],
    persons: ["person-jacques-chirac", "person-jean-pierre-raffarin", "person-francois-bayrou", "person-lionel-jospin"],
    consequences: "L'UMP obtient une majorité parlementaire et Jean-Pierre Raffarin devient Premier ministre.",
    sources: ["source-vie-publique-ump"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-referendum-tce-2005",
    name: "Référendum sur le traité constitutionnel européen",
    date: "2005-05-29",
    type: "referendum",
    regime: "regime-cinquieme-republique",
    context: "Consultation sur le traité établissant une Constitution pour l'Europe, rejeté par les électeurs français.",
    resultsNote: "Les résultats détaillés ne sont pas intégrés sans source électorale officielle.",
    parties: ["party-ump", "party-ps-1969", "party-udf", "party-pcf", "party-verts", "party-mrc", "party-fn"],
    persons: ["person-jacques-chirac", "person-nicolas-sarkozy", "person-francois-bayrou", "person-jean-pierre-chevenement"],
    consequences: "Le rejet approfondit les divisions françaises sur la construction européenne et fragilise les directions partisanes favorables au traité.",
    sources: ["source-vie-publique-maastricht"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-presidentielle-2007",
    name: "Élection présidentielle de 2007",
    date: "2007-04-22",
    type: "presidentielle",
    regime: "regime-cinquieme-republique",
    context: "Élection opposant notamment Nicolas Sarkozy, Ségolène Royal et François Bayrou.",
    resultsNote: "Les résultats détaillés par tour sont à compléter depuis une table électorale officielle.",
    parties: ["party-ump", "party-ps-1969", "party-modem", "party-fn", "party-lcr", "party-mpf", "party-pcf", "party-verts"],
    persons: ["person-nicolas-sarkozy", "person-segolene-royal", "person-francois-bayrou", "person-jean-marie-le-pen", "person-olivier-besancenot", "person-philippe-de-villiers", "person-marie-george-buffet", "person-dominique-voynet"],
    consequences: "Nicolas Sarkozy est élu président de la République et François Fillon devient Premier ministre.",
    sources: ["source-vie-publique-presidentielle-2007"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-legislatives-2007",
    name: "Élections législatives de 2007",
    date: "2007-06-10",
    type: "legislative",
    regime: "regime-cinquieme-republique",
    context: "Scrutin consécutif à l'élection de Nicolas Sarkozy et marqué par la majorité UMP.",
    electoralSystem: "Scrutin majoritaire à deux tours.",
    resultsNote: "Les résultats chiffrés détaillés ne sont pas intégrés sans table électorale officielle consolidée.",
    parties: ["party-ump", "party-ps-1969", "party-modem", "party-fn", "party-pcf", "party-verts", "party-lcr"],
    persons: ["person-nicolas-sarkozy", "person-francois-fillon", "person-segolene-royal", "person-francois-bayrou"],
    consequences: "La majorité présidentielle conserve l'Assemblée nationale et le gouvernement Fillon est confirmé.",
    sources: ["source-vie-publique-ump"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-europeennes-2009",
    name: "Élections européennes de 2009",
    date: "2009-06-07",
    type: "autre",
    regime: "regime-cinquieme-republique",
    context: "Scrutin marqué par le succès de la coalition Europe Écologie et par la constitution du Front de gauche.",
    electoralSystem: "Scrutin de liste à la représentation proportionnelle.",
    resultsNote: "Les pourcentages et sièges détaillés sont à compléter depuis les résultats électoraux officiels.",
    parties: ["party-europe-ecologie-2009", "party-verts", "party-ump", "party-ps-1969", "party-front-gauche", "party-fn", "party-mpf"],
    persons: ["person-daniel-cohn-bendit", "person-cecile-duflot", "person-yannick-jadot", "person-jean-luc-melenchon", "person-marie-george-buffet"],
    consequences: "L'écologie politique devient une force électorale nationale et le Front de gauche s'installe dans l'espace de la gauche radicale.",
    sources: ["source-europe-ecologie-2009", "source-francearchives-front-gauche"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-regionales-2010",
    name: "Élections régionales de 2010",
    date: "2010-03-14",
    type: "autre",
    regime: "regime-cinquieme-republique",
    context: "Scrutin régional marquant la progression des écologistes et la consolidation des alliances de gauche.",
    electoralSystem: "Scrutin de liste à deux tours avec représentation proportionnelle et prime majoritaire.",
    resultsNote: "Les résultats détaillés ne sont pas intégrés sans table électorale officielle.",
    parties: ["party-ps-1969", "party-eelv", "party-ump", "party-front-gauche", "party-fn", "party-modem"],
    persons: ["person-cecile-duflot", "person-jean-luc-melenchon", "person-nicolas-sarkozy"],
    consequences: "Le scrutin confirme la domination électorale de la gauche dans les régions et la structuration d'EELV.",
    sources: ["source-francearchives-eelv"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-presidentielle-2012",
    name: "Élection présidentielle de 2012",
    date: "2012-04-22",
    type: "presidentielle",
    regime: "regime-cinquieme-republique",
    context: "Scrutin opposant notamment François Hollande, Nicolas Sarkozy, Marine Le Pen, Jean-Luc Mélenchon et François Bayrou.",
    resultsNote: "Les résultats détaillés par tour sont à compléter depuis une table électorale officielle.",
    parties: ["party-ps-1969", "party-ump", "party-fn", "party-front-gauche", "party-modem", "party-eelv", "party-npa", "party-dlf"],
    persons: ["person-francois-hollande", "person-nicolas-sarkozy", "person-marine-le-pen", "person-jean-luc-melenchon", "person-francois-bayrou", "person-eva-joly", "person-philippe-poutou", "person-nicolas-dupont-aignan"],
    consequences: "François Hollande est élu président de la République et ouvre l'alternance de 2012.",
    sources: ["source-vie-publique-hollande-2012"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-legislatives-2012",
    name: "Élections législatives de 2012",
    date: "2012-06-10",
    type: "legislative",
    regime: "regime-cinquieme-republique",
    context: "Scrutin organisé après l'élection de François Hollande et marqué par la majorité socialiste.",
    electoralSystem: "Scrutin majoritaire à deux tours.",
    resultsNote: "Les résultats chiffrés détaillés par formation ne sont pas intégrés sans table électorale officielle consolidée.",
    parties: ["party-ps-1969", "party-ump", "party-front-gauche", "party-eelv", "party-fn", "party-modem"],
    persons: ["person-francois-hollande", "person-jean-luc-melenchon", "person-cecile-duflot", "person-marine-le-pen"],
    consequences: "La gauche obtient la majorité à l'Assemblée nationale et forme le gouvernement Jean-Marc Ayrault.",
    sources: ["source-vie-publique-hollande-2012"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-europeennes-2014",
    name: "Élections européennes de 2014",
    date: "2014-05-25",
    type: "autre",
    regime: "regime-cinquieme-republique",
    context: "Scrutin européen marqué par la progression du Front national et la concurrence entre PS, UMP, UDI-MoDem et écologistes.",
    electoralSystem: "Scrutin de liste à la représentation proportionnelle.",
    resultsNote: "Les résultats chiffrés détaillés ne sont pas intégrés sans table électorale officielle.",
    parties: ["party-fn", "party-ump", "party-ps-1969", "party-eelv", "party-modem", "party-front-gauche"],
    persons: ["person-marine-le-pen", "person-nicolas-sarkozy", "person-francois-hollande", "person-yannick-jadot"],
    consequences: "Le scrutin confirme la crise des partis de gouvernement et la montée du Front national.",
    sources: ["source-francearchives-rn", "source-francearchives-eelv"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-presidentielle-2017",
    name: "Élection présidentielle de 2017",
    date: "2017-04-23",
    type: "presidentielle",
    regime: "regime-cinquieme-republique",
    context: "Scrutin marquant la recomposition du paysage politique autour d'Emmanuel Macron, de Marine Le Pen, de François Fillon et de Jean-Luc Mélenchon.",
    resultsNote: "Les résultats détaillés par tour sont à compléter depuis une table électorale officielle.",
    parties: ["party-lrem", "party-rn", "party-lr", "party-lfi", "party-ps-1969", "party-dlf", "party-npa", "party-upr"],
    persons: ["person-emmanuel-macron", "person-marine-le-pen", "person-francois-fillon", "person-jean-luc-melenchon", "person-benoit-hamon", "person-nicolas-dupont-aignan", "person-philippe-poutou", "person-francois-asselineau"],
    consequences: "Emmanuel Macron est élu président de la République et fonde une majorité nouvelle autour de LREM.",
    sources: ["source-vie-publique-macron-2017"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-legislatives-2017",
    name: "Élections législatives de 2017",
    date: "2017-06-11",
    type: "legislative",
    regime: "regime-cinquieme-republique",
    context: "Scrutin organisé après l'élection d'Emmanuel Macron et marqué par la majorité LREM-MoDem.",
    electoralSystem: "Scrutin majoritaire à deux tours.",
    resultsNote: "Les résultats chiffrés détaillés ne sont pas intégrés sans table électorale officielle consolidée.",
    parties: ["party-lrem", "party-modem", "party-lr", "party-ps-1969", "party-lfi", "party-rn", "party-eelv"],
    persons: ["person-emmanuel-macron", "person-edouard-philippe", "person-jean-luc-melenchon", "person-marine-le-pen"],
    consequences: "La majorité présidentielle obtient une majorité parlementaire, tandis que le PS et LR reculent fortement.",
    sources: ["source-francearchives-lrem", "source-vie-publique-macron-2017"],
    dataStatus: "partially_verified"
  },
  {
    id: "election-europeennes-2019",
    name: "Élections européennes de 2019",
    date: "2019-05-26",
    type: "autre",
    regime: "regime-cinquieme-republique",
    context: "Scrutin européen marqué par la compétition entre le RN, la liste présidentielle, EELV, le PS et LFI.",
    electoralSystem: "Scrutin de liste à la représentation proportionnelle.",
    resultsNote: "Les résultats chiffrés détaillés ne sont pas intégrés sans table électorale officielle.",
    parties: ["party-rn", "party-lrem", "party-eelv", "party-ps-1969", "party-lfi", "party-pcf", "party-dlf", "party-parti-animaliste"],
    persons: ["person-marine-le-pen", "person-emmanuel-macron", "person-yannick-jadot", "person-manon-aubry", "person-helene-thouy"],
    consequences: "Le RN arrive en tête et EELV confirme son retour électoral dans un paysage partisan recomposé.",
    sources: ["source-francearchives-rn", "source-francearchives-eelv", "source-francearchives-parti-animaliste"],
    dataStatus: "partially_verified"
  }
];

export const events: Event[] = [
  {
    id: "event-creation-prrrs",
    date: "1901-06-21",
    period: "period-1900-1914",
    regime: "regime-troisieme-republique",
    title: "Fondation du Parti radical et radical-socialiste",
    description: "Le congrès fondateur de Paris donne naissance au Parti républicain, radical et radical-socialiste.",
    category: "creation_de_parti",
    importance: 5,
    parties: ["party-prrrs"],
    persons: [],
    elections: [],
    sources: ["source-francearchives-parti-radical", "source-larousse-parti-radical"],
    dataStatus: "verified"
  },
  {
    id: "event-creation-ard",
    date: "1901",
    period: "period-1900-1914",
    regime: "regime-troisieme-republique",
    title: "Fondation de l'Alliance républicaine démocratique",
    description: "Des républicains modérés se structurent dans l'Alliance républicaine démocratique.",
    category: "creation_de_parti",
    importance: 4,
    parties: ["party-ard"],
    persons: [],
    elections: [],
    sources: ["source-sanson-alliance-democratique"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-alp",
    date: "1902",
    period: "period-1900-1914",
    regime: "regime-troisieme-republique",
    title: "Fondation de l'Action libérale populaire",
    description: "L'Action libérale populaire organise des catholiques ralliés à la République dans le contexte des politiques anticléricales.",
    category: "creation_de_parti",
    importance: 4,
    parties: ["party-alp"],
    persons: ["person-jacques-piou", "person-albert-de-mun"],
    elections: [],
    sources: ["source-openedition-alp"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-federation-republicaine",
    date: "1903",
    period: "period-1900-1914",
    regime: "regime-troisieme-republique",
    title: "Fondation de la Fédération républicaine",
    description: "La Fédération républicaine devient un grand parti de la droite républicaine sous la IIIe République.",
    category: "creation_de_parti",
    importance: 4,
    parties: ["party-federation-republicaine", "party-republicains-progressistes"],
    persons: ["person-eugene-motte"],
    elections: [],
    sources: ["source-francearchives-federation-republicaine"],
    dataStatus: "verified"
  },
  {
    id: "event-creation-sfio",
    date: "1905",
    period: "period-1900-1914",
    regime: "regime-troisieme-republique",
    title: "Création de la SFIO",
    description: "Unification de courants socialistes français dans une même organisation.",
    category: "creation_de_parti",
    importance: 4,
    parties: ["party-sfio"],
    persons: ["person-jean-jaures"],
    elections: [],
    sources: ["source-vie-publique-front-populaire", "source-francearchives-jaures"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-psf-1902",
    date: "1902",
    period: "period-1900-1914",
    regime: "regime-troisieme-republique",
    title: "Création du Parti socialiste français",
    description: "Formation transitoire associée aux socialistes réformistes autour de Jean Jaurès avant l'unification de 1905.",
    category: "creation_de_parti",
    importance: 3,
    parties: ["party-psf-1902"],
    persons: ["person-jean-jaures"],
    elections: [],
    sources: ["source-vie-publique-front-populaire", "source-francearchives-jaures"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-psdf",
    date: "1902-09-26",
    period: "period-1900-1914",
    regime: "regime-troisieme-republique",
    title: "Congrès de Commentry du Parti socialiste de France",
    description: "Le Parti socialiste de France se structure autour de courants guesdistes et blanquistes.",
    category: "congres",
    importance: 3,
    parties: ["party-psdf"],
    persons: ["person-jules-guesde"],
    elections: [],
    sources: ["source-gallica-psdf-commentry"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-fusion-pof-psdf",
    date: "1902",
    period: "period-1900-1914",
    regime: "regime-troisieme-republique",
    title: "Le POF fusionne dans le Parti socialiste de France",
    description: "Le Parti ouvrier français participe à la formation du Parti socialiste de France, étape antérieure à la SFIO.",
    category: "fusion",
    importance: 3,
    parties: ["party-pof", "party-psdf"],
    persons: ["person-jules-guesde", "person-paul-lafargue"],
    elections: [],
    sources: ["source-persee-mouvement-socialiste", "source-gallica-psdf-commentry"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-action-francaise",
    date: "1899",
    period: "period-1880-1899",
    regime: "regime-troisieme-republique",
    title: "Naissance de l'Action française",
    description: "Le mouvement apparaît dans le contexte de l'affaire Dreyfus et évolue vers un nationalisme monarchiste.",
    category: "creation_de_parti",
    importance: 4,
    parties: ["party-action-francaise"],
    persons: ["person-henri-vaugeois", "person-maurice-pujo", "person-charles-maurras"],
    elections: [],
    sources: ["source-larousse-action-francaise", "source-hal-action-francaise"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-ligue-des-patriotes",
    date: "1882",
    period: "period-1880-1899",
    regime: "regime-troisieme-republique",
    title: "Fondation de la Ligue des patriotes",
    description: "Paul Déroulède fonde une ligue nationaliste initialement tournée vers le thème de la revanche.",
    category: "creation_de_parti",
    importance: 3,
    parties: ["party-ligue-des-patriotes"],
    persons: ["person-paul-deroulede"],
    elections: [],
    sources: ["source-theses-deroulede"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-prs",
    date: "1911",
    period: "period-1900-1914",
    regime: "regime-troisieme-republique",
    title: "Création du Parti républicain-socialiste",
    description: "Des socialistes indépendants et républicains-socialistes se regroupent hors de la discipline de la SFIO.",
    category: "creation_de_parti",
    importance: 3,
    parties: ["party-prs"],
    persons: ["person-aristide-briand", "person-rene-viviani", "person-joseph-paul-boncour"],
    elections: [],
    sources: ["source-theses-prs"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-psf-1919",
    date: "1919",
    period: "period-1919-1932",
    regime: "regime-troisieme-republique",
    title: "Création du Parti socialiste français de 1919",
    description: "Des dissidents socialistes exclus ou séparés de la SFIO créent une nouvelle formation socialiste française après le choix de la SFIO de se retirer de l'Union sacrée.",
    category: "scission",
    importance: 3,
    parties: ["party-psf-1919", "party-sfio"],
    persons: [],
    elections: ["election-legislatives-1919"],
    sources: ["source-persee-prs"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-ard-devient-prd",
    date: "1911",
    period: "period-1900-1914",
    regime: "regime-troisieme-republique",
    title: "L'ARD devient Parti républicain démocratique",
    description: "L'Alliance républicaine démocratique adopte la dénomination de Parti républicain démocratique dans une phase de consolidation organisationnelle.",
    category: "changement_de_nom",
    importance: 3,
    parties: ["party-ard"],
    persons: [],
    elections: [],
    sources: ["source-sanson-alliance-democratique"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-premiere-guerre-mondiale",
    date: "1914-08",
    endDate: "1918-11",
    period: "period-1914-1918",
    regime: "regime-troisieme-republique",
    title: "Première Guerre mondiale et Union sacrée",
    description: "La guerre suspend en partie les oppositions partisanes et prépare une recomposition du paysage politique à la sortie du conflit.",
    category: "guerre",
    importance: 5,
    parties: ["party-sfio", "party-prrrs", "party-ard", "party-federation-republicaine", "party-alp"],
    persons: ["person-rene-viviani"],
    elections: [],
    sources: ["source-assemblee-bloc-national-1919"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-bloc-national",
    date: "1919-10-22",
    period: "period-1919-1932",
    regime: "regime-troisieme-republique",
    title: "Formation du Bloc national",
    description: "L'Alliance démocratique initie une alliance électorale de centre et de droite en vue des élections législatives de novembre 1919.",
    category: "alliance",
    importance: 5,
    parties: ["party-bloc-national", "party-ard", "party-federation-republicaine", "party-alp", "party-radicaux-independants"],
    persons: [],
    elections: ["election-legislatives-1919"],
    sources: ["source-assemblee-bloc-national-1919", "source-larousse-bloc-national"],
    dataStatus: "verified"
  },
  {
    id: "event-legislatives-1919",
    date: "1919-11-16",
    period: "period-1919-1932",
    regime: "regime-troisieme-republique",
    title: "Victoire du Bloc national aux législatives",
    description: "Les élections législatives de novembre 1919 donnent une majorité à la coalition du Bloc national.",
    category: "election",
    importance: 5,
    parties: ["party-bloc-national", "party-ard", "party-federation-republicaine", "party-alp", "party-radicaux-independants", "party-sfio"],
    persons: [],
    elections: ["election-legislatives-1919"],
    sources: ["source-assemblee-bloc-national-1919", "source-larousse-bloc-national"],
    dataStatus: "verified"
  },
  {
    id: "event-congres-tours",
    date: "1920-12-30",
    period: "period-1919-1932",
    regime: "regime-troisieme-republique",
    title: "Congrès de Tours",
    description: "Le congrès socialiste aboutit à une scission entre la majorité communiste et la minorité demeurant dans la SFIO.",
    category: "scission",
    importance: 5,
    parties: ["party-sfio", "party-pcf"],
    persons: ["person-leon-blum", "person-ludovic-oscar-frossard", "person-marcel-cachin"],
    elections: [],
    sources: ["source-maitron-tours"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-denomination-sfic",
    date: "1921",
    period: "period-1919-1932",
    regime: "regime-troisieme-republique",
    title: "La SFIC devient progressivement le Parti communiste français",
    description: "La Section française de l'Internationale communiste constitue la première dénomination du parti issu de la majorité du Congrès de Tours avant l'usage stabilisé de PCF.",
    category: "changement_de_nom",
    importance: 3,
    parties: ["party-pcf"],
    persons: ["person-ludovic-oscar-frossard", "person-marcel-cachin"],
    elections: [],
    sources: ["source-maitron-tours", "source-pcf-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-cartel-gauches",
    date: "1924",
    period: "period-1919-1932",
    regime: "regime-troisieme-republique",
    title: "Formation du Cartel des gauches",
    description: "Les radicaux, la SFIO et les républicains-socialistes s'accordent dans une coalition électorale contre les forces issues du Bloc national.",
    category: "alliance",
    importance: 5,
    parties: ["party-cartel-des-gauches", "party-prrrs", "party-sfio", "party-prs"],
    persons: ["person-edouard-herriot"],
    elections: ["election-legislatives-1924"],
    sources: ["source-jstor-cartel-socialistes", "source-france-memoire-herriot-cartel"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-legislatives-1924",
    date: "1924-05-11",
    period: "period-1919-1932",
    regime: "regime-troisieme-republique",
    title: "Victoire électorale du Cartel des gauches",
    description: "Les élections législatives de mai 1924 portent au pouvoir la coalition du Cartel des gauches.",
    category: "election",
    importance: 5,
    parties: ["party-cartel-des-gauches", "party-prrrs", "party-sfio", "party-prs"],
    persons: ["person-edouard-herriot"],
    elections: ["election-legislatives-1924"],
    sources: ["source-jstor-cartel-socialistes", "source-france-memoire-herriot-cartel"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-pdp",
    date: "1924",
    period: "period-1919-1932",
    regime: "regime-troisieme-republique",
    title: "Création du Parti démocrate populaire",
    description: "Le Parti démocrate populaire se structure comme parti démocrate-chrétien dans l'entre-deux-guerres.",
    category: "creation_de_parti",
    importance: 4,
    parties: ["party-pdp"],
    persons: ["person-marc-sangnier"],
    elections: [],
    sources: ["source-persee-pdp", "source-maitron-sangnier"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-faisceau",
    date: "1925-11-11",
    period: "period-1919-1932",
    regime: "regime-troisieme-republique",
    title: "Création du Faisceau",
    description: "Georges Valois fonde Le Faisceau, généralement présenté par l'historiographie comme le premier mouvement fasciste français.",
    category: "creation_de_parti",
    importance: 4,
    parties: ["party-faisceau", "party-action-francaise"],
    persons: ["person-georges-valois"],
    elections: [],
    sources: ["source-persee-faisceau"],
    dataStatus: "verified"
  },
  {
    id: "event-condamnation-action-francaise-1926",
    date: "1926-12-29",
    period: "period-1919-1932",
    regime: "regime-troisieme-republique",
    title: "Condamnation pontificale de l'Action française",
    description: "Pie XI confirme et étend la condamnation de l'Action française, ce qui provoque une crise majeure parmi les catholiques proches du mouvement.",
    category: "crise",
    importance: 4,
    parties: ["party-action-francaise"],
    persons: ["person-charles-maurras"],
    elections: [],
    sources: ["source-persee-action-francaise-1926"],
    dataStatus: "verified"
  },
  {
    id: "event-creation-croix-de-feu",
    date: "1927",
    period: "period-1919-1932",
    regime: "regime-troisieme-republique",
    title: "Création des Croix-de-Feu",
    description: "Les Croix-de-Feu apparaissent comme association d'anciens combattants avant leur transformation en mouvement politique de masse dans les années 1930.",
    category: "creation_de_parti",
    importance: 4,
    parties: ["party-croix-de-feu"],
    persons: ["person-francois-de-la-rocque"],
    elections: [],
    sources: ["source-1914-1918-croix-de-feu", "source-erudit-croix-de-feu-psf"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-pco",
    date: "1929",
    period: "period-1919-1932",
    regime: "regime-troisieme-republique",
    title: "Scission communiste oppositionnelle",
    description: "Des dissidences communistes critiques de la ligne du PCF donnent naissance à un petit courant oppositionnel.",
    category: "scission",
    importance: 2,
    parties: ["party-pco", "party-pcf"],
    persons: ["person-pierre-celor"],
    elections: [],
    sources: ["source-maitron-tours"],
    dataStatus: "unverified"
  },
  {
    id: "event-crise-1929",
    date: "1929",
    period: "period-1919-1932",
    regime: "regime-troisieme-republique",
    title: "Crise économique de 1929",
    description: "La crise économique internationale ouvre une séquence de tensions sociales et politiques qui pèse sur les recompositions des années 1930.",
    category: "evenement_economique",
    importance: 4,
    parties: ["party-pcf", "party-sfio", "party-prrrs", "party-federation-republicaine", "party-action-francaise", "party-faisceau"],
    persons: [],
    elections: [],
    sources: ["source-france-memoire-herriot-cartel"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-legislatives-1932",
    date: "1932-05",
    period: "period-1932-1939",
    regime: "regime-troisieme-republique",
    title: "Victoire du Cartel des gauches en 1932",
    description: "Les gauches remportent les élections législatives de 1932, mais la majorité reste fragile dans un contexte de crise économique.",
    category: "election",
    importance: 4,
    parties: ["party-cartel-des-gauches", "party-prrrs", "party-sfio", "party-prs"],
    persons: ["person-edouard-herriot"],
    elections: ["election-legislatives-1932"],
    sources: ["source-jstor-cartel-socialistes", "source-france-memoire-herriot-cartel"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-psdf-ujj",
    date: "1933",
    period: "period-1932-1939",
    regime: "regime-troisieme-republique",
    title: "Scission néo-socialiste et création du PSdF-UJJ",
    description: "Des néo-socialistes exclus ou dissidents de la SFIO créent le Parti socialiste de France - Union Jean Jaurès.",
    category: "scission",
    importance: 4,
    parties: ["party-sfio", "party-psdf-ujj"],
    persons: ["person-marcel-deat", "person-adrien-marquet", "person-paul-ramadier"],
    elections: [],
    sources: ["source-cambridge-neosocialisme"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-francisme",
    date: "1933-09-29",
    period: "period-1932-1939",
    regime: "regime-troisieme-republique",
    title: "Création du Mouvement franciste",
    description: "Marcel Bucard fonde un mouvement fasciste français dans le contexte des ligues et de la crise politique des années 1930.",
    category: "creation_de_parti",
    importance: 3,
    parties: ["party-francisme"],
    persons: ["person-marcel-bucard"],
    elections: [],
    sources: ["source-francearchives-bucard"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-six-fevrier-1934",
    date: "1934-02-06",
    period: "period-1932-1939",
    regime: "regime-troisieme-republique",
    title: "Crise du 6 février 1934",
    description: "Manifestations et violences antiparlementaires à Paris, impliquant plusieurs ligues et organisations de droite ou d'extrême droite.",
    category: "crise",
    importance: 5,
    parties: ["party-action-francaise", "party-croix-de-feu", "party-jeunesses-patriotes", "party-francisme", "party-federation-republicaine"],
    persons: ["person-edouard-daladier", "person-francois-de-la-rocque", "person-pierre-taittinger"],
    elections: [],
    sources: ["source-larousse-6-fevrier-1934", "source-1914-1918-croix-de-feu"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-cagoule",
    date: "1935",
    period: "period-1932-1939",
    regime: "regime-troisieme-republique",
    title: "Formation de la Cagoule",
    description: "L'organisation clandestine connue comme la Cagoule apparaît dans les années 1930 et prépare des actions violentes contre la République.",
    category: "crise",
    importance: 4,
    parties: ["party-cagoule", "party-action-francaise"],
    persons: ["person-eugene-deloncle", "person-jean-filliol"],
    elections: [],
    sources: ["source-cagoule-historytoday"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-dissolution-ligues-1936",
    date: "1936-06",
    period: "period-1932-1939",
    regime: "regime-troisieme-republique",
    title: "Dissolution de plusieurs ligues",
    description: "Le gouvernement du Front populaire dissout plusieurs ligues, dont les Croix-de-Feu et les Jeunesses patriotes.",
    category: "dissolution",
    importance: 4,
    parties: ["party-croix-de-feu", "party-jeunesses-patriotes", "party-francisme"],
    persons: ["person-francois-de-la-rocque", "person-pierre-taittinger", "person-marcel-bucard"],
    elections: ["election-legislatives-1936"],
    sources: ["source-erudit-croix-de-feu-psf", "source-archives-loire-atlantique-jp"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-psf-la-rocque",
    date: "1936",
    period: "period-1932-1939",
    regime: "regime-troisieme-republique",
    title: "Création du Parti social français",
    description: "François de La Rocque crée le Parti social français après la dissolution des Croix-de-Feu.",
    category: "creation_de_parti",
    importance: 4,
    parties: ["party-social-francais", "party-croix-de-feu"],
    persons: ["person-francois-de-la-rocque"],
    elections: [],
    sources: ["source-erudit-croix-de-feu-psf", "source-1914-1918-croix-de-feu"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-ppf",
    date: "1936-06-28",
    period: "period-1932-1939",
    regime: "regime-troisieme-republique",
    title: "Création du Parti populaire français",
    description: "Jacques Doriot fonde le PPF après son exclusion du PCF et son éloignement du communisme.",
    category: "creation_de_parti",
    importance: 4,
    parties: ["party-ppf", "party-pcf"],
    persons: ["person-jacques-doriot"],
    elections: [],
    sources: ["source-francearchives-ppf", "source-larousse-ppf"],
    dataStatus: "verified"
  },
  {
    id: "event-formation-droite-anti-front-populaire",
    date: "1936",
    period: "period-1932-1939",
    regime: "regime-troisieme-republique",
    title: "Oppositions de droite au Front populaire",
    description: "Des forces de droite parlementaire, de centre droit et des mouvements nationalistes s'opposent au Front populaire, sans former un parti unique.",
    category: "alliance",
    importance: 3,
    parties: ["party-droite-anti-front-populaire", "party-federation-republicaine", "party-ard", "party-social-francais", "party-ppf", "party-action-francaise"],
    persons: ["person-louis-marin", "person-pierre-etienne-flandin", "person-francois-de-la-rocque", "person-jacques-doriot"],
    elections: ["election-legislatives-1936"],
    sources: ["source-vie-publique-front-populaire", "source-erudit-croix-de-feu-psf", "source-larousse-ppf"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-jeunesses-patriotes",
    date: "1924",
    period: "period-1919-1932",
    regime: "regime-troisieme-republique",
    title: "Création des Jeunesses patriotes",
    description: "La Ligue des Jeunesses patriotes est créée en 1924 autour de Pierre Taittinger ; elle appartient donc à l'après-1920.",
    category: "creation_de_parti",
    importance: 3,
    parties: ["party-jeunesses-patriotes", "party-ligue-des-patriotes"],
    persons: ["person-pierre-taittinger"],
    elections: [],
    sources: ["source-theses-jeunesses-patriotes", "source-archives-loire-atlantique-jp"],
    dataStatus: "verified"
  },
  {
    id: "event-front-populaire-1936",
    date: "1936-05",
    period: "period-1932-1939",
    regime: "regime-troisieme-republique",
    title: "Victoire du Front populaire",
    description: "La coalition du Front populaire obtient une majorité parlementaire, dans un contexte de crise politique et sociale.",
    category: "election",
    importance: 5,
    parties: ["party-front-populaire", "party-sfio", "party-pcf", "party-prrrs"],
    persons: ["person-leon-blum", "person-edouard-daladier", "person-maurice-thorez"],
    elections: ["election-legislatives-1936"],
    sources: ["source-vie-publique-front-populaire", "source-assemblee-1936"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-gouvernement-blum",
    date: "1936-06",
    period: "period-1932-1939",
    regime: "regime-troisieme-republique",
    title: "Gouvernement Blum",
    description: "Léon Blum devient président du Conseil dans la séquence du Front populaire ; le PCF soutient la majorité sans entrer au gouvernement.",
    category: "gouvernement",
    importance: 5,
    parties: ["party-front-populaire", "party-sfio", "party-prrrs", "party-pcf"],
    persons: ["person-leon-blum", "person-vincent-auriol", "person-edouard-daladier", "person-maurice-thorez"],
    elections: ["election-legislatives-1936"],
    sources: ["source-vie-publique-front-populaire"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-psop",
    date: "1938-06-08",
    period: "period-1932-1939",
    regime: "regime-troisieme-republique",
    title: "Création du PSOP",
    description: "Marceau Pivert et des militants de la Gauche révolutionnaire créent le Parti socialiste ouvrier et paysan après une rupture avec la SFIO.",
    category: "scission",
    importance: 4,
    parties: ["party-psop", "party-sfio"],
    persons: ["person-marceau-pivert"],
    elections: [],
    sources: ["source-francearchives-psop", "source-bataille-socialiste-psop"],
    dataStatus: "verified"
  },
  {
    id: "event-interdiction-pcf-1939",
    date: "1939-09",
    period: "period-1939-1945",
    regime: "regime-troisieme-republique",
    title: "Interdiction du PCF",
    description: "Après le pacte germano-soviétique, le gouvernement interdit le Parti communiste français et ses organisations liées.",
    category: "dissolution",
    importance: 5,
    parties: ["party-pcf"],
    persons: ["person-maurice-thorez"],
    elections: [],
    sources: ["source-pcf-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-seconde-guerre-mondiale",
    date: "1939-09-03",
    endDate: "1945-05-08",
    period: "period-1939-1945",
    regime: "regime-vichy",
    title: "Seconde Guerre mondiale, Occupation et crise de l'État",
    description: "La guerre, la défaite de 1940, l'Occupation et le régime de Vichy constituent une crise majeure qui bouleverse les partis, les institutions, la Résistance et les recompositions de la Libération.",
    category: "guerre",
    importance: 5,
    parties: ["party-pcf", "party-sfio", "party-prrrs", "party-ard", "party-federation-republicaine", "party-pdp", "party-mrp"],
    persons: ["person-charles-de-gaulle", "person-philippe-petain", "person-paul-reynaud"],
    elections: [],
    sources: ["source-vie-publique-vichy-resistance", "source-assemblee-pleins-pouvoirs-1940"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-ligue-francaise",
    date: "1940",
    period: "period-1939-1945",
    regime: "regime-vichy",
    title: "Création de la Ligue française",
    description: "Pierre Costantini fonde la Ligue française collaborationniste sous l'Occupation ; cette organisation ne doit pas être datée des années 1930.",
    category: "creation_de_parti",
    importance: 2,
    parties: ["party-ligue-francaise"],
    persons: ["person-pierre-costantini"],
    elections: [],
    sources: ["source-cagoule-historytoday"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-pleins-pouvoirs-1940",
    date: "1940-07-10",
    period: "period-1939-1945",
    regime: "regime-vichy",
    title: "Vote des pleins pouvoirs à Philippe Pétain",
    description: "L'Assemblée nationale réunie à Vichy vote les pleins pouvoirs constituants au gouvernement placé sous l'autorité de Philippe Pétain, ouvrant la fin de fait de la IIIe République.",
    category: "constitution",
    importance: 5,
    parties: ["party-prrrs", "party-sfio", "party-ard", "party-federation-republicaine", "party-pdp", "party-social-francais"],
    persons: ["person-philippe-petain", "person-paul-reynaud"],
    elections: [],
    sources: ["source-assemblee-pleins-pouvoirs-1940"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-mrp",
    date: "1944",
    period: "period-1939-1945",
    regime: "regime-liberation",
    title: "Création du MRP",
    description: "Le Mouvement républicain populaire naît dans le contexte de la Libération et de la recomposition démocrate-chrétienne.",
    category: "creation_de_parti",
    importance: 4,
    parties: ["party-mrp"],
    persons: ["person-georges-bidault", "person-robert-schuman"],
    elections: [],
    sources: ["source-francearchives-mrp"],
    dataStatus: "verified"
  },
  {
    id: "event-creation-udsr",
    date: "1945-06-25",
    period: "period-1945-1958",
    regime: "regime-quatrieme-republique",
    title: "Création de l'UDSR",
    description: "L'Union démocratique et socialiste de la Résistance se constitue comme formation issue de réseaux résistants non communistes.",
    category: "creation_de_parti",
    importance: 3,
    parties: ["party-udsr"],
    persons: ["person-rene-pleven", "person-francois-mitterrand"],
    elections: [],
    sources: ["source-francearchives-udsr", "source-sciencespo-udsr"],
    dataStatus: "verified"
  },
  {
    id: "event-creation-rpf",
    date: "1947-04-14",
    period: "period-1945-1958",
    regime: "regime-quatrieme-republique",
    title: "Création du RPF",
    description: "Charles de Gaulle lance le Rassemblement du peuple français pour défendre une réforme profonde des institutions.",
    category: "creation_de_parti",
    importance: 5,
    parties: ["party-rpf"],
    persons: ["person-charles-de-gaulle", "person-jacques-soustelle", "person-andre-malraux"],
    elections: [],
    sources: ["source-fondation-de-gaulle-rpf"],
    dataStatus: "verified"
  },
  {
    id: "event-exclusion-pcf-gouvernement-1947",
    date: "1947-05",
    period: "period-1945-1958",
    regime: "regime-quatrieme-republique",
    title: "Exclusion des ministres communistes",
    description: "Dans le contexte de la guerre froide, les ministres communistes sont écartés du gouvernement, ouvrant une période d'isolement gouvernemental du PCF.",
    category: "gouvernement",
    importance: 5,
    parties: ["party-pcf", "party-sfio", "party-mrp"],
    persons: ["person-maurice-thorez"],
    elections: [],
    sources: ["source-pcf-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-cnip",
    date: "1949",
    period: "period-1945-1958",
    regime: "regime-quatrieme-republique",
    title: "Création du CNI puis structuration du CNIP",
    description: "Roger Duchet fonde le Centre national des indépendants, qui fédère des droites modérées et intègre ensuite des composantes paysannes et libérales.",
    category: "creation_de_parti",
    importance: 4,
    parties: ["party-cnip", "party-prl"],
    persons: ["person-roger-duchet", "person-antoine-pinay"],
    elections: [],
    sources: ["source-francearchives-cnip", "source-theses-cnip"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-jeune-nation",
    date: "1949",
    period: "period-1945-1958",
    regime: "regime-quatrieme-republique",
    title: "Formation de Jeune Nation",
    description: "Jeune Nation apparaît comme un groupuscule nationaliste d'après-guerre, actif notamment pendant les tensions liées à l'Algérie française.",
    category: "creation_de_parti",
    importance: 2,
    parties: ["party-jeune-nation"],
    persons: ["person-pierre-sidos"],
    elections: [],
    sources: ["source-jeune-nation-sidos"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-ceca-1951",
    date: "1951-04-18",
    period: "period-1945-1958",
    regime: "regime-quatrieme-republique",
    title: "Signature du traité instituant la CECA",
    description: "La Communauté européenne du charbon et de l'acier devient un marqueur important des clivages européens de la IVe République.",
    category: "alliance",
    importance: 4,
    parties: ["party-mrp", "party-sfio", "party-prrrs", "party-pcf"],
    persons: ["person-robert-schuman"],
    elections: [],
    sources: ["source-francearchives-mrp"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-legislatives-1951",
    date: "1951-06-17",
    period: "period-1945-1958",
    regime: "regime-quatrieme-republique",
    title: "Élections législatives de 1951",
    description: "Les élections de 1951 confirment la fragmentation parlementaire et l'importance du PCF, du RPF, du MRP, de la SFIO et des indépendants.",
    category: "election",
    importance: 4,
    parties: ["party-pcf", "party-rpf", "party-mrp", "party-sfio", "party-cnip", "party-prrrs", "party-udsr"],
    persons: ["person-charles-de-gaulle", "person-maurice-thorez", "person-guy-mollet"],
    elections: ["election-legislatives-1951"],
    sources: ["source-francearchives-mrp", "source-fondation-de-gaulle-rpf", "source-francearchives-cnip"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-dien-bien-phu-1954",
    date: "1954-05",
    period: "period-1945-1958",
    regime: "regime-quatrieme-republique",
    title: "Défaite de Diên Biên Phu",
    description: "La défaite française accélère la fin de la guerre d'Indochine et pèse sur la recomposition politique autour de Pierre Mendès France.",
    category: "guerre",
    importance: 5,
    parties: ["party-prrrs", "party-mrp", "party-sfio", "party-pcf"],
    persons: ["person-pierre-mendes-france"],
    elections: [],
    sources: ["source-archives-nationales-1958"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-debut-guerre-algerie-1954",
    date: "1954-11-01",
    period: "period-1945-1958",
    regime: "regime-quatrieme-republique",
    title: "Début de la guerre d'Algérie",
    description: "Les attentats du 1er novembre 1954 ouvrent une crise coloniale majeure qui déstabilise la IVe République.",
    category: "guerre",
    importance: 5,
    parties: ["party-sfio", "party-mrp", "party-prrrs", "party-pcf", "party-cnip"],
    persons: ["person-pierre-mendes-france", "person-guy-mollet"],
    elections: [],
    sources: ["source-archives-nationales-1958"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-mise-en-sommeil-rpf",
    date: "1955",
    period: "period-1945-1958",
    regime: "regime-quatrieme-republique",
    title: "Mise en sommeil du RPF",
    description: "Après le retrait politique de Charles de Gaulle, le RPF décline et laisse place à des formations gaullistes intermédiaires.",
    category: "dissolution",
    importance: 4,
    parties: ["party-rpf", "party-republicains-sociaux"],
    persons: ["person-charles-de-gaulle", "person-jacques-chaban-delmas"],
    elections: [],
    sources: ["source-fondation-de-gaulle-rpf", "source-francearchives-unr"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-front-republicain-1956",
    date: "1956-01",
    period: "period-1945-1958",
    regime: "regime-quatrieme-republique",
    title: "Front républicain de 1956",
    description: "La SFIO, des radicaux, l'UDSR et des mendésistes se regroupent dans une coalition électorale ; le PCF demeure extérieur à cette coalition.",
    category: "alliance",
    importance: 4,
    parties: ["party-front-republicain-1956", "party-sfio", "party-prrrs", "party-udsr", "party-pcf"],
    persons: ["person-guy-mollet", "person-pierre-mendes-france", "person-francois-mitterrand"],
    elections: ["election-legislatives-1956"],
    sources: ["source-francearchives-udsr", "source-francearchives-psa"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-traite-rome-1957",
    date: "1957-03-25",
    period: "period-1945-1958",
    regime: "regime-quatrieme-republique",
    title: "Signature du traité de Rome",
    description: "La création de la CEE et d'Euratom accentue les clivages sur la construction européenne.",
    category: "alliance",
    importance: 4,
    parties: ["party-mrp", "party-sfio", "party-prrrs", "party-pcf"],
    persons: ["person-robert-schuman"],
    elections: [],
    sources: ["source-francearchives-mrp"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-crise-mai-1958",
    date: "1958-05-13",
    period: "period-1945-1958",
    regime: "regime-quatrieme-republique",
    title: "Crise de mai 1958",
    description: "La crise d'Alger précipite le retour de Charles de Gaulle au pouvoir et la transition institutionnelle vers la Ve République.",
    category: "crise",
    importance: 5,
    parties: ["party-unr", "party-rpf", "party-republicains-sociaux", "party-mrp", "party-sfio", "party-prrrs", "party-cnip"],
    persons: ["person-charles-de-gaulle", "person-pierre-pflimlin", "person-jacques-soustelle"],
    elections: [],
    sources: ["source-archives-nationales-1958", "source-vie-publique-1958"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-psa",
    date: "1958-09-11",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Création du Parti socialiste autonome",
    description: "Des minoritaires de la SFIO fondent le PSA après le congrès d'Issy-les-Moulineaux.",
    category: "scission",
    importance: 4,
    parties: ["party-psa", "party-sfio"],
    persons: ["person-edouard-depreux", "person-alain-savary"],
    elections: ["election-legislatives-1958"],
    sources: ["source-francearchives-psa", "source-maitron-depreux"],
    dataStatus: "verified"
  },
  {
    id: "event-creation-unr",
    date: "1958-10-01",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Création de l'UNR",
    description: "L'Union pour la nouvelle République rassemble les principales forces gaullistes autour du soutien aux nouvelles institutions.",
    category: "creation_de_parti",
    importance: 5,
    parties: ["party-unr", "party-republicains-sociaux"],
    persons: ["person-charles-de-gaulle", "person-jacques-chaban-delmas", "person-georges-pompidou"],
    elections: ["election-legislatives-1958"],
    sources: ["source-francearchives-unr", "source-vie-publique-1958"],
    dataStatus: "verified"
  },
  {
    id: "event-legislatives-1958",
    date: "1958-11-23",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Élections législatives de 1958",
    description: "Les premières législatives de la Ve République installent une majorité favorable au gaullisme.",
    category: "election",
    importance: 5,
    parties: ["party-unr", "party-cnip", "party-mrp", "party-sfio", "party-pcf", "party-prrrs"],
    persons: ["person-charles-de-gaulle", "person-maurice-thorez", "person-guy-mollet"],
    elections: ["election-legislatives-1958"],
    sources: ["source-francearchives-unr", "source-archives-nationales-1958"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-election-de-gaulle-1958",
    date: "1958-12-21",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Élection de Charles de Gaulle à la présidence",
    description: "Charles de Gaulle est élu président de la République par un collège électoral, selon les institutions initiales de la Ve République.",
    category: "election",
    importance: 5,
    parties: ["party-unr"],
    persons: ["person-charles-de-gaulle"],
    elections: [],
    sources: ["source-archives-nationales-1958"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-autodetermination-algerie-1959",
    date: "1959-09-16",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Discours sur l'autodétermination en Algérie",
    description: "Charles de Gaulle ouvre la perspective de l'autodétermination, ce qui reconfigure les droites, le gaullisme et les mouvements Algérie française.",
    category: "discours",
    importance: 5,
    parties: ["party-unr", "party-cnip", "party-jeune-nation", "party-oas"],
    persons: ["person-charles-de-gaulle", "person-raoul-salan"],
    elections: [],
    sources: ["source-archives-nationales-1958", "source-oas-herodote"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-psu",
    date: "1960-04-03",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Création du PSU",
    description: "Le Parti socialiste unifié naît de la fusion du PSA, de l'UGS et d'autres groupes de gauche.",
    category: "fusion",
    importance: 4,
    parties: ["party-psu", "party-psa"],
    persons: ["person-edouard-depreux", "person-pierre-mendes-france", "person-alain-savary"],
    elections: [],
    sources: ["source-francearchives-psu", "source-maitron-depreux"],
    dataStatus: "verified"
  },
  {
    id: "event-semaine-barricades-alger-1960",
    date: "1960-01",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Semaine des barricades à Alger",
    description: "La crise d'Alger confirme la conflictualité entre le pouvoir gaulliste et les partisans de l'Algérie française.",
    category: "crise",
    importance: 4,
    parties: ["party-unr", "party-oas", "party-jeune-nation"],
    persons: ["person-charles-de-gaulle"],
    elections: [],
    sources: ["source-archives-nationales-1958", "source-oas-herodote"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-oas",
    date: "1961-02-11",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Création de l'OAS",
    description: "L'Organisation armée secrète est créée en 1961 contre l'indépendance de l'Algérie ; elle est donc postérieure à la décennie 1950-1960.",
    category: "crise",
    importance: 4,
    parties: ["party-oas"],
    persons: ["person-jean-jacques-susini", "person-pierre-lagaillarde", "person-raoul-salan"],
    elections: [],
    sources: ["source-oas-herodote"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-parti-nationaliste",
    date: "1959",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Création du Parti nationaliste",
    description: "Un petit Parti nationaliste lié aux milieux de Jeune Nation apparaît autour de Pierre Sidos ; la donnée reste à vérifier.",
    category: "creation_de_parti",
    importance: 2,
    parties: ["party-parti-nationaliste", "party-jeune-nation"],
    persons: ["person-pierre-sidos"],
    elections: [],
    sources: ["source-jeune-nation-sidos"],
    dataStatus: "unverified"
  },
  {
    id: "event-creation-pno",
    date: "1959",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Création du Partit de la Nacion Occitana",
    description: "François Fontan fonde un mouvement nationaliste occitan, à distinguer du Partit Occitan fondé en 1987.",
    category: "creation_de_parti",
    importance: 2,
    parties: ["party-pno"],
    persons: ["person-francois-fontan"],
    elections: [],
    sources: ["source-pno", "source-parti-occitan"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-putsch-generaux-1961",
    date: "1961-04-21",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Putsch des généraux à Alger",
    description: "Le putsch d'Alger échoue et renforce l'autorité présidentielle dans la crise algérienne.",
    category: "crise",
    importance: 5,
    parties: ["party-unr", "party-oas"],
    persons: ["person-charles-de-gaulle", "person-raoul-salan"],
    elections: [],
    sources: ["source-oas-herodote", "source-archives-nationales-1958"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-accords-evian",
    date: "1962-03-18",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Accords d'Évian",
    description: "Les accords d'Évian ouvrent la fin de la guerre d'Algérie et préparent l'indépendance algérienne.",
    category: "guerre",
    importance: 5,
    parties: ["party-unr", "party-oas", "party-pcf", "party-psu", "party-sfio"],
    persons: ["person-charles-de-gaulle"],
    elections: [],
    sources: ["source-archives-nationales-1958", "source-oas-herodote"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-referendum-1962",
    date: "1962-10-28",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Référendum sur le suffrage universel présidentiel",
    description: "La réforme approuvée par référendum prévoit l'élection du président de la République au suffrage universel direct.",
    category: "referendum",
    importance: 5,
    parties: ["party-unr", "party-pcf", "party-sfio", "party-mrp", "party-cnip", "party-psu"],
    persons: ["person-charles-de-gaulle", "person-francois-mitterrand"],
    elections: ["election-referendum-1962"],
    sources: ["source-vie-publique-referendum-1962", "source-conseil-constitutionnel-1962"],
    dataStatus: "verified"
  },
  {
    id: "event-legislatives-1962",
    date: "1962-11",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Élections législatives de 1962",
    description: "Le scrutin consolide la majorité gaulliste après la crise institutionnelle de 1962.",
    category: "election",
    importance: 4,
    parties: ["party-unr", "party-fnri", "party-cnip", "party-pcf", "party-sfio", "party-psu"],
    persons: ["person-georges-pompidou", "person-maurice-thorez", "person-guy-mollet"],
    elections: ["election-legislatives-1962"],
    sources: ["source-francearchives-unr", "source-persee-republicains-independants"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-cir",
    date: "1964",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Création de la CIR",
    description: "François Mitterrand fonde la Convention des institutions républicaines dans l'opposition aux institutions gaulliennes.",
    category: "creation_de_parti",
    importance: 3,
    parties: ["party-cir"],
    persons: ["person-francois-mitterrand"],
    elections: ["election-presidentielle-1965"],
    sources: ["source-archives-socialistes-1969"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-presidentielle-1965",
    date: "1965-12-05",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Première présidentielle au suffrage universel direct",
    description: "L'élection oppose notamment Charles de Gaulle, François Mitterrand et Jean Lecanuet, et structure durablement les oppositions.",
    category: "election",
    importance: 5,
    parties: ["party-unr", "party-cir", "party-fgds", "party-centre-democrate", "party-sfio", "party-pcf"],
    persons: ["person-charles-de-gaulle", "person-francois-mitterrand", "person-jean-lecanuet"],
    elections: ["election-presidentielle-1965"],
    sources: ["source-francearchives-centre-democrate", "source-vie-publique-referendum-1962"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-fgds",
    date: "1965",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Création de la FGDS",
    description: "La Fédération de la gauche démocrate et socialiste regroupe la gauche non communiste autour de François Mitterrand.",
    category: "alliance",
    importance: 4,
    parties: ["party-fgds", "party-cir", "party-sfio", "party-prrrs"],
    persons: ["person-francois-mitterrand", "person-guy-mollet", "person-gaston-defferre"],
    elections: ["election-presidentielle-1965", "election-legislatives-1967"],
    sources: ["source-archives-socialistes-1969"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-centre-democrate",
    date: "1966",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Création du Centre démocrate",
    description: "Jean Lecanuet fonde le Centre démocrate dans la suite de sa campagne présidentielle de 1965.",
    category: "creation_de_parti",
    importance: 4,
    parties: ["party-centre-democrate", "party-mrp"],
    persons: ["person-jean-lecanuet", "person-alain-poher"],
    elections: ["election-presidentielle-1965", "election-legislatives-1967"],
    sources: ["source-francearchives-centre-democrate", "source-persee-centre-democrate"],
    dataStatus: "verified"
  },
  {
    id: "event-creation-fnri",
    date: "1966-06-01",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Structuration des Républicains indépendants",
    description: "Le courant giscardien se constitue en Fédération nationale des républicains indépendants.",
    category: "creation_de_parti",
    importance: 4,
    parties: ["party-fnri", "party-cnip"],
    persons: ["person-valery-giscard-destaing"],
    elections: ["election-legislatives-1967"],
    sources: ["source-persee-republicains-independants"],
    dataStatus: "verified"
  },
  {
    id: "event-creation-udr",
    date: "1967-11-24",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "L'UNR devient l'UDR",
    description: "La formation gaulliste prend la forme de l'Union des démocrates pour la République.",
    category: "changement_de_nom",
    importance: 5,
    parties: ["party-unr", "party-udr"],
    persons: ["person-georges-pompidou", "person-jacques-chaban-delmas", "person-roger-frey"],
    elections: ["election-legislatives-1968"],
    sources: ["source-francearchives-unr"],
    dataStatus: "verified"
  },
  {
    id: "event-mai-68",
    date: "1968-05",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Mai 68",
    description: "Mouvement étudiant, grèves générales et crise politique majeure ; le PCF et la CGT jouent un rôle important dans le mouvement ouvrier sans diriger l'ensemble de la contestation.",
    category: "manifestation",
    importance: 5,
    parties: ["party-mouvement-22-mars", "party-pcf", "party-psu", "party-sfio", "party-gauche-proletarienne"],
    persons: ["person-daniel-cohn-bendit", "person-alain-geismar", "person-georges-pompidou", "person-charles-de-gaulle"],
    elections: ["election-legislatives-1968"],
    sources: ["source-persee-mouvement-22-mars", "source-mahj-cohn-bendit", "source-paris8-gauche-proletarienne"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-gauche-proletarienne",
    date: "1968",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Création de la Gauche prolétarienne",
    description: "Des militants maoïstes créent la Gauche prolétarienne dans le sillage de Mai 68.",
    category: "creation_de_parti",
    importance: 3,
    parties: ["party-gauche-proletarienne"],
    persons: ["person-benny-levy", "person-alain-geismar"],
    elections: [],
    sources: ["source-paris8-gauche-proletarienne", "source-gis-gauche-proletarienne"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-legislatives-1968",
    date: "1968-06",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Élections législatives de 1968",
    description: "Après Mai 68, les élections législatives donnent une large majorité à la droite gaulliste.",
    category: "election",
    importance: 5,
    parties: ["party-udr", "party-fnri", "party-centre-democrate", "party-pcf", "party-fgds", "party-sfio", "party-psu"],
    persons: ["person-charles-de-gaulle", "person-georges-pompidou", "person-francois-mitterrand"],
    elections: ["election-legislatives-1968"],
    sources: ["source-francearchives-unr", "source-archives-socialistes-1969"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-referendum-1969",
    date: "1969-04-27",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Échec du référendum de 1969",
    description: "L'échec du référendum sur la régionalisation et le Sénat entraîne la démission du général de Gaulle.",
    category: "referendum",
    importance: 5,
    parties: ["party-udr", "party-centre-democrate", "party-pcf", "party-sfio", "party-psu"],
    persons: ["person-charles-de-gaulle", "person-alain-poher"],
    elections: ["election-referendum-1969"],
    sources: ["source-senat-referendum-1969"],
    dataStatus: "verified"
  },
  {
    id: "event-presidentielle-1969",
    date: "1969-06",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Élection présidentielle de 1969",
    description: "Georges Pompidou est élu président de la République face à Alain Poher ; la gauche non communiste apparaît très affaiblie.",
    category: "election",
    importance: 5,
    parties: ["party-udr", "party-centre-democrate", "party-pcf", "party-sfio", "party-psu", "party-ligue-communiste"],
    persons: ["person-georges-pompidou", "person-alain-poher", "person-jacques-duclos", "person-gaston-defferre", "person-michel-rocard", "person-alain-krivine"],
    elections: ["election-presidentielle-1969"],
    sources: ["source-senat-referendum-1969", "source-maitron-krivine"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-ps-1969",
    date: "1969-07-13",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Naissance du Parti socialiste",
    description: "Le congrès d'Issy-les-Moulineaux transforme la SFIO en nouveau Parti socialiste avec l'Union des clubs pour le renouveau de la gauche.",
    category: "changement_de_nom",
    importance: 5,
    parties: ["party-sfio", "party-ps-1969"],
    persons: ["person-guy-mollet", "person-gaston-defferre"],
    elections: [],
    sources: ["source-archives-socialistes-1969"],
    dataStatus: "verified"
  },
  {
    id: "event-creation-ligue-communiste",
    date: "1969",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Création de la Ligue communiste",
    description: "La Ligue communiste se forme dans les recompositions trotskistes postérieures à Mai 68.",
    category: "creation_de_parti",
    importance: 3,
    parties: ["party-ligue-communiste", "party-pci-trotskiste"],
    persons: ["person-alain-krivine"],
    elections: ["election-presidentielle-1969"],
    sources: ["source-maitron-krivine"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-centre-democratie-progres",
    date: "1969",
    period: "period-1969-1981",
    regime: "regime-cinquieme-republique",
    title: "Création du Centre démocratie et progrès",
    description: "Une partie du Centre démocrate se rapproche de la majorité présidentielle après l'élection de Georges Pompidou.",
    category: "scission",
    importance: 3,
    parties: ["party-centre-democratie-progres", "party-centre-democrate"],
    persons: [],
    elections: ["election-presidentielle-1969"],
    sources: ["source-francearchives-centre-democrate", "source-persee-centre-democrate"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-amis-terre",
    date: "1970",
    period: "period-1969-1981",
    regime: "regime-cinquieme-republique",
    title: "Préfiguration associative de l'écologie politique",
    description: "Les Amis de la Terre marquent l'émergence associative d'une écologie politique qui ne dispose pas encore de parti structuré national.",
    category: "evenement_social",
    importance: 2,
    parties: ["party-amis-terre"],
    persons: ["person-rene-dumont"],
    elections: [],
    sources: ["source-parti-occitan"],
    dataStatus: "unverified"
  },
  {
    id: "event-mort-de-gaulle-1970",
    date: "1970-11-09",
    period: "period-1969-1981",
    regime: "regime-cinquieme-republique",
    title: "Mort de Charles de Gaulle",
    description: "La mort de Charles de Gaulle symbolise la fin de la séquence fondatrice du gaullisme, alors que Georges Pompidou préside déjà la République.",
    category: "deces",
    importance: 4,
    parties: ["party-udr", "party-rpr"],
    persons: ["person-charles-de-gaulle", "person-georges-pompidou"],
    elections: [],
    sources: ["source-francearchives-unr", "source-francearchives-rpr"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-manifeste-343-1971",
    date: "1971-04-05",
    period: "period-1969-1981",
    regime: "regime-cinquieme-republique",
    title: "Manifeste des 343",
    description: "Le manifeste contribue à politiser la question de l'avortement et prépare les mobilisations féministes du début des années 1970.",
    category: "evenement_social",
    importance: 4,
    parties: ["party-mlac"],
    persons: ["person-simone-de-beauvoir", "person-gisele-halimi"],
    elections: [],
    sources: ["source-mlac-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-congres-epinay-1971",
    date: "1971-06",
    period: "period-1969-1981",
    regime: "regime-cinquieme-republique",
    title: "Congrès d'Épinay du Parti socialiste",
    description: "Le congrès d'Épinay intègre notamment la CIR et porte François Mitterrand à la tête du Parti socialiste.",
    category: "congres",
    importance: 5,
    parties: ["party-ps-1969", "party-cir"],
    persons: ["person-francois-mitterrand", "person-guy-mollet", "person-gaston-defferre"],
    elections: [],
    sources: ["source-archives-socialistes-1969"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-prg-1972",
    date: "1972",
    period: "period-1969-1981",
    regime: "regime-cinquieme-republique",
    title: "Scission des radicaux de gauche",
    description: "Une composante du radicalisme choisit l'union de la gauche et se structure en radicaux de gauche.",
    category: "scission",
    importance: 4,
    parties: ["party-prg", "party-prrrs", "party-parti-radical-valoisien"],
    persons: ["person-robert-fabre", "person-jean-jacques-servan-schreiber"],
    elections: ["election-legislatives-1973"],
    sources: ["source-prg-histoire", "source-francearchives-parti-radical"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-programme-commun-1972",
    date: "1972-06-27",
    period: "period-1969-1981",
    regime: "regime-cinquieme-republique",
    title: "Signature du Programme commun de la gauche",
    description: "Le PS, le PCF et les radicaux de gauche concluent un accord programmatique qui structure l'union de la gauche pendant la décennie.",
    category: "programme",
    importance: 5,
    parties: ["party-programme-commun-gauche", "party-ps-1969", "party-pcf", "party-prg"],
    persons: ["person-francois-mitterrand", "person-georges-marchais", "person-robert-fabre"],
    elections: ["election-legislatives-1973", "election-presidentielle-1974", "election-legislatives-1978"],
    sources: ["source-archives-socialistes-1969", "source-pcf-histoire", "source-prg-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-fn-1972",
    date: "1972-10-05",
    period: "period-1969-1981",
    regime: "regime-cinquieme-republique",
    title: "Création du Front national",
    description: "Jean-Marie Le Pen prend la tête du Front national, nouvelle organisation d'extrême droite dans le paysage partisan français.",
    category: "creation_de_parti",
    importance: 4,
    parties: ["party-fn"],
    persons: ["person-jean-marie-le-pen"],
    elections: ["election-legislatives-1973", "election-presidentielle-1974"],
    sources: ["source-fondation-jean-jaures-le-pen"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-choc-petrolier-1973",
    date: "1973",
    period: "period-1969-1981",
    regime: "regime-cinquieme-republique",
    title: "Premier choc pétrolier",
    description: "La crise économique ouverte par le choc pétrolier transforme les débats sur la croissance, l'inflation, l'emploi et la politique industrielle.",
    category: "evenement_economique",
    importance: 5,
    parties: ["party-udr", "party-fnri", "party-pcf", "party-ps-1969", "party-amis-terre"],
    persons: ["person-georges-pompidou", "person-valery-giscard-destaing"],
    elections: [],
    sources: ["source-francearchives-udf", "source-francearchives-rpr"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-mlac-1973",
    date: "1973",
    period: "period-1969-1981",
    regime: "regime-cinquieme-republique",
    title: "Création du MLAC",
    description: "Le Mouvement pour la liberté de l'avortement et de la contraception organise des mobilisations décisives pour les droits reproductifs.",
    category: "evenement_social",
    importance: 4,
    parties: ["party-mlac"],
    persons: ["person-gisele-halimi", "person-simone-de-beauvoir"],
    elections: [],
    sources: ["source-mlac-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-dissolution-gauche-proletarienne-1973",
    date: "1973",
    period: "period-1969-1981",
    regime: "regime-cinquieme-republique",
    title: "Fin de la Gauche prolétarienne",
    description: "La Gauche prolétarienne se dissout dans le reflux des organisations maoïstes issues de l'après-Mai 68.",
    category: "dissolution",
    importance: 3,
    parties: ["party-gauche-proletarienne"],
    persons: ["person-benny-levy", "person-alain-geismar", "person-olivier-rolin"],
    elections: [],
    sources: ["source-paris8-gauche-proletarienne", "source-gis-gauche-proletarienne"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-pfn-1974",
    date: "1974",
    period: "period-1969-1981",
    regime: "regime-cinquieme-republique",
    title: "Création du Parti des forces nouvelles",
    description: "Le PFN apparaît comme une concurrence d'extrême droite au Front national naissant.",
    category: "creation_de_parti",
    importance: 2,
    parties: ["party-pfn", "party-fn"],
    persons: ["person-pascal-gauchon", "person-jean-marie-le-pen"],
    elections: [],
    sources: ["source-fondation-jean-jaures-le-pen"],
    dataStatus: "unverified"
  },
  {
    id: "event-creation-lcr-1974",
    date: "1974",
    period: "period-1969-1981",
    regime: "regime-cinquieme-republique",
    title: "Création de la LCR",
    description: "La Ligue communiste révolutionnaire succède aux recompositions trotskistes postérieures à la dissolution de la Ligue communiste.",
    category: "creation_de_parti",
    importance: 3,
    parties: ["party-lcr", "party-ligue-communiste"],
    persons: ["person-alain-krivine"],
    elections: ["election-presidentielle-1974"],
    sources: ["source-maitron-krivine"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-presidentielle-1974",
    date: "1974-05",
    period: "period-1969-1981",
    regime: "regime-cinquieme-republique",
    title: "Élection présidentielle de 1974",
    description: "Valéry Giscard d'Estaing remporte l'élection présidentielle face à François Mitterrand, dans une recomposition entre gaullistes et libéraux.",
    category: "election",
    importance: 5,
    parties: ["party-fnri", "party-udr", "party-ps-1969", "party-programme-commun-gauche", "party-lutte-ouvriere", "party-lcr", "party-fn"],
    persons: ["person-valery-giscard-destaing", "person-francois-mitterrand", "person-jacques-chaban-delmas", "person-arlette-laguiller", "person-alain-krivine", "person-jean-marie-le-pen"],
    elections: ["election-presidentielle-1974"],
    sources: ["source-francearchives-udf", "source-ina-lutte-ouvriere-1974", "source-fondation-jean-jaures-le-pen"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-loi-veil-1975",
    date: "1975-01-17",
    period: "period-1969-1981",
    regime: "regime-cinquieme-republique",
    title: "Promulgation de la loi Veil",
    description: "La loi portée par Simone Veil dépénalise l'interruption volontaire de grossesse dans un contexte de fortes mobilisations sociales et parlementaires.",
    category: "reforme",
    importance: 5,
    parties: ["party-mlac", "party-udf"],
    persons: ["person-simone-veil", "person-gisele-halimi", "person-simone-de-beauvoir"],
    elections: [],
    sources: ["source-mlac-histoire", "source-francearchives-udf"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-cds-1976",
    date: "1976",
    period: "period-1969-1981",
    regime: "regime-cinquieme-republique",
    title: "Création du CDS",
    description: "Le Centre des démocrates sociaux réunit le Centre démocrate et le Centre démocratie et progrès.",
    category: "fusion",
    importance: 4,
    parties: ["party-cds", "party-centre-democrate", "party-centre-democratie-progres"],
    persons: ["person-jean-lecanuet", "person-jacques-duhamel", "person-pierre-mehaignerie"],
    elections: ["election-legislatives-1978"],
    sources: ["source-francearchives-cds"],
    dataStatus: "verified"
  },
  {
    id: "event-creation-rpr-1976",
    date: "1976-12-05",
    period: "period-1969-1981",
    regime: "regime-cinquieme-republique",
    title: "Création du RPR",
    description: "Jacques Chirac transforme l'UDR en Rassemblement pour la République et affirme un gaullisme concurrent du giscardisme.",
    category: "changement_de_nom",
    importance: 5,
    parties: ["party-udr", "party-rpr"],
    persons: ["person-jacques-chirac", "person-valery-giscard-destaing"],
    elections: ["election-legislatives-1978"],
    sources: ["source-francearchives-rpr"],
    dataStatus: "verified"
  },
  {
    id: "event-creation-pr-1977",
    date: "1977-05-20",
    period: "period-1969-1981",
    regime: "regime-cinquieme-republique",
    title: "Les Républicains indépendants deviennent Parti républicain",
    description: "La FNRI se transforme en Parti républicain, composante libérale de la future UDF.",
    category: "changement_de_nom",
    importance: 4,
    parties: ["party-fnri", "party-parti-republicain-1977"],
    persons: ["person-valery-giscard-destaing", "person-jean-pierre-soisson", "person-michel-poniatowski"],
    elections: ["election-legislatives-1978"],
    sources: ["source-perspective-ri", "source-francearchives-udf"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-rupture-programme-commun-1977",
    date: "1977-09",
    period: "period-1969-1981",
    regime: "regime-cinquieme-republique",
    title: "Rupture de l'actualisation du Programme commun",
    description: "Les négociations d'actualisation du Programme commun échouent entre socialistes, communistes et radicaux de gauche.",
    category: "rupture",
    importance: 5,
    parties: ["party-programme-commun-gauche", "party-ps-1969", "party-pcf", "party-prg"],
    persons: ["person-francois-mitterrand", "person-georges-marchais", "person-robert-fabre"],
    elections: ["election-legislatives-1978"],
    sources: ["source-archives-socialistes-1969", "source-pcf-histoire", "source-prg-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-udf-1978",
    date: "1978-02-01",
    period: "period-1969-1981",
    regime: "regime-cinquieme-republique",
    title: "Création de l'UDF",
    description: "Valéry Giscard d'Estaing lance l'Union pour la démocratie française pour rassembler la majorité centriste, libérale et radicale non gaulliste.",
    category: "alliance",
    importance: 5,
    parties: ["party-udf", "party-parti-republicain-1977", "party-cds", "party-parti-radical-valoisien"],
    persons: ["person-valery-giscard-destaing", "person-jean-lecanuet", "person-jean-pierre-fourcade"],
    elections: ["election-legislatives-1978", "election-europeennes-1979"],
    sources: ["source-francearchives-udf"],
    dataStatus: "verified"
  },
  {
    id: "event-legislatives-1978",
    date: "1978-03",
    period: "period-1969-1981",
    regime: "regime-cinquieme-republique",
    title: "Élections législatives de 1978",
    description: "La majorité RPR-UDF conserve l'Assemblée face à une gauche divisée après la rupture du Programme commun.",
    category: "election",
    importance: 5,
    parties: ["party-rpr", "party-udf", "party-pcf", "party-ps-1969", "party-prg", "party-fn"],
    persons: ["person-jacques-chirac", "person-raymond-barre", "person-francois-mitterrand", "person-georges-marchais"],
    elections: ["election-legislatives-1978"],
    sources: ["source-francearchives-rpr", "source-francearchives-udf", "source-pcf-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-europeennes-1979",
    date: "1979-06",
    period: "period-1969-1981",
    regime: "regime-cinquieme-republique",
    title: "Premières élections européennes au suffrage universel direct",
    description: "Les élections européennes deviennent un nouveau champ de confrontation entre UDF, RPR, gauche et listes écologistes.",
    category: "election",
    importance: 4,
    parties: ["party-udf", "party-rpr", "party-pcf", "party-ps-1969", "party-europe-ecologie-1979"],
    persons: ["person-valery-giscard-destaing", "person-jacques-chirac", "person-francois-mitterrand", "person-rene-dumont"],
    elections: ["election-europeennes-1979"],
    sources: ["source-francearchives-udf", "source-francearchives-rpr", "source-europe-ecologie-1979"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-europe-ecologie-1979",
    date: "1979",
    period: "period-1969-1981",
    regime: "regime-cinquieme-republique",
    title: "Liste Europe Écologie",
    description: "Europe Écologie marque une étape dans l'inscription électorale de l'écologie politique avant la création d'un parti vert structuré.",
    category: "alliance",
    importance: 3,
    parties: ["party-europe-ecologie-1979", "party-amis-terre"],
    persons: ["person-rene-dumont"],
    elections: ["election-europeennes-1979"],
    sources: ["source-europe-ecologie-1979"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-presidentielle-1981",
    date: "1981-05-10",
    period: "period-1981-1995",
    regime: "regime-cinquieme-republique",
    title: "Élection de François Mitterrand",
    description: "François Mitterrand remporte l'élection présidentielle et devient le premier président de gauche de la Ve République.",
    category: "election",
    importance: 5,
    parties: ["party-ps-1969", "party-pcf", "party-udf", "party-rpr"],
    persons: ["person-francois-mitterrand", "person-valery-giscard-destaing", "person-jacques-chirac"],
    elections: ["election-presidentielle-1981"],
    sources: ["source-vie-publique-mitterrand-1981"],
    dataStatus: "verified"
  },
  {
    id: "event-legislatives-1981",
    date: "1981-06-21",
    period: "period-1981-1995",
    regime: "regime-cinquieme-republique",
    title: "Victoire de la gauche aux législatives de 1981",
    description: "La dissolution de l'Assemblée nationale permet au Parti socialiste et à ses alliés d'obtenir une majorité parlementaire.",
    category: "election",
    importance: 5,
    parties: ["party-ps-1969", "party-pcf", "party-prg"],
    persons: ["person-francois-mitterrand", "person-pierre-mauroy", "person-georges-marchais"],
    elections: ["election-legislatives-1981"],
    sources: ["source-vie-publique-mitterrand-1981", "source-assemblee-legislatives-1988"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-abolition-peine-mort-1981",
    date: "1981-10-09",
    period: "period-1981-1995",
    regime: "regime-cinquieme-republique",
    title: "Abolition de la peine de mort",
    description: "La loi portée par Robert Badinter abolit la peine de mort en France au début de la présidence Mitterrand.",
    category: "reforme",
    importance: 5,
    parties: ["party-ps-1969"],
    persons: ["person-francois-mitterrand"],
    elections: [],
    sources: ["source-vie-publique-mitterrand-1981"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-decentralisation-1982",
    date: "1982-03-02",
    period: "period-1981-1995",
    regime: "regime-cinquieme-republique",
    title: "Lois Defferre de décentralisation",
    description: "Les lois de 1982 et 1983 transfèrent des compétences de l'État vers les collectivités territoriales et renforcent leur autonomie.",
    category: "reforme",
    importance: 5,
    parties: ["party-ps-1969"],
    persons: ["person-francois-mitterrand", "person-gaston-defferre"],
    elections: [],
    sources: ["source-vie-publique-decentralisation"],
    dataStatus: "verified"
  },
  {
    id: "event-retrait-pcf-gouvernement-1984",
    date: "1984-07",
    period: "period-1981-1995",
    regime: "regime-cinquieme-republique",
    title: "Retrait des ministres communistes du gouvernement",
    description: "Le PCF quitte le gouvernement après le tournant de la rigueur et la réorientation économique de la majorité socialiste.",
    category: "rupture",
    importance: 4,
    parties: ["party-pcf", "party-ps-1969"],
    persons: ["person-georges-marchais", "person-francois-mitterrand"],
    elections: [],
    sources: ["source-pcf-histoire", "source-vie-publique-mitterrand-1981"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-verts-1984",
    date: "1984",
    period: "period-1981-1995",
    regime: "regime-cinquieme-republique",
    title: "Structuration des Verts",
    description: "Les mouvements écologistes se structurent dans le parti Les Verts, qui devient l'organisation nationale de référence de l'écologie politique.",
    category: "creation_de_parti",
    importance: 4,
    parties: ["party-verts", "party-europe-ecologie-1979"],
    persons: ["person-brice-lalonde", "person-antoine-waechter", "person-noel-mamere"],
    elections: ["election-europeennes-1989"],
    sources: ["source-francearchives-verts"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-premiere-cohabitation-1986",
    date: "1986-03-20",
    period: "period-1981-1995",
    regime: "regime-cinquieme-republique",
    title: "Première cohabitation",
    description: "Après les législatives de 1986, François Mitterrand nomme Jacques Chirac Premier ministre dans une configuration de cohabitation.",
    category: "gouvernement",
    importance: 5,
    parties: ["party-ps-1969", "party-rpr", "party-udf", "party-fn"],
    persons: ["person-francois-mitterrand", "person-jacques-chirac", "person-raymond-barre", "person-jean-marie-le-pen"],
    elections: ["election-legislatives-1986"],
    sources: ["source-vie-publique-cohabitation-1986"],
    dataStatus: "verified"
  },
  {
    id: "event-presidentielle-1988",
    date: "1988-05-08",
    period: "period-1981-1995",
    regime: "regime-cinquieme-republique",
    title: "Réélection de François Mitterrand",
    description: "François Mitterrand remporte un second mandat présidentiel face à Jacques Chirac et met fin à la première cohabitation.",
    category: "election",
    importance: 5,
    parties: ["party-ps-1969", "party-rpr"],
    persons: ["person-francois-mitterrand", "person-jacques-chirac"],
    elections: ["election-presidentielle-1988"],
    sources: ["source-vie-publique-mitterrand-1981", "source-vie-publique-cohabitation-1986"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-legislatives-1988",
    date: "1988-06-12",
    period: "period-1981-1995",
    regime: "regime-cinquieme-republique",
    title: "Législatives de 1988 et gouvernement Rocard",
    description: "Les élections législatives reconstituent une majorité présidentielle relative ; Michel Rocard devient Premier ministre.",
    category: "gouvernement",
    importance: 4,
    parties: ["party-ps-1969", "party-pcf", "party-rpr", "party-udf", "party-prg"],
    persons: ["person-francois-mitterrand", "person-michel-rocard", "person-jacques-chirac"],
    elections: ["election-legislatives-1988"],
    sources: ["source-assemblee-legislatives-1988", "source-vie-publique-mitterrand-1981"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-chute-mur-berlin-1989",
    date: "1989-11-09",
    period: "period-1981-1995",
    regime: "regime-cinquieme-republique",
    title: "Chute du mur de Berlin",
    description: "La chute du mur de Berlin accélère la fin de la Guerre froide et ouvre une crise idéologique durable pour le PCF.",
    category: "crise",
    importance: 5,
    parties: ["party-pcf", "party-ps-1969", "party-rpr", "party-udf"],
    persons: ["person-georges-marchais", "person-francois-mitterrand"],
    elections: [],
    sources: ["source-pcf-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-europeennes-1989",
    date: "1989-06-18",
    period: "period-1981-1995",
    regime: "regime-cinquieme-republique",
    title: "Percée électorale des écologistes aux européennes",
    description: "Les élections européennes de 1989 donnent une visibilité nationale accrue aux Verts et confirment la progression du Front national.",
    category: "election",
    importance: 4,
    parties: ["party-verts", "party-fn", "party-ps-1969", "party-udf", "party-pcf"],
    persons: ["person-antoine-waechter", "person-jean-marie-le-pen", "person-francois-mitterrand"],
    elections: ["election-europeennes-1989"],
    sources: ["source-francearchives-verts", "source-fondation-jean-jaures-fn-1984"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-traite-maastricht-1992",
    date: "1992-02-07",
    period: "period-1981-1995",
    regime: "regime-cinquieme-republique",
    title: "Signature du traité de Maastricht",
    description: "Le traité de Maastricht réorganise la construction européenne et prépare l'Union européenne ainsi que la monnaie unique.",
    category: "reforme",
    importance: 5,
    parties: ["party-ps-1969", "party-udf", "party-rpr", "party-pcf", "party-mdc", "party-fn"],
    persons: ["person-francois-mitterrand", "person-jean-pierre-chevenement"],
    elections: ["election-referendum-maastricht-1992"],
    sources: ["source-vie-publique-maastricht"],
    dataStatus: "verified"
  },
  {
    id: "event-deuxieme-cohabitation-1993",
    date: "1993-03-29",
    period: "period-1981-1995",
    regime: "regime-cinquieme-republique",
    title: "Deuxième cohabitation",
    description: "La victoire de la droite aux législatives conduit François Mitterrand à nommer Édouard Balladur Premier ministre.",
    category: "gouvernement",
    importance: 5,
    parties: ["party-rpr", "party-udf", "party-ps-1969"],
    persons: ["person-francois-mitterrand", "person-edouard-balladur", "person-jacques-chirac"],
    elections: ["election-legislatives-1993"],
    sources: ["source-francearchives-1990s-elections"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-presidentielle-1995",
    date: "1995-05-07",
    period: "period-1981-1995",
    regime: "regime-cinquieme-republique",
    title: "Élection de Jacques Chirac",
    description: "Jacques Chirac remporte l'élection présidentielle et ouvre une nouvelle séquence de gouvernement de droite.",
    category: "election",
    importance: 5,
    parties: ["party-rpr", "party-ps-1969", "party-udf", "party-fn"],
    persons: ["person-jacques-chirac", "person-lionel-jospin", "person-edouard-balladur", "person-jean-marie-le-pen"],
    elections: ["election-presidentielle-1995"],
    sources: ["source-vie-publique-chirac-1995"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-greves-1995",
    date: "1995-11",
    period: "period-1981-1995",
    regime: "regime-cinquieme-republique",
    title: "Grèves de l'automne 1995",
    description: "Un mouvement social massif s'oppose au plan de réforme de la Sécurité sociale et aux projets du gouvernement Juppé.",
    category: "manifestation",
    importance: 4,
    parties: ["party-rpr", "party-ps-1969", "party-pcf", "party-lcr"],
    persons: ["person-alain-juppe", "person-jacques-chirac"],
    elections: [],
    sources: ["source-vie-publique-chirac-1995"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-gauche-plurielle-1997",
    date: "1997-06-02",
    period: "period-1995-2002",
    regime: "regime-cinquieme-republique",
    title: "Victoire de la gauche plurielle",
    description: "La gauche plurielle remporte les législatives anticipées et Lionel Jospin devient Premier ministre dans une troisième cohabitation.",
    category: "gouvernement",
    importance: 5,
    parties: ["party-gauche-plurielle", "party-ps-1969", "party-pcf", "party-verts", "party-mdc", "party-prg", "party-rpr"],
    persons: ["person-lionel-jospin", "person-jacques-chirac", "person-robert-hue", "person-dominique-voynet", "person-jean-pierre-chevenement"],
    elections: ["election-legislatives-1997"],
    sources: ["source-vie-publique-gauche-plurielle"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-scission-fn-mnr-1999",
    date: "1999",
    period: "period-1995-2002",
    regime: "regime-cinquieme-republique",
    title: "Scission du Front national et création du MNR",
    description: "La crise entre Jean-Marie Le Pen et Bruno Mégret conduit à la création du Mouvement national républicain.",
    category: "scission",
    importance: 4,
    parties: ["party-fn", "party-mnr"],
    persons: ["person-jean-marie-le-pen", "person-bruno-megret"],
    elections: ["election-europeennes-1999"],
    sources: ["source-francearchives-mnr"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-euro-1999",
    date: "1999-01-01",
    period: "period-1995-2002",
    regime: "regime-cinquieme-republique",
    title: "Mise en place de l'euro",
    description: "L'euro devient la monnaie scripturale des États participants, nouvelle étape de l'intégration européenne avant la mise en circulation des billets et pièces en 2002.",
    category: "reforme",
    importance: 4,
    parties: ["party-ps-1969", "party-udf", "party-rpr", "party-pcf", "party-fn", "party-mdc"],
    persons: ["person-francois-mitterrand", "person-jacques-chirac"],
    elections: [],
    sources: ["source-vie-publique-maastricht"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-choc-21-avril-2002",
    date: "2002-04-21",
    period: "period-2002-2012",
    regime: "regime-cinquieme-republique",
    title: "Jean-Marie Le Pen au second tour de 2002",
    description: "Jean-Marie Le Pen devance Lionel Jospin au premier tour de l'élection présidentielle, provoquant un choc politique et une mobilisation républicaine au second tour.",
    category: "crise",
    importance: 5,
    parties: ["party-fn", "party-ps-1969", "party-rpr", "party-ump"],
    persons: ["person-jean-marie-le-pen", "person-lionel-jospin", "person-jacques-chirac"],
    elections: ["election-presidentielle-2002"],
    sources: ["source-vie-publique-presidentielle-2002"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-ump-2002",
    date: "2002-11-17",
    period: "period-2002-2012",
    regime: "regime-cinquieme-republique",
    title: "Création de l'UMP",
    description: "Le RPR, Démocratie libérale et des composantes de la majorité présidentielle se regroupent dans l'Union pour un mouvement populaire.",
    category: "fusion",
    importance: 5,
    parties: ["party-ump", "party-rpr", "party-democratie-liberale"],
    persons: ["person-alain-juppe", "person-jacques-chirac", "person-nicolas-sarkozy"],
    elections: ["election-legislatives-2002"],
    sources: ["source-vie-publique-ump"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-reforme-retraites-2003",
    date: "2003-08-21",
    period: "period-2002-2012",
    regime: "regime-cinquieme-republique",
    title: "Réforme des retraites de 2003",
    description: "La réforme Fillon modifie les durées de cotisation et suscite de fortes mobilisations sociales.",
    category: "reforme",
    importance: 4,
    parties: ["party-ump", "party-ps-1969", "party-pcf", "party-lcr"],
    persons: ["person-francois-fillon", "person-jean-pierre-raffarin"],
    elections: [],
    sources: ["source-vie-publique-ump"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-loi-signes-religieux-2004",
    date: "2004-03-15",
    period: "period-2002-2012",
    regime: "regime-cinquieme-republique",
    title: "Loi sur les signes religieux à l'école",
    description: "La loi encadre le port de signes religieux ostensibles dans les établissements scolaires publics.",
    category: "reforme",
    importance: 4,
    parties: ["party-ump", "party-ps-1969", "party-udf"],
    persons: ["person-jacques-chirac", "person-nicolas-sarkozy"],
    elections: [],
    sources: ["source-vie-publique-ump"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-referendum-tce-2005",
    date: "2005-05-29",
    period: "period-2002-2012",
    regime: "regime-cinquieme-republique",
    title: "Rejet du traité constitutionnel européen",
    description: "Les électeurs français rejettent le traité constitutionnel européen, révélant des divisions transpartisanes sur l'intégration européenne.",
    category: "referendum",
    importance: 5,
    parties: ["party-ump", "party-ps-1969", "party-pcf", "party-verts", "party-mrc", "party-fn"],
    persons: ["person-jacques-chirac", "person-jean-pierre-chevenement", "person-francois-bayrou"],
    elections: ["election-referendum-tce-2005"],
    sources: ["source-vie-publique-maastricht"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-cpe-2006",
    date: "2006-03-31",
    period: "period-2002-2012",
    regime: "regime-cinquieme-republique",
    title: "Retrait du contrat première embauche",
    description: "Après plusieurs semaines de mobilisations étudiantes et syndicales, le gouvernement renonce au CPE.",
    category: "evenement_social",
    importance: 4,
    parties: ["party-ump", "party-ps-1969", "party-pcf", "party-lcr"],
    persons: ["person-dominique-villepin", "person-nicolas-sarkozy"],
    elections: [],
    sources: ["source-vie-publique-ump"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-presidentielle-2007",
    date: "2007-05-06",
    period: "period-2002-2012",
    regime: "regime-cinquieme-republique",
    title: "Élection de Nicolas Sarkozy",
    description: "Nicolas Sarkozy remporte l'élection présidentielle face à Ségolène Royal et ouvre une nouvelle présidence UMP.",
    category: "election",
    importance: 5,
    parties: ["party-ump", "party-ps-1969", "party-modem"],
    persons: ["person-nicolas-sarkozy", "person-segolene-royal", "person-francois-bayrou"],
    elections: ["election-presidentielle-2007"],
    sources: ["source-vie-publique-presidentielle-2007"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-grenelle-environnement-2007",
    date: "2007-10",
    period: "period-2002-2012",
    regime: "regime-cinquieme-republique",
    title: "Grenelle de l'environnement",
    description: "Le Grenelle de l'environnement réunit pouvoirs publics, collectivités, entreprises et associations autour de mesures environnementales et climatiques.",
    category: "reforme",
    importance: 4,
    parties: ["party-ump", "party-verts", "party-europe-ecologie-2009", "party-cap21"],
    persons: ["person-nicolas-sarkozy", "person-cecile-duflot"],
    elections: [],
    sources: ["source-vie-publique-ump"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-parti-gauche-2008",
    date: "2008-11-29",
    period: "period-2002-2012",
    regime: "regime-cinquieme-republique",
    title: "Création du Parti de gauche",
    description: "Jean-Luc Mélenchon et des militants issus du Parti socialiste fondent le Parti de gauche, qui prépare une alliance avec le PCF.",
    category: "creation_de_parti",
    importance: 4,
    parties: ["party-parti-gauche", "party-ps-1969"],
    persons: ["person-jean-luc-melenchon"],
    elections: ["election-europeennes-2009"],
    sources: ["source-francearchives-front-gauche"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-front-gauche-2009",
    date: "2009-03",
    period: "period-2002-2012",
    regime: "regime-cinquieme-republique",
    title: "Constitution du Front de gauche",
    description: "Le Parti de gauche et le PCF forment le Front de gauche pour les élections européennes, rejoints par d'autres composantes de la gauche radicale.",
    category: "alliance",
    importance: 4,
    parties: ["party-front-gauche", "party-parti-gauche", "party-pcf"],
    persons: ["person-jean-luc-melenchon", "person-marie-george-buffet"],
    elections: ["election-europeennes-2009"],
    sources: ["source-francearchives-front-gauche"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-npa-2009",
    date: "2009-02-05",
    period: "period-2002-2012",
    regime: "regime-cinquieme-republique",
    title: "Création du Nouveau Parti anticapitaliste",
    description: "La LCR se transforme dans un nouveau parti ouvert à des comités militants anticapitalistes, le NPA.",
    category: "creation_de_parti",
    importance: 4,
    parties: ["party-npa", "party-lcr"],
    persons: ["person-olivier-besancenot", "person-philippe-poutou"],
    elections: ["election-europeennes-2009"],
    sources: ["source-francearchives-npa"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-eelv-2010",
    date: "2010-11-13",
    period: "period-2002-2012",
    regime: "regime-cinquieme-republique",
    title: "Création d'Europe Écologie Les Verts",
    description: "Les Verts et la dynamique Europe Écologie se regroupent dans Europe Écologie Les Verts.",
    category: "fusion",
    importance: 4,
    parties: ["party-eelv", "party-verts", "party-europe-ecologie-2009"],
    persons: ["person-cecile-duflot", "person-daniel-cohn-bendit"],
    elections: ["election-regionales-2010"],
    sources: ["source-francearchives-eelv"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-presidentielle-2012",
    date: "2012-05-06",
    period: "period-2012-2017",
    regime: "regime-cinquieme-republique",
    title: "Élection de François Hollande",
    description: "François Hollande remporte l'élection présidentielle face à Nicolas Sarkozy et ouvre une alternance à gauche.",
    category: "election",
    importance: 5,
    parties: ["party-ps-1969", "party-ump", "party-front-gauche", "party-fn", "party-eelv"],
    persons: ["person-francois-hollande", "person-nicolas-sarkozy", "person-jean-luc-melenchon", "person-marine-le-pen"],
    elections: ["election-presidentielle-2012"],
    sources: ["source-vie-publique-hollande-2012"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-mariage-tous-2013",
    date: "2013-05-17",
    period: "period-2012-2017",
    regime: "regime-cinquieme-republique",
    title: "Loi ouvrant le mariage aux couples de même sexe",
    description: "La loi Taubira ouvre le mariage et l'adoption aux couples de même sexe après un débat parlementaire et social intense.",
    category: "reforme",
    importance: 5,
    parties: ["party-ps-1969", "party-eelv", "party-ump", "party-fn"],
    persons: ["person-francois-hollande"],
    elections: [],
    sources: ["source-vie-publique-mariage-tous"],
    dataStatus: "verified"
  },
  {
    id: "event-attentats-2015",
    date: "2015-01-07",
    endDate: "2015-11-13",
    period: "period-2012-2017",
    regime: "regime-cinquieme-republique",
    title: "Attentats de 2015",
    description: "Les attentats de janvier et de novembre 2015 provoquent une mobilisation nationale et un renforcement durable des politiques antiterroristes.",
    category: "crise",
    importance: 5,
    parties: ["party-ps-1969", "party-ump", "party-fn", "party-eelv"],
    persons: ["person-francois-hollande", "person-marine-le-pen"],
    elections: [],
    sources: ["source-vie-publique-hollande-2012"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-loi-travail-2016",
    date: "2016-08-08",
    period: "period-2012-2017",
    regime: "regime-cinquieme-republique",
    title: "Promulgation de la loi Travail",
    description: "La loi El Khomri réforme le droit du travail et suscite des mobilisations sociales, notamment Nuit debout.",
    category: "reforme",
    importance: 4,
    parties: ["party-ps-1969", "party-lfi", "party-pcf", "party-eelv"],
    persons: ["person-francois-hollande", "person-benoit-hamon"],
    elections: [],
    sources: ["source-vie-publique-hollande-2012"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-creation-lrem-2016",
    date: "2016-04-06",
    period: "period-2012-2017",
    regime: "regime-cinquieme-republique",
    title: "Création d'En marche !",
    description: "Emmanuel Macron lance le mouvement En marche !, qui deviendra La République en marche et prépare la recomposition électorale de 2017.",
    category: "creation_de_parti",
    importance: 5,
    parties: ["party-lrem", "party-ps-1969"],
    persons: ["person-emmanuel-macron"],
    elections: ["election-presidentielle-2017"],
    sources: ["source-francearchives-lrem"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-presidentielle-2017",
    date: "2017-05-07",
    period: "period-2017-2022",
    regime: "regime-cinquieme-republique",
    title: "Élection d'Emmanuel Macron",
    description: "Emmanuel Macron remporte l'élection présidentielle face à Marine Le Pen et installe un nouveau pôle central.",
    category: "election",
    importance: 5,
    parties: ["party-lrem", "party-rn", "party-lr", "party-lfi", "party-ps-1969"],
    persons: ["person-emmanuel-macron", "person-marine-le-pen", "person-francois-fillon", "person-jean-luc-melenchon", "person-benoit-hamon"],
    elections: ["election-presidentielle-2017"],
    sources: ["source-vie-publique-macron-2017"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-gilets-jaunes-2018",
    date: "2018-11-17",
    endDate: "2019-12-31",
    period: "period-2017-2022",
    regime: "regime-cinquieme-republique",
    title: "Mouvement des Gilets jaunes",
    description: "Le mouvement des Gilets jaunes conteste la hausse de la fiscalité sur les carburants et s'élargit à une critique sociale et démocratique du pouvoir.",
    category: "evenement_social",
    importance: 5,
    parties: ["party-lrem", "party-rn", "party-lfi", "party-lr", "party-dlf"],
    persons: ["person-emmanuel-macron", "person-edouard-philippe", "person-marine-le-pen", "person-jean-luc-melenchon"],
    elections: [],
    sources: ["source-vie-publique-gilets-jaunes"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-europeennes-2019",
    date: "2019-05-26",
    period: "period-2017-2022",
    regime: "regime-cinquieme-republique",
    title: "Européennes de 2019",
    description: "Le RN arrive en tête et EELV confirme sa progression électorale dans un scrutin marqué par la recomposition des partis.",
    category: "election",
    importance: 4,
    parties: ["party-rn", "party-lrem", "party-eelv", "party-lfi", "party-ps-1969", "party-parti-animaliste"],
    persons: ["person-marine-le-pen", "person-emmanuel-macron", "person-yannick-jadot", "person-manon-aubry", "person-helene-thouy"],
    elections: ["election-europeennes-2019"],
    sources: ["source-francearchives-rn", "source-francearchives-eelv", "source-francearchives-parti-animaliste"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-pandemie-covid-2020",
    date: "2020-03-17",
    period: "period-2017-2022",
    regime: "regime-cinquieme-republique",
    title: "Début du confinement face à la pandémie de Covid-19",
    description: "La pandémie entraîne un confinement national, une gestion de crise sanitaire et un plan de soutien économique massif.",
    category: "crise",
    importance: 5,
    parties: ["party-lrem", "party-lr", "party-ps-1969", "party-lfi", "party-rn", "party-eelv"],
    persons: ["person-emmanuel-macron", "person-edouard-philippe"],
    elections: [],
    sources: ["source-vie-publique-covid-2020"],
    dataStatus: "partially_verified"
  },
  {
    id: "event-constitution-1958",
    date: "1958-10-04",
    period: "period-1958-1969",
    regime: "regime-cinquieme-republique",
    title: "Promulgation de la Constitution de 1958",
    description: "La Constitution du 4 octobre 1958 fonde la Ve République.",
    category: "constitution",
    importance: 5,
    parties: ["party-unr"],
    persons: ["person-charles-de-gaulle"],
    elections: ["election-referendum-1958"],
    sources: ["source-vie-publique-1958", "source-conseil-constitutionnel-1958"],
    dataStatus: "verified"
  }
];

export const relations: Relation[] = [
  {
    id: "relation-lr-renamed-ump",
    source: "party-ump",
    sourceKind: "party",
    relation: "RENAMED_TO",
    target: "party-lr",
    targetKind: "party",
    date: "2015",
    description: "Les Républicains succèdent à l'UMP sous une nouvelle appellation.",
    sources: ["source-francearchives-lr"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-rn-renamed-fn",
    source: "party-fn",
    sourceKind: "party",
    relation: "RENAMED_TO",
    target: "party-rn",
    targetKind: "party",
    date: "2018",
    description: "Le Front national adopte le nom Rassemblement national en 2018.",
    sources: ["source-francearchives-rn"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-lfi-founded-from-pg",
    source: "party-lfi",
    sourceKind: "party",
    relation: "FOUNDED_FROM",
    target: "party-parti-gauche",
    targetKind: "party",
    date: "2016",
    description: "LFI reprend une partie des réseaux et de l'héritage militant du Parti de gauche sans constituer une simple fusion juridique.",
    sources: ["source-francearchives-lfi"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-lrem-founded-by-macron",
    source: "person-emmanuel-macron",
    sourceKind: "person",
    relation: "FOUNDED_BY",
    target: "party-lrem",
    targetKind: "party",
    date: "2016",
    description: "Emmanuel Macron fonde le mouvement En marche !, qui devient LREM après 2017.",
    sources: ["source-francearchives-lrem"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-europe-ecologie-2009-merged-eelv",
    source: "party-europe-ecologie-2009",
    sourceKind: "party",
    relation: "MERGED_INTO",
    target: "party-eelv",
    targetKind: "party",
    date: "2010",
    description: "La dynamique Europe Écologie est intégrée à la création d'EELV avec les Verts.",
    sources: ["source-francearchives-eelv"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-nupes-allied-lfi",
    source: "party-nupes",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-lfi",
    targetKind: "party",
    date: "2022",
    description: "La NUPES est une coalition postérieure à la période, structurée notamment autour de LFI.",
    sources: ["source-francearchives-nupes"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-ump-successor-rpr",
    source: "party-ump",
    sourceKind: "party",
    relation: "SUCCESSOR_OF",
    target: "party-rpr",
    targetKind: "party",
    date: "2002",
    description: "L'UMP rassemble et prolonge le principal parti gaulliste, le RPR, dans la majorité présidentielle.",
    sources: ["source-vie-publique-ump"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-ump-absorbed-dl",
    source: "party-democratie-liberale",
    sourceKind: "party",
    relation: "ABSORBED_BY",
    target: "party-ump",
    targetKind: "party",
    date: "2002",
    description: "Démocratie libérale participe à la recomposition de la droite autour de l'UMP.",
    sources: ["source-vie-publique-ump"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-modem-split-udf",
    source: "party-modem",
    sourceKind: "party",
    relation: "SPLIT_FROM",
    target: "party-udf",
    targetKind: "party",
    date: "2007",
    description: "Le MoDem est fondé par François Bayrou après la rupture avec l'orientation de l'UDF vers la majorité UMP.",
    sources: ["source-francearchives-modem"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-mrc-successor-mdc",
    source: "party-mrc",
    sourceKind: "party",
    relation: "SUCCESSOR_OF",
    target: "party-mdc",
    targetKind: "party",
    date: "2003",
    description: "Le MRC succède au Mouvement des citoyens dans la continuité du courant chevènementiste.",
    sources: ["source-francearchives-mrc"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-front-gauche-allied-pg",
    source: "party-front-gauche",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-parti-gauche",
    targetKind: "party",
    date: "2009",
    description: "Le Parti de gauche participe à la coalition Front de gauche avec le PCF et d'autres composantes.",
    sources: ["source-francearchives-front-gauche"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-npa-successor-lcr",
    source: "party-npa",
    sourceKind: "party",
    relation: "SUCCESSOR_OF",
    target: "party-lcr",
    targetKind: "party",
    date: "2009",
    description: "Le NPA prolonge la LCR dans une organisation plus large de l'anticapitalisme militant.",
    sources: ["source-francearchives-npa"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-eelv-merged-verts",
    source: "party-verts",
    sourceKind: "party",
    relation: "MERGED_INTO",
    target: "party-eelv",
    targetKind: "party",
    date: "2010",
    description: "La création d'EELV résulte du regroupement des Verts et de la dynamique Europe Écologie.",
    sources: ["source-francearchives-eelv"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-mdc-founded-from-socialisme-republique",
    source: "party-mdc",
    sourceKind: "party",
    relation: "FOUNDED_FROM",
    target: "party-ps-1969",
    targetKind: "party",
    date: "1993",
    description: "Le MDC est issu de la transformation du courant socialiste républicain et souverainiste autour de Jean-Pierre Chevènement.",
    sources: ["source-francearchives-mdc"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-gauche-plurielle-allied-ps",
    source: "party-gauche-plurielle",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-ps-1969",
    targetKind: "party",
    date: "1997",
    description: "La Gauche plurielle est structurée autour du Parti socialiste après les législatives de 1997.",
    sources: ["source-vie-publique-gauche-plurielle"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-gauche-plurielle-allied-pcf",
    source: "party-gauche-plurielle",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-pcf",
    targetKind: "party",
    date: "1997",
    description: "Le PCF participe à la majorité parlementaire et au gouvernement de la Gauche plurielle.",
    sources: ["source-vie-publique-gauche-plurielle", "source-pcf-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-gauche-plurielle-allied-verts",
    source: "party-gauche-plurielle",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-verts",
    targetKind: "party",
    date: "1997",
    description: "Les Verts participent à la majorité et Dominique Voynet entre au gouvernement Jospin.",
    sources: ["source-vie-publique-gauche-plurielle", "source-francearchives-verts"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-democratie-liberale-split-udf",
    source: "party-democratie-liberale",
    sourceKind: "party",
    relation: "SPLIT_FROM",
    target: "party-udf",
    targetKind: "party",
    date: "1997",
    description: "Démocratie libérale se détache de la composante républicaine de l'UDF après les recompositions de 1997.",
    sources: ["source-francearchives-democratie-liberale"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-rpf-1999-founded-from-rpr",
    source: "party-rpf-1999",
    sourceKind: "party",
    relation: "SPLIT_FROM",
    target: "party-rpr",
    targetKind: "party",
    date: "1999",
    description: "Le RPF souverainiste est fondé par Charles Pasqua et Philippe de Villiers après leur rupture avec la direction du RPR.",
    sources: ["source-francearchives-rpf-1999"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-mnr-split-fn",
    source: "party-mnr",
    sourceKind: "party",
    relation: "SPLIT_FROM",
    target: "party-fn",
    targetKind: "party",
    date: "1999",
    description: "Le MNR est issu de la scission du Front national autour de Bruno Mégret.",
    sources: ["source-francearchives-mnr"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-mpf-founded-by-villiers",
    source: "person-philippe-de-villiers",
    sourceKind: "person",
    relation: "FOUNDED_BY",
    target: "party-mpf",
    targetKind: "party",
    date: "1994",
    description: "Philippe de Villiers fonde le Mouvement pour la France.",
    sources: ["source-francearchives-mpf"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-ligue-communiste-successor-lcr",
    source: "party-lcr",
    sourceKind: "party",
    relation: "SUCCESSOR_OF",
    target: "party-ligue-communiste",
    targetKind: "party",
    date: "1974",
    description: "La LCR prolonge les recompositions trotskistes après la dissolution de la Ligue communiste.",
    sources: ["source-maitron-krivine"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-cir-merged-ps-epinay",
    source: "party-cir",
    sourceKind: "party",
    relation: "MERGED_INTO",
    target: "party-ps-1969",
    targetKind: "party",
    date: "1971-06",
    description: "La CIR rejoint le Parti socialiste lors du congrès d'Épinay, qui porte François Mitterrand à la direction du parti.",
    sources: ["source-archives-socialistes-1969"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-prg-split-prrrs",
    source: "party-prg",
    sourceKind: "party",
    relation: "SPLIT_FROM",
    target: "party-prrrs",
    targetKind: "party",
    date: "1972",
    description: "Les radicaux de gauche se séparent du Parti radical dans le contexte du choix de l'union de la gauche.",
    sources: ["source-prg-histoire", "source-francearchives-parti-radical"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-parti-radical-valoisien-successor-prrrs",
    source: "party-parti-radical-valoisien",
    sourceKind: "party",
    relation: "SUCCESSOR_OF",
    target: "party-prrrs",
    targetKind: "party",
    date: "1972",
    description: "Le Parti radical dit valoisien représente la continuité centriste du radicalisme après la scission des radicaux de gauche.",
    sources: ["source-francearchives-parti-radical", "source-prg-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-programme-commun-allied-ps",
    source: "party-programme-commun-gauche",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-ps-1969",
    targetKind: "party",
    date: "1972-06-27",
    description: "Le Parti socialiste est l'une des composantes du Programme commun de la gauche.",
    sources: ["source-archives-socialistes-1969"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-programme-commun-allied-pcf",
    source: "party-programme-commun-gauche",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-pcf",
    targetKind: "party",
    date: "1972-06-27",
    description: "Le PCF est l'une des composantes du Programme commun de la gauche.",
    sources: ["source-pcf-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-programme-commun-allied-prg",
    source: "party-programme-commun-gauche",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-prg",
    targetKind: "party",
    date: "1972-06-27",
    description: "Les radicaux de gauche participent à l'accord du Programme commun.",
    sources: ["source-prg-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-centre-democrate-merged-cds",
    source: "party-centre-democrate",
    sourceKind: "party",
    relation: "MERGED_INTO",
    target: "party-cds",
    targetKind: "party",
    date: "1976",
    description: "Le Centre démocrate participe à la création du Centre des démocrates sociaux.",
    sources: ["source-francearchives-cds"],
    dataStatus: "verified"
  },
  {
    id: "relation-cdp-merged-cds",
    source: "party-centre-democratie-progres",
    sourceKind: "party",
    relation: "MERGED_INTO",
    target: "party-cds",
    targetKind: "party",
    date: "1976",
    description: "Le Centre démocratie et progrès participe à la création du Centre des démocrates sociaux.",
    sources: ["source-francearchives-cds"],
    dataStatus: "verified"
  },
  {
    id: "relation-udr-renamed-rpr",
    source: "party-udr",
    sourceKind: "party",
    relation: "RENAMED_TO",
    target: "party-rpr",
    targetKind: "party",
    date: "1976-12-05",
    description: "L'UDR est transformée en Rassemblement pour la République autour de Jacques Chirac.",
    sources: ["source-francearchives-rpr"],
    dataStatus: "verified"
  },
  {
    id: "relation-fnri-renamed-pr-1977",
    source: "party-fnri",
    sourceKind: "party",
    relation: "RENAMED_TO",
    target: "party-parti-republicain-1977",
    targetKind: "party",
    date: "1977-05-20",
    description: "La Fédération nationale des républicains indépendants devient le Parti républicain.",
    sources: ["source-perspective-ri", "source-francearchives-udf"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-udf-allied-pr",
    source: "party-udf",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-parti-republicain-1977",
    targetKind: "party",
    date: "1978-02-01",
    description: "Le Parti républicain est une composante libérale de l'UDF.",
    sources: ["source-francearchives-udf"],
    dataStatus: "verified"
  },
  {
    id: "relation-udf-allied-cds",
    source: "party-udf",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-cds",
    targetKind: "party",
    date: "1978-02-01",
    description: "Le CDS est une composante démocrate-chrétienne de l'UDF.",
    sources: ["source-francearchives-udf", "source-francearchives-cds"],
    dataStatus: "verified"
  },
  {
    id: "relation-udf-allied-radical-valoisien",
    source: "party-udf",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-parti-radical-valoisien",
    targetKind: "party",
    date: "1978-02-01",
    description: "Le Parti radical valoisien participe à la confédération UDF.",
    sources: ["source-francearchives-udf", "source-francearchives-parti-radical"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-pfn-split-fn",
    source: "party-pfn",
    sourceKind: "party",
    relation: "SPLIT_FROM",
    target: "party-fn",
    targetKind: "party",
    date: "1974",
    description: "Le PFN est placé comme dissidence ou concurrence issue des recompositions de l'extrême droite autour du FN ; la relation reste à vérifier.",
    sources: ["source-fondation-jean-jaures-le-pen"],
    dataStatus: "unverified"
  },
  {
    id: "relation-europe-ecologie-founded-from-adt",
    source: "party-europe-ecologie-1979",
    sourceKind: "party",
    relation: "FOUNDED_FROM",
    target: "party-amis-terre",
    targetKind: "party",
    date: "1979",
    description: "La liste Europe Écologie s'inscrit dans les réseaux de l'écologie politique associative, dont les Amis de la Terre.",
    sources: ["source-europe-ecologie-1979"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-unr-merged-unr-udt",
    source: "party-unr",
    sourceKind: "party",
    relation: "MERGED_INTO",
    target: "party-unr-udt",
    targetKind: "party",
    date: "1962-11-10",
    description: "L'UNR se rapproche des gaullistes de gauche de l'UDT et devient l'UNR-UDT.",
    sources: ["source-bnf-unr-udt", "source-assemblee-unr-udt"],
    dataStatus: "verified"
  },
  {
    id: "relation-udt-merged-unr-udt",
    source: "party-udt",
    sourceKind: "party",
    relation: "MERGED_INTO",
    target: "party-unr-udt",
    targetKind: "party",
    date: "1962-11-10",
    description: "L'Union démocratique du travail rejoint l'UNR dans la formation UNR-UDT.",
    sources: ["source-bnf-unr-udt", "source-assemblee-unr-udt"],
    dataStatus: "verified"
  },
  {
    id: "relation-unr-udt-renamed-udr",
    source: "party-unr-udt",
    sourceKind: "party",
    relation: "RENAMED_TO",
    target: "party-udr",
    targetKind: "party",
    date: "1967-11-24",
    description: "L'UNR-UDT prend la dénomination d'Union des démocrates pour la République dans la recomposition gaulliste.",
    sources: ["source-francearchives-unr", "source-bnf-unr-udt"],
    dataStatus: "verified"
  },
  {
    id: "relation-mrp-founded-centre-democrate",
    source: "party-centre-democrate",
    sourceKind: "party",
    relation: "FOUNDED_FROM",
    target: "party-mrp",
    targetKind: "party",
    date: "1966",
    description: "Le Centre démocrate se constitue notamment à partir de l'héritage démocrate-chrétien du MRP.",
    sources: ["source-francearchives-centre-democrate", "source-persee-centre-democrate"],
    dataStatus: "verified"
  },
  {
    id: "relation-cdp-split-centre-democrate",
    source: "party-centre-democratie-progres",
    sourceKind: "party",
    relation: "SPLIT_FROM",
    target: "party-centre-democrate",
    targetKind: "party",
    date: "1969",
    description: "Le Centre démocratie et progrès est issu d'une scission du Centre démocrate après la présidentielle de 1969.",
    sources: ["source-francearchives-centre-democrate", "source-persee-centre-democrate"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-fnri-split-cnip",
    source: "party-fnri",
    sourceKind: "party",
    relation: "SPLIT_FROM",
    target: "party-cnip",
    targetKind: "party",
    date: "1966-06-01",
    description: "Les Républicains indépendants se structurent autour de Valéry Giscard d'Estaing après une rupture avec le CNIP.",
    sources: ["source-persee-republicains-independants", "source-francearchives-cnip"],
    dataStatus: "verified"
  },
  {
    id: "relation-sfio-renamed-ps-1969",
    source: "party-sfio",
    sourceKind: "party",
    relation: "RENAMED_TO",
    target: "party-ps-1969",
    targetKind: "party",
    date: "1969-07-13",
    description: "Le congrès d'Issy-les-Moulineaux transforme la SFIO en nouveau Parti socialiste, sans fusion du PSU à cette date.",
    sources: ["source-archives-socialistes-1969"],
    dataStatus: "verified"
  },
  {
    id: "relation-fgds-allied-sfio",
    source: "party-fgds",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-sfio",
    targetKind: "party",
    date: "1965",
    description: "La SFIO participe à la Fédération de la gauche démocrate et socialiste.",
    sources: ["source-archives-socialistes-1969"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-fgds-allied-cir",
    source: "party-fgds",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-cir",
    targetKind: "party",
    date: "1965",
    description: "La CIR de François Mitterrand est une composante centrale de la FGDS.",
    sources: ["source-archives-socialistes-1969"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-fgds-allied-radical",
    source: "party-fgds",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-prrrs",
    targetKind: "party",
    date: "1965",
    description: "Des radicaux participent à la dynamique de la gauche non communiste fédérée.",
    sources: ["source-archives-socialistes-1969"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-ligue-communiste-founded-from-pci",
    source: "party-ligue-communiste",
    sourceKind: "party",
    relation: "FOUNDED_FROM",
    target: "party-pci-trotskiste",
    targetKind: "party",
    date: "1969",
    description: "La Ligue communiste s'inscrit dans les recompositions trotskistes postérieures à Mai 68.",
    sources: ["source-maitron-krivine", "source-maitron-bleibtreu"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-gauche-proletarienne-successor-m22",
    source: "party-gauche-proletarienne",
    sourceKind: "party",
    relation: "SUCCESSOR_OF",
    target: "party-mouvement-22-mars",
    targetKind: "party",
    date: "1968",
    description: "La Gauche prolétarienne appartient au cycle militant ouvert par Mai 68, sans être une continuation organique directe du Mouvement du 22 mars.",
    sources: ["source-persee-mouvement-22-mars", "source-paris8-gauche-proletarienne"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-parti-nationaliste-founded-from-jeune-nation",
    source: "party-parti-nationaliste",
    sourceKind: "party",
    relation: "FOUNDED_FROM",
    target: "party-jeune-nation",
    targetKind: "party",
    date: "1959",
    description: "Le Parti nationaliste est rattaché aux recompositions de Jeune Nation, mais cette relation reste à vérifier par source archivistique.",
    sources: ["source-jeune-nation-sidos"],
    dataStatus: "unverified"
  },
  {
    id: "relation-rpf-predecessor-republicains-sociaux",
    source: "party-rpf",
    sourceKind: "party",
    relation: "PREDECESSOR_OF",
    target: "party-republicains-sociaux",
    targetKind: "party",
    date: "1955",
    description: "Les Républicains sociaux prolongent une partie du courant gaulliste après la mise en sommeil du RPF.",
    sources: ["source-fondation-de-gaulle-rpf", "source-francearchives-unr"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-republicains-sociaux-merged-unr",
    source: "party-republicains-sociaux",
    sourceKind: "party",
    relation: "MERGED_INTO",
    target: "party-unr",
    targetKind: "party",
    date: "1958-10-01",
    description: "Les Républicains sociaux participent à la recomposition gaulliste menant à l'UNR.",
    sources: ["source-francearchives-unr", "source-vie-publique-1958"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-prl-absorbed-cnip",
    source: "party-prl",
    sourceKind: "party",
    relation: "ABSORBED_BY",
    target: "party-cnip",
    targetKind: "party",
    date: "1951",
    description: "Le PRL est absorbé dans les recompositions de droite autour du CNIP.",
    sources: ["source-openedition-prl", "source-francearchives-cnip"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-psa-split-sfio",
    source: "party-psa",
    sourceKind: "party",
    relation: "SPLIT_FROM",
    target: "party-sfio",
    targetKind: "party",
    date: "1958-09-11",
    description: "Le PSA est créé par des minoritaires quittant la SFIO après la crise de 1958.",
    sources: ["source-francearchives-psa", "source-maitron-depreux"],
    dataStatus: "verified"
  },
  {
    id: "relation-psa-merged-psu",
    source: "party-psa",
    sourceKind: "party",
    relation: "MERGED_INTO",
    target: "party-psu",
    targetKind: "party",
    date: "1960-04-03",
    description: "Le PSA fusionne avec l'UGS et d'autres groupes pour former le PSU.",
    sources: ["source-francearchives-psu", "source-francearchives-psa"],
    dataStatus: "verified"
  },
  {
    id: "relation-front-republicain-allied-sfio",
    source: "party-front-republicain-1956",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-sfio",
    targetKind: "party",
    date: "1956",
    description: "La SFIO est une composante centrale du Front républicain de 1956.",
    sources: ["source-francearchives-udsr"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-front-republicain-allied-prrrs",
    source: "party-front-republicain-1956",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-prrrs",
    targetKind: "party",
    date: "1956",
    description: "Le Parti radical participe au Front républicain de 1956.",
    sources: ["source-francearchives-udsr"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-front-republicain-allied-udsr",
    source: "party-front-republicain-1956",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-udsr",
    targetKind: "party",
    date: "1956",
    description: "L'UDSR est l'une des composantes du Front républicain de 1956.",
    sources: ["source-francearchives-udsr", "source-sciencespo-udsr"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-jeune-nation-founded-from-francisme",
    source: "party-jeune-nation",
    sourceKind: "party",
    relation: "FOUNDED_FROM",
    target: "party-francisme",
    targetKind: "party",
    date: "1949",
    description: "Jeune Nation est placée dans les continuités d'extrême droite de l'après-guerre ; la relation avec le francisme doit rester contextualisée par les trajectoires militantes, notamment celles de Pierre Sidos.",
    sources: ["source-jeune-nation-sidos"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-oas-founded-from-jeune-nation-milieus",
    source: "party-oas",
    sourceKind: "party",
    relation: "FOUNDED_FROM",
    target: "party-jeune-nation",
    targetKind: "party",
    date: "1961-02-11",
    description: "L'OAS appartient aux prolongements de milieux Algérie française, mais elle constitue une organisation clandestine distincte et postérieure à Jeune Nation.",
    sources: ["source-oas-herodote", "source-jeune-nation-sidos"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-pco-split-pcf",
    source: "party-pco",
    sourceKind: "party",
    relation: "SPLIT_FROM",
    target: "party-pcf",
    targetKind: "party",
    date: "1929",
    description: "Le Parti communiste oppositionnel est modélisé comme dissidence communiste issue du PCF ; la précision de ses réseaux reste à vérifier.",
    sources: ["source-maitron-tours"],
    dataStatus: "unverified"
  },
  {
    id: "relation-psdf-ujj-split-sfio",
    source: "party-psdf-ujj",
    sourceKind: "party",
    relation: "SPLIT_FROM",
    target: "party-sfio",
    targetKind: "party",
    date: "1933",
    description: "Le PSdF-UJJ naît de la rupture des néo-socialistes avec la SFIO.",
    sources: ["source-cambridge-neosocialisme"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-psop-split-sfio",
    source: "party-psop",
    sourceKind: "party",
    relation: "SPLIT_FROM",
    target: "party-sfio",
    targetKind: "party",
    date: "1938-06-08",
    description: "Le PSOP est issu de la scission de militants de la Gauche révolutionnaire de la SFIO.",
    sources: ["source-francearchives-psop", "source-bataille-socialiste-psop"],
    dataStatus: "verified"
  },
  {
    id: "relation-social-francais-successor-croix-de-feu",
    source: "party-social-francais",
    sourceKind: "party",
    relation: "SUCCESSOR_OF",
    target: "party-croix-de-feu",
    targetKind: "party",
    date: "1936",
    description: "Le Parti social français succède aux Croix-de-Feu après la dissolution des ligues.",
    sources: ["source-erudit-croix-de-feu-psf", "source-1914-1918-croix-de-feu"],
    dataStatus: "verified"
  },
  {
    id: "relation-ppf-split-pcf",
    source: "party-ppf",
    sourceKind: "party",
    relation: "FOUNDED_FROM",
    target: "party-pcf",
    targetKind: "party",
    date: "1936-06-28",
    description: "Le PPF est fondé par Jacques Doriot après son exclusion du PCF, sans être une continuation idéologique du communisme.",
    sources: ["source-francearchives-ppf", "source-larousse-ppf"],
    dataStatus: "verified"
  },
  {
    id: "relation-francisme-founded-from-faisceau",
    source: "party-francisme",
    sourceKind: "party",
    relation: "FOUNDED_FROM",
    target: "party-faisceau",
    targetKind: "party",
    date: "1933-09-29",
    description: "Marcel Bucard est passé par plusieurs mouvements nationalistes et fascistes, dont Le Faisceau, avant de fonder le Francisme.",
    sources: ["source-francearchives-bucard", "source-persee-faisceau"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-front-populaire-alliance-prrrs",
    source: "party-front-populaire",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-prrrs",
    targetKind: "party",
    date: "1935",
    description: "Le Parti radical participe à la coalition du Front populaire aux côtés de la SFIO et avec le soutien communiste.",
    sources: ["source-vie-publique-front-populaire", "source-assemblee-1936"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-droite-anti-fp-allied-fr",
    source: "party-droite-anti-front-populaire",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-federation-republicaine",
    targetKind: "party",
    date: "1936",
    description: "La Fédération républicaine appartient aux principales oppositions parlementaires de droite au Front populaire.",
    sources: ["source-vie-publique-front-populaire"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-droite-anti-fp-allied-ard",
    source: "party-droite-anti-front-populaire",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-ard",
    targetKind: "party",
    date: "1936",
    description: "L'Alliance démocratique appartient aux oppositions républicaines modérées au Front populaire.",
    sources: ["source-vie-publique-front-populaire"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-droite-anti-fp-allied-psf",
    source: "party-droite-anti-front-populaire",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-social-francais",
    targetKind: "party",
    date: "1936",
    description: "Le Parti social français se situe dans l'opposition au Front populaire après la dissolution des Croix-de-Feu.",
    sources: ["source-erudit-croix-de-feu-psf"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-pcf-sfic-renamed",
    source: "party-pcf",
    sourceKind: "party",
    relation: "RENAMED_TO",
    target: "party-pcf",
    targetKind: "party",
    date: "1921",
    description: "La SFIC est conservée comme première dénomination du Parti communiste français plutôt que comme une organisation séparée.",
    sources: ["source-maitron-tours", "source-pcf-histoire"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-cartel-allied-prrrs",
    source: "party-cartel-des-gauches",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-prrrs",
    targetKind: "party",
    date: "1924",
    description: "Le Parti radical est la composante centrale du Cartel des gauches de 1924.",
    sources: ["source-jstor-cartel-socialistes", "source-france-memoire-herriot-cartel"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-cartel-allied-sfio",
    source: "party-cartel-des-gauches",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-sfio",
    targetKind: "party",
    date: "1924",
    description: "La SFIO participe à l'accord électoral du Cartel des gauches tout en conservant son autonomie partisane.",
    sources: ["source-jstor-cartel-socialistes", "source-france-memoire-herriot-cartel"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-cartel-allied-prs",
    source: "party-cartel-des-gauches",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-prs",
    targetKind: "party",
    date: "1924",
    description: "Les républicains-socialistes appartiennent aux composantes de gauche républicaine du Cartel.",
    sources: ["source-jstor-cartel-socialistes"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-faisceau-founded-from-action-francaise",
    source: "party-faisceau",
    sourceKind: "party",
    relation: "FOUNDED_FROM",
    target: "party-action-francaise",
    targetKind: "party",
    date: "1925-11-11",
    description: "Georges Valois fonde Le Faisceau après un parcours important à l'Action française.",
    sources: ["source-persee-faisceau"],
    dataStatus: "verified"
  },
  {
    id: "relation-cagoule-founded-from-action-francaise-dissidents",
    source: "party-cagoule",
    sourceKind: "party",
    relation: "FOUNDED_FROM",
    target: "party-action-francaise",
    targetKind: "party",
    date: "1935",
    description: "La Cagoule est liée à des dissidences et sociabilités issues de l'Action française, mais elle constitue une organisation clandestine distincte et postérieure à 1930.",
    sources: ["source-cagoule-historytoday"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-pof-merged-psdf",
    source: "party-pof",
    sourceKind: "party",
    relation: "MERGED_INTO",
    target: "party-psdf",
    targetKind: "party",
    date: "1902",
    description: "Le Parti ouvrier français participe à la formation du Parti socialiste de France.",
    sources: ["source-persee-mouvement-socialiste", "source-gallica-psdf-commentry"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-socialistes-independants-founded-prs",
    source: "party-prs",
    sourceKind: "party",
    relation: "FOUNDED_FROM",
    target: "party-socialistes-independants",
    targetKind: "party",
    date: "1911",
    description: "Le Parti républicain-socialiste est issu de socialistes indépendants restés hors de la SFIO.",
    sources: ["source-persee-prs", "source-theses-prs"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-psf-1919-split-sfio",
    source: "party-psf-1919",
    sourceKind: "party",
    relation: "SPLIT_FROM",
    target: "party-sfio",
    targetKind: "party",
    date: "1919",
    description: "Le Parti socialiste français de 1919 est créé par des dissidents socialistes séparés de la SFIO.",
    sources: ["source-persee-prs"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-jeunesses-patriotes-founded-from-ligue",
    source: "party-jeunesses-patriotes",
    sourceKind: "party",
    relation: "FOUNDED_FROM",
    target: "party-ligue-des-patriotes",
    targetKind: "party",
    date: "1924",
    description: "Les Jeunesses patriotes sont créées dans le cadre de la Ligue des patriotes avant de s'en détacher.",
    sources: ["source-theses-jeunesses-patriotes", "source-archives-loire-atlantique-jp"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-ard-renamed-prd",
    source: "party-ard",
    sourceKind: "party",
    relation: "RENAMED_TO",
    target: "party-ard",
    targetKind: "party",
    date: "1911",
    description: "L'Alliance républicaine démocratique prend la dénomination de Parti républicain démocratique ; le dataset conserve une fiche organisationnelle unique avec variantes de nom.",
    sources: ["source-sanson-alliance-democratique"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-bloc-national-allied-ard",
    source: "party-bloc-national",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-ard",
    targetKind: "party",
    date: "1919-10-22",
    description: "L'Alliance démocratique est une composante centrale du Bloc national de 1919.",
    sources: ["source-assemblee-bloc-national-1919"],
    dataStatus: "verified"
  },
  {
    id: "relation-bloc-national-allied-fr",
    source: "party-bloc-national",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-federation-republicaine",
    targetKind: "party",
    date: "1919",
    description: "La Fédération républicaine participe à la coalition du Bloc national.",
    sources: ["source-assemblee-bloc-national-1919", "source-larousse-bloc-national"],
    dataStatus: "verified"
  },
  {
    id: "relation-bloc-national-allied-alp",
    source: "party-bloc-national",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-alp",
    targetKind: "party",
    date: "1919",
    description: "L'Action libérale populaire est intégrée aux recompositions de droite et de centre du Bloc national.",
    sources: ["source-assemblee-bloc-national-1919"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-bloc-national-allied-ri",
    source: "party-bloc-national",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-radicaux-independants",
    targetKind: "party",
    date: "1919",
    description: "Des radicaux indépendants participent aux configurations électorales du Bloc national sans que cela soit assimilé au Parti radical dans son ensemble.",
    sources: ["source-assemblee-bloc-national-1919"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-psf-merged-sfio",
    source: "party-psf-1902",
    sourceKind: "party",
    relation: "MERGED_INTO",
    target: "party-sfio",
    targetKind: "party",
    date: "1905",
    description: "Le Parti socialiste français fait partie des organisations socialistes réunies dans la SFIO.",
    sources: ["source-vie-publique-front-populaire"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-psdf-merged-sfio",
    source: "party-psdf",
    sourceKind: "party",
    relation: "MERGED_INTO",
    target: "party-sfio",
    targetKind: "party",
    date: "1905",
    description: "Le Parti socialiste de France participe à l'unification socialiste de 1905.",
    sources: ["source-gallica-psdf-commentry"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-progressistes-predecessor-fr",
    source: "party-republicains-progressistes",
    sourceKind: "party",
    relation: "PREDECESSOR_OF",
    target: "party-federation-republicaine",
    targetKind: "party",
    date: "1903",
    description: "La Fédération républicaine rassemble une partie des républicains progressistes et modérés de droite.",
    sources: ["source-francearchives-federation-republicaine"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-alp-absorbed-fr",
    source: "party-alp",
    sourceKind: "party",
    relation: "ABSORBED_BY",
    target: "party-federation-republicaine",
    targetKind: "party",
    date: "1919",
    description: "Après la Première Guerre mondiale, l'ALP perd son existence propre et une partie de ses cadres rejoint les recompositions de droite autour de la Fédération républicaine.",
    sources: ["source-francearchives-federation-republicaine"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-pcf-split-sfio",
    source: "party-pcf",
    sourceKind: "party",
    relation: "SPLIT_FROM",
    target: "party-sfio",
    targetKind: "party",
    date: "1920-12-30",
    description: "Le PCF trouve son origine dans la majorité issue de la scission du Congrès de Tours.",
    sources: ["source-maitron-tours"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-blum-leader-sfio",
    source: "person-leon-blum",
    sourceKind: "person",
    relation: "LEADER_OF",
    target: "party-sfio",
    targetKind: "party",
    description: "Léon Blum est associé à la direction politique de la SFIO dans l'entre-deux-guerres.",
    sources: ["source-bnf-blum", "source-vie-publique-front-populaire"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-front-populaire-alliance",
    source: "party-front-populaire",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-sfio",
    targetKind: "party",
    date: "1935",
    description: "Le Front populaire est traité ici comme une coalition liée à la SFIO.",
    sources: ["source-vie-publique-front-populaire"],
    dataStatus: "partially_verified"
  },
  {
    id: "relation-front-populaire-alliance-pcf",
    source: "party-front-populaire",
    sourceKind: "party",
    relation: "ALLIED_WITH",
    target: "party-pcf",
    targetKind: "party",
    date: "1935",
    description: "Le PCF est associé au Front populaire dans le contexte de l'alliance antifasciste et électorale.",
    sources: ["source-vie-publique-front-populaire"],
    dataStatus: "partially_verified"
  }
];
